import { CandleRecovery, CandleRequests } from '../../shared-web/candle-recovery';
import { indicativePayouts, PayoutContext } from '../../shared-web/payout-feed';
import { chooseDisplayCurrency, refreshDisplayEstimate, chooseFundingWallet, getLiveUsdFxRate, getCurrencyStakePresets } from "../../shared-web/display-currency";
import { applyStakePolicy } from "../../shared-web/stake-policy";
import { renderPairIconSVG } from "../../shared-web/instrument-icon";
import { SentimentEngine } from "../../shared-web/sentiment-engine";
import { RestClient } from "./api/rest_client";
import { WSClient } from "./api/ws_client";
import { CanvasChart, DrawingTool, IndicatorId } from "./chart/canvas_chart";
import { OrderStore } from "./state/order_store";
import { AppState, formatSmartBalance, getCurrencySymbol } from "./state/app_state";
import { TelemetryMonitor } from "./telemetry/telemetry_monitor";
import { renderContractReceipt } from "./components/ContractReceipt";
import { renderComplianceModal } from "./components/ComplianceModal";
import { renderLedgerStatement } from "./components/LedgerStatement";
import { CashierDepositModal } from "./components/CashierDepositModal";
import { renderBonusCenter } from "./components/BonusCenter";
import { AffiliatePortal } from "./components/AffiliatePortal";
import { TurnstileChallengeModal } from "./components/TurnstileChallengeModal";
import { Timeframe, Contract, Candle } from "@starx/shared-types";

try {
  console.log(
    '%cSTOP! 🛑\n%cThis browser console is intended for developers only. Pasting scripts or third-party code here to attempt "signal hacking" or "bot automation" is unsafe and cannot alter server-side trades or outcomes. All orders and prices are cryptographically settled on the StarX server.',
    'color: #ef4444; font-size: 26px; font-weight: 800;',
    'color: #94a3b8; font-size: 13px; line-height: 1.5;'
  );
} catch {}

const CHART_TIMEFRAMES: Timeframe[] = [
  "5s", "15s", "30s", "1m", "2m", "5m", "15m", "30m", "45m", "1h", "2h", "4h",
];
const CONTRACT_DURATIONS_SECONDS = [
  5, 15, 30, 60, 120, 300, 900, 1800, 2700, 3600, 7200, 14400,
];

export class TradingTerminalApp {
  private restClient: RestClient;
  private wsClient: WSClient;
  private appState: AppState;
  private orderStore: OrderStore;
  private telemetry: TelemetryMonitor;
  private cashierModal: CashierDepositModal | null = null;
  private challengeModal: TurnstileChallengeModal | null = null;
  private chart: CanvasChart | null = null;
  private selectedTimeframe: Timeframe = "1m";
  private isMuted: boolean = false;
  private audioCtx: any = null;
  private terminalPreferences: Record<string, any> = {
    auto_scroll: true,
    one_click_trade: true,
    performance_mode: true,
    short_order_labels: false,
    hide_trade_results: false,
    timezone: "UTC",
    language: "en",
    active_indicators: [],
    chart_type: "CANDLESTICK",
    timeframe: "1m",
    theme: "dark",
  };
  private leaderboardRefreshTimer: number | null = null;
  private currentPayoutPercentage = 0;
  private selectedInstrumentSession: any = null;
  private openSymbols: string[] = [];
  private preferenceSaveChain: Promise<void> = Promise.resolve();
  private currentProfile: any = null;
  private isAuthenticated = false;
  private pendingTradeEnabled = false;
  private pendingTradeMode: "QUOTE" | "TIME" = "QUOTE";
  private pendingDirection: "CALL" | "PUT" = "CALL";
  private pendingQuoteEdited = false;
  private pendingTimeEdited = false;
  private pendingOrders: any[] = [];
  private pendingOrderStatuses = new Map<string, string>();
  private demoPendingExecutionIds = new Set<string>();
  private pendingOrderTimer: number | null = null;
  private pendingOrdersProcessing = new Set<string>();
  private drawingSaveTimer: number | null = null;
  private cashoutFeatureEnabled = true;
  private notificationRefreshTimer: number | null = null;
  private payoutRefreshTimer: number | null = null;
  private cashoutQuoteRefreshTimer: number | null = null;
  private cashoutQuoteRefreshInFlight = new Set<string>();
  private syntheticFeedWordingEnabled = false;
  private candleBufferCache = new Map<string, Candle[]>();
  private favouriteMarkets = new Set<string>(["EURUSD_OTC", "BTCUSD_OTC", "GOLD_OTC"]);
  private lastClockCheckAt = 0;
  private candleRequestId = 0;
  private candleRequests = new CandleRequests();
  private candleRecovery: CandleRecovery | null = null;
  private instrumentSelectionId = 0;
  private isSubmittingTrade = false;
  private lastTradeExecutionTime = 0;

  constructor() {
    this.restClient = new RestClient();
    this.appState = new AppState(this.restClient);
    this.orderStore = new OrderStore(this.restClient);
    this.telemetry = new TelemetryMonitor();
    this.wsClient = new WSClient(
      this.restClient,
      "",
      this.appState.getSelectedSymbol(),
    );
    try {
      const saved = JSON.parse(localStorage.getItem("starx-favourite-markets") || "[]");
      if (Array.isArray(saved) && saved.every((item) => typeof item === "string")) {
        this.favouriteMarkets = new Set(saved);
      }
    } catch {
      // Storage can be unavailable in hardened/private browser contexts.
    }
    try {
      const savedOpenSymbols = JSON.parse(
        localStorage.getItem("starx-trading-open-symbols") || "[]",
      );
      if (Array.isArray(savedOpenSymbols)) {
        this.openSymbols = savedOpenSymbols
          .map(String)
          .filter(
            (symbol, index, list) =>
              /^[A-Z0-9_]{2,32}$/.test(symbol) &&
              list.indexOf(symbol) === index,
          )
          .slice(0, 12);
      }
    } catch {
      // The server preference remains the fallback when local storage is unavailable.
    }
    setTimeout(() => this.hideStartupLoader(), 3500);
  }

  public async init() {
    // 0A. Geo-compliance check: restrict access for non-permitted jurisdictions
    const allowed = await this.checkGeoAccess();
    if (!allowed) {
      this.hideStartupLoader();
      return;
    }

    // 0. Landing Page & View Routing Initialization (synchronous, 0ms delay)
    this.initLandingPageRouting();

    // 1. Production session & OIDC resolution
    if (!(await this.resolveSession())) return;

    // 2. Initialize Canvas Chart
    const canvas = (document.getElementById("live-chart-canvas") ||
      document.getElementById("trading-chart-canvas")) as HTMLCanvasElement;
    if (canvas) {
      this.chart = new CanvasChart(canvas);
      this.chart.setTimeframe(this.selectedTimeframe);
      this.chart.onGapDetected(() => {
        this.candleBufferCache.clear();
        void this.loadCandles();
      });
      const initialCached = this.getCachedCandles(this.appState.getSelectedSymbol(), this.selectedTimeframe);
      if (initialCached && initialCached.length > 0) {
        this.chart.setCandles(initialCached);
      }
      const savedVolume = localStorage.getItem("starx_chart_volume_visible");
      const isVolEnabled = savedVolume !== null ? savedVolume === "true" : true;
      this.chart.setVolumeVisible(isVolEnabled);
      const volBtn = document.getElementById("volume-toggle-btn");
      if (volBtn) {
        volBtn.classList.toggle("active", isVolEnabled);
        volBtn.setAttribute("aria-pressed", String(isVolEnabled));
        volBtn.title = isVolEnabled ? "Hide tick volume" : "Show tick volume";
        volBtn.setAttribute("aria-label", isVolEnabled ? "Hide tick volume" : "Show tick volume");
      }
      document.getElementById("chart-zoom-out")?.addEventListener("click", () => this.chart?.applyZoom(1 / 1.18));
      document.getElementById("chart-zoom-in")?.addEventListener("click", () => this.chart?.applyZoom(1.18));
      document.getElementById("chart-reset-view")?.addEventListener("click", () => this.chart?.resetView());
      document.getElementById("otc-audit-badge-btn")?.addEventListener("click", () => this.showOTCAuditModal());
    }

    // 2B. Bind UI events & State immediately for responsive first frame
    this.bindUIEvents();
    this.bindState();

    // 3. Load initial state, active positions & candles from authoritative API
    try {
      const publicInstruments = await this.restClient.getInstruments();
      if (publicInstruments && Array.isArray(publicInstruments) && publicInstruments.length > 0) {
        this.appState.setInstruments(publicInstruments);
      }
    } catch (err) {
      console.warn("Initial instruments fetch failed:", err);
    }
    if (this.isAuthenticated) {
      await this.appState.refreshLiveState();
    }
    try {
      this.currentProfile = await this.restClient.getProfile();
    } catch {
      this.currentProfile = null;
    }
    if (this.isAuthenticated && this.currentProfile) {
      const explicitChoice = localStorage.getItem("starx_user_explicit_currency");
      const savedChoice = localStorage.getItem("starx_trading_currency") || localStorage.getItem("starx_display_currency");
      const userCurr = (explicitChoice === "true" && savedChoice && /^[A-Z]{3,5}$/.test(savedChoice))
        ? savedChoice.toUpperCase()
        : String(this.currentProfile.preferred_currency || this.currentProfile.currency || "").toUpperCase();
      if (userCurr) {
        localStorage.setItem("starx_display_currency", userCurr);
        this.appState.setSelectedCurrency(userCurr);
      }
      if (this.currentProfile.country_code) {
        localStorage.setItem("starx_detected_country", this.currentProfile.country_code.toUpperCase());
      }
    }
    try {
      const geo = await fetch("/api/v1/display-currency", { cache: "no-store" }).then((r) => r.json()).catch(() => null);
      if (geo) {
        if (geo.rates && geo.rates["USD/INR"]) {
          const parsedRate = parseFloat(geo.rates["USD/INR"]);
          if (!isNaN(parsedRate) && parsedRate > 0) {
            localStorage.setItem("starx_fx_USD_INR", String(parsedRate));
          }
        }
        if (geo.country) {
          const country = String(geo.country).toUpperCase();
          if (!this.isAuthenticated) {
            localStorage.setItem("starx_detected_country", country);
          }
          const userExplicitChoice = localStorage.getItem("starx_user_explicit_currency");
          if (!userExplicitChoice && !this.isAuthenticated) {
            const localCurr = geo.local_currency || (country === "IN" ? "INR" : "USD");
            localStorage.setItem("starx_display_currency", localCurr);
            this.appState.setSelectedCurrency(localCurr);
          }
        }
      }
    } catch {}
    this.renderHeader();
    this.renderQuickStakeChips();
    await this.loadTerminalExperienceConfig();
    await this.loadPersistedChartState(this.appState.getSelectedSymbol());
    this.chart?.onDrawingsChanged(() => {
      this.scheduleChartStateSave();
      this.renderActiveDrawingChip();
    });
    window.addEventListener("beforeunload", () => {
      if (this.chart) {
        const symbol = this.appState.getSelectedSymbol();
        try {
          localStorage.setItem(`starx_chart_drawings_${symbol}`, JSON.stringify(this.chart.getDrawings()));
        } catch {}
      }
    });
    this.renderActiveDrawingChip();
    this.renderInstrumentTabs();
    const initialInstrument = this.appState
      .getInstruments()
      .find((item) => item.symbol === this.appState.getSelectedSymbol());
    if (initialInstrument) {
      this.chart?.setInstrument(initialInstrument.symbol, initialInstrument.price_decimals);
      this.chart?.setAutoScroll(true);
      this.currentPayoutPercentage = 0;
      const title = document.getElementById("exec-symbol-title");
      const payout = document.getElementById("exec-payout-pct");
      if (title)
        title.innerHTML = `${this.pairIconSVG(initialInstrument.symbol)} ${this.escapeHTML(this.pairLabel(initialInstrument.symbol))}`;
      if (payout)
        payout.textContent = `${this.currentPayoutPercentage.toFixed(0)}%`;
    }
    this.refreshDurationOptions();
    if (this.appState.getMode() === "LIVE") await this.reconcileActiveContracts();
    else this.reconcileDemoContracts();
    this.renderClosedPositions();
    await this.loadCandles();

    // 4. Setup WebSocket listeners
    this.setupWebSocket();
    void this.prewarmOpenSymbolCandles();

    // 4b. Live Realistic Sentiment Engine (StarX Style)
    SentimentEngine.getShared().subscribe((state) => {
      const fillBar = document.getElementById("sentiment-fill-bar");
      const topText = document.getElementById("sentiment-top-text");
      const botText = document.getElementById("sentiment-bot-text");

      if (fillBar) fillBar.style.height = `${state.call}%`;
      if (topText) topText.textContent = `${state.call}%`;
      if (botText) botText.textContent = `${state.put}%`;
    });

    // 5. Periodic polling & background tasks
    void this.loadFirstDepositPromotion();
    void this.refreshPendingOrders();
    void this.fetchDynamicPayout();
    this.payoutRefreshTimer = window.setInterval(() => void this.fetchDynamicPayout(), 15_000);
    void this.refreshEarlyCashoutOffers();
    this.cashoutQuoteRefreshTimer = window.setInterval(() => void this.refreshEarlyCashoutOffers(), 3_000);
    // WebSocket events keep prices real-time; pending/active reconciliation is
    // deliberately bounded so a normal terminal session cannot self-trigger
    // the protected API's per-user velocity policy.
    this.pendingOrderTimer = window.setInterval(() => void this.evaluatePendingOrders(), 3000);
    void this.refreshNotificationBadge();
    this.notificationRefreshTimer = window.setInterval(() => void this.refreshNotificationBadge(), 30_000);
    window.setInterval(() => this.updatePositionCountdowns(), 250);
    window.setInterval(() => this.updateLiveClock(), 1_000);
    this.updateLiveClock();
    this.candleRecovery?.destroy();
    this.candleRecovery = new CandleRecovery({
      pause: () => this.chart?.pauseForRecovery(),
      recover: async () => {
        ++this.candleRequestId;
        this.candleRequests = new CandleRequests();
        this.candleBufferCache.clear();
        this.wsClient.reconnect();
        if (!await this.loadCandles() || !this.chart?.getCandlesCount()) throw new Error('History recovery unavailable');
      },
      ready: () => this.chart?.completeRecovery(),
    });
    if (this.chart) this.chart.onResumeDetected = () => { this.candleRecovery?.suspend(); void this.candleRecovery?.request(); };
    this.candleRecovery.start();

    // 6. Connect WebSocket
    this.wsClient.connect();

    // 7. Landing Page & View Routing Initialization
    this.initLandingPageRouting();

    // 8. Dismiss startup loader smoothly
    this.hideStartupLoader();
  }

  private async checkGeoAccess(): Promise<boolean> {
    try {
      const userTz = typeof Intl !== "undefined" && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone || "" : "";
      const res = await fetch(`/api/v1/auth/geo-check?tz=${encodeURIComponent(userTz)}`, { cache: "no-store" });
      if (!res.ok) return true;
      const data = await res.json();
      if (data && data.is_allowed === false) {
        const countryName = data.country_name || data.detected_country || "Your Region";
        const countryCode = data.detected_country || "";
        this.renderGeoRestrictedScreen(countryName, countryCode);
        return false;
      }
    } catch (err) {
      console.warn("[GeoGate] Geo verification skipped:", err);
    }
    return true;
  }

  private renderGeoRestrictedScreen(countryName: string, countryCode: string) {
    document.body.style.overflow = "hidden";
    // Remove / hide existing loaders & terminal views
    const appLoader = document.getElementById("starx-app-loader");
    if (appLoader) {
      try { appLoader.remove(); } catch {}
    }
    const landing = document.getElementById("landing-page-view");
    if (landing) landing.style.display = "none";
    const terminal = document.getElementById("trading-terminal-view");
    if (terminal) terminal.style.display = "none";

    // Create the branded full-screen restriction overlay
    const overlay = document.createElement("div");
    overlay.id = "starx-geo-restricted-screen";
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 99999999;
      background: radial-gradient(circle at 50% 25%, #151d2a 0%, #090d16 85%);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
      color: #f8fafc;
      text-align: center;
      user-select: none;
    `;

    overlay.innerHTML = `
      <div style="max-width: 560px; width: 100%; background: rgba(18, 24, 38, 0.94); border: 1px solid #1e293b; border-radius: 20px; padding: 44px 32px; box-shadow: 0 30px 70px -15px rgba(0, 0, 0, 0.85); backdrop-filter: blur(16px); display: flex; flex-direction: column; align-items: center;">
        
        <!-- StarX Institutional Brand -->
        <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 32px;">
          <img src="/brand/starx-logo.png" alt="StarX Options" style="height: 38px; width: auto; object-fit: contain;" onerror="this.style.display='none'" />
          <span style="font-size: 22px; font-weight: 900; letter-spacing: 2px; color: #ffffff;">STARX<span style="color: #00d9ff;">OPTIONS</span></span>
        </div>

        <!-- Restriction Shield Emblem -->
        <div style="width: 68px; height: 68px; border-radius: 50%; background: rgba(239, 68, 68, 0.1); border: 1.5px solid rgba(239, 68, 68, 0.35); display: flex; align-items: center; justify-content: center; margin-bottom: 24px;">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
          </svg>
        </div>

        <!-- Primary Notice Header (Exact user requested wording) -->
        <h1 style="font-size: 21px; font-weight: 800; color: #ffffff; line-height: 1.45; margin: 0 0 16px; letter-spacing: -0.2px;">
          Unfortunately, StarX Options is currently not available in your region. (${countryName})
        </h1>

        <!-- Explanatory Compliance Context -->
        <p style="font-size: 14px; line-height: 1.65; color: #94a3b8; margin: 0 0 24px; max-width: 460px;">
          In strict compliance with regional financial licensing requirements and international regulatory frameworks, StarX Options digital contract trading and account onboarding services are prohibited in your country or region.
        </p>

        <!-- Detected Region Badge -->
        <div style="display: inline-flex; align-items: center; gap: 8px; padding: 7px 16px; background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.25); border-radius: 20px; font-size: 12px; color: #fca5a5; font-weight: 600; margin-bottom: 28px;">
          <span style="display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #ef4444;"></span>
          <span>Detected Region: <strong>${countryName}${countryCode ? ` (${countryCode})` : ""}</strong></span>
        </div>

        <!-- Footer Notice -->
        <div style="border-top: 1px solid #1e293b; width: 100%; padding-top: 20px;">
          <p style="font-size: 11px; color: #64748b; line-height: 1.5; margin: 0;">
            If you are accessing from an authorized country or believe this detection is incorrect, contact our international compliance desk at <a href="mailto:support@starxoptions.com" style="color: #38bdf8; text-decoration: none; font-weight: 500;">support@starxoptions.com</a>.
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
  }

  private hideStartupLoader() {
    const loader = document.getElementById("starx-app-loader");
    if (loader && !loader.classList.contains("loaded")) {
      loader.classList.add("loaded");
      setTimeout(() => {
        try { loader.remove(); } catch {}
      }, 400);
    }
  }

  private initLandingPageRouting() {
    // Action: Sign up
    document.querySelectorAll(".qx-action-signup").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.showAuthModal("register");
      });
    });

    // Action: Log in
    document.querySelectorAll(".qx-action-login").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.showAuthModal("login");
      });
    });

    // Action: Demo account / Try demo / Mockup click
    document.querySelectorAll(".qx-action-demo").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.switchToTradingTerminal("DEMO");
      });
    });

    // Action: Service agreement modal
    document.querySelectorAll(".qx-action-service-agreement").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.openServiceAgreementModal();
      });
    });

    // FAQ Accordion toggles
    document.querySelectorAll(".qx-faq-header").forEach((header) => {
      header.addEventListener("click", () => {
        const item = header.closest(".qx-faq-item");
        if (item) {
          const isActive = item.classList.contains("active");
          document.querySelectorAll(".qx-faq-item").forEach((it) => it.classList.remove("active"));
          if (!isActive) {
            item.classList.add("active");
          }
        }
      });
    });

    // Terminal top-bar return to landing page
    document.getElementById("nav-landing-btn")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.switchToLandingPage();
    });
    document.getElementById("terminal-brand-logo")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.switchToLandingPage();
    });
    document.getElementById("landing-logo-btn")?.addEventListener("click", (e) => {
      e.preventDefault();
      const isTrade = document.documentElement.getAttribute("data-view") === "trade";
      if (isTrade) {
        this.switchToLandingPage();
      } else {
        const landingView = document.getElementById("landing-page-view");
        if (landingView && landingView.scrollTop > 0) {
          landingView.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    });

    // History popstate
    window.addEventListener("popstate", () => {
      if (window.location.pathname === "/trade" || window.location.search.includes("trade=true")) {
        this.switchToTradingTerminal();
      } else {
        this.switchToLandingPage();
      }
    });

    // Initial route check
    const urlParams = new URLSearchParams(window.location.search);
    const hasToken = typeof localStorage !== "undefined" && Boolean(localStorage.getItem("starx_auth_token") || localStorage.getItem("starx_session_token"));
    const savedMode = typeof localStorage !== "undefined" ? (localStorage.getItem("starx_trading_mode") || localStorage.getItem("starx_active_mode")) : null;
    const isTradePath = window.location.pathname === "/trade" || 
                        urlParams.get("trade") === "true" ||
                        urlParams.get("mode") === "demo" ||
                        hasToken;
    if (isTradePath) {
      if (hasToken) {
        const targetMode = (savedMode === "DEMO" || urlParams.get("mode") === "demo") ? "DEMO" : "LIVE";
        this.switchToTradingTerminal(targetMode);
      } else if (urlParams.get("mode") === "demo") {
        this.switchToTradingTerminal("DEMO");
      } else {
        this.switchToTradingTerminal();
      }
    } else {
      this.switchToLandingPage();
    }
  }

  public switchToTradingTerminal(mode?: "LIVE" | "DEMO") {
    if (typeof window !== "undefined") {
      const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        window.location.href = "/mobile/";
        return;
      }
    }
    if (mode) {
      this.appState.setMode(mode);
    }
    if (typeof (window as any).setStarXView === "function") {
      (window as any).setStarXView("trade", true);
    } else {
      document.documentElement.setAttribute("data-view", "trade");
      document.documentElement.setAttribute("data-initial-view", "trade");
      const landingView = document.getElementById("landing-page-view");
      const terminalView = document.getElementById("trading-terminal-view");
      if (landingView) landingView.style.setProperty("display", "none", "important");
      if (terminalView) terminalView.style.setProperty("display", "flex", "important");
      if (typeof window !== "undefined" && window.location.pathname !== "/trade") {
        window.history.pushState({ view: "trade" }, "StarX Option — Terminal", "/trade");
      }
    }
    setTimeout(() => {
      try {
        (this.chart as any)?.resize?.();
      } catch {}
    }, 50);
  }

  public switchToLandingPage() {
    if (typeof (window as any).setStarXView === "function") {
      (window as any).setStarXView("landing", true);
    } else {
      const isAlreadyLanding = document.documentElement.getAttribute("data-view") === "landing";
      document.documentElement.setAttribute("data-view", "landing");
      document.documentElement.setAttribute("data-initial-view", "landing");
      const landingView = document.getElementById("landing-page-view");
      const terminalView = document.getElementById("trading-terminal-view");
      if (landingView) {
        const wasHidden = landingView.style.display === "none";
        landingView.style.setProperty("display", "block", "important");
        if (wasHidden && !isAlreadyLanding) {
          landingView.scrollTop = 0;
        }
      }
      if (terminalView) terminalView.style.setProperty("display", "none", "important");
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.history.pushState({ view: "landing" }, "StarX Option — Smart Investments", "/");
      }
    }
  }

  private async resolveSession(): Promise<boolean> {
    // 1. Check if token exists in localStorage or query params
    let token = typeof localStorage !== "undefined" ? (localStorage.getItem("starx_auth_token") || localStorage.getItem("starx_session_token")) : null;
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const queryToken = params.get("token") || params.get("access_token");
      if (queryToken) {
        token = queryToken;
        try { localStorage.setItem("starx_auth_token", token); } catch {}
        if (params.get("auth_source") === "google") {
          setTimeout(() => {
            this.playChime(587.33, "sine", 0.25);
            this.showDashboardNotice("✅ Signed in successfully with Google!", "success");
          }, 600);
        }
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const authErr = params.get("auth_error");
      if (authErr) {
        setTimeout(() => {
          this.showDashboardNotice("⚠️ Google authentication was cancelled or could not be completed.", "warning");
        }, 600);
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const verified = params.get("verified");
      if (verified === "true") {
        setTimeout(() => {
          this.playChime(587.33, "sine", 0.25);
          this.showDashboardNotice("✅ Email verified successfully! Your StarX Live Account is fully activated.", "success");
        }, 600);
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (verified === "invalid") {
        setTimeout(() => {
          this.showDashboardNotice("⚠️ Email verification link is expired or invalid.", "warning");
        }, 600);
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const resetToken = params.get("reset_token");
      if (resetToken) {
        setTimeout(() => {
          this.showAuthModal("reset", undefined, resetToken);
        }, 500);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    if (token) {
      this.restClient.setToken(token);
      try {
        this.currentProfile = await this.restClient.getProfile();
        this.isAuthenticated = true;
        const savedMode = typeof localStorage !== "undefined" ? (localStorage.getItem("starx_trading_mode") || localStorage.getItem("starx_active_mode")) : null;
        const targetMode = savedMode === "DEMO" ? "DEMO" : "LIVE";
        this.appState.setMode(targetMode);
        return true;
      } catch (e) {
        console.warn("Stored session invalid or expired; continuing as guest demo:", e);
        try { localStorage.removeItem("starx_auth_token"); } catch {}
        this.restClient.setToken("");
        this.currentProfile = null;
        this.isAuthenticated = false;
      }
    }

    // Cross-device and cross-tab synchronization
    if (typeof window !== "undefined") {
      if (typeof BroadcastChannel !== "undefined") {
        try {
          const rewardsChan = new BroadcastChannel("starx_rewards_sync");
          rewardsChan.onmessage = async (event) => {
            const data = event.data;
            if (!data) return;
            if (data.type === "GEMS_CLAIMED" || data.type === "GEMS_UPDATED") {
              if (typeof data.gems === "number") this.userGemsBalance = data.gems;
              if (typeof data.streak === "number") this.loginStreak = data.streak;
              if (typeof data.claimedToday === "boolean") this.claimedStreakToday = data.claimedToday;
              const drawer = document.getElementById("experience-drawer");
              const drawerTitle = document.getElementById("experience-drawer-title")?.textContent || "";
              if (drawer?.classList.contains("open") && drawerTitle.includes("Rewards")) {
                void this.showGemsStore();
              }
            } else if (data.type === "PERK_REDEEMED") {
              if (typeof data.gems_balance === "number") this.userGemsBalance = data.gems_balance;
              if (data.perk) {
                const existing = this.userRedeemedTokens.find(t => t.id === data.perk.id);
                if (!existing) this.userRedeemedTokens.push(data.perk);
              } else if (Array.isArray(data.tokens)) {
                this.userRedeemedTokens = data.tokens;
              }
              try { localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens)); } catch {}
              this.updateRiskFreeUI();
              const drawer = document.getElementById("experience-drawer");
              const drawerTitle = document.getElementById("experience-drawer-title")?.textContent || "";
              if (drawer?.classList.contains("open") && drawerTitle.includes("Rewards")) {
                void this.showGemsStore();
              }
            } else if (data.type === "PERK_APPLIED") {
              this.activeRiskFreeToken = data.token;
              try { localStorage.setItem("starx_active_risk_free_token", JSON.stringify(data.token)); } catch {}
              this.updateRiskFreeUI();
              const drawer = document.getElementById("experience-drawer");
              const drawerTitle = document.getElementById("experience-drawer-title")?.textContent || "";
              if (drawer?.classList.contains("open") && drawerTitle.includes("Rewards")) {
                void this.showGemsStore();
              }
            } else if (data.type === "PERK_UNAPPLIED") {
              this.activeRiskFreeToken = null;
              try { localStorage.removeItem("starx_active_risk_free_token"); } catch {}
              this.updateRiskFreeUI();
              const drawer = document.getElementById("experience-drawer");
              const drawerTitle = document.getElementById("experience-drawer-title")?.textContent || "";
              if (drawer?.classList.contains("open") && drawerTitle.includes("Rewards")) {
                void this.showGemsStore();
              }
            } else if (data.type === "PERK_CONSUMED") {
              this.userRedeemedTokens = this.userRedeemedTokens.filter(t => t.id !== data.tokenId);
              if (this.activeRiskFreeToken && this.activeRiskFreeToken.id === data.tokenId) {
                this.activeRiskFreeToken = null;
                try { localStorage.removeItem("starx_active_risk_free_token"); } catch {}
              }
              try { localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens)); } catch {}
              this.updateRiskFreeUI();
              const drawer = document.getElementById("experience-drawer");
              const drawerTitle = document.getElementById("experience-drawer-title")?.textContent || "";
              if (drawer?.classList.contains("open") && drawerTitle.includes("Rewards")) {
                void this.showGemsStore();
              }
            } else if (data.type === "BALANCE_UPDATED" || data.type === "BONUS_CLAIMED" || data.type === "DEPOSIT_CONFIRMED") {
              await this.appState.refreshLiveState();
              this.renderHeader();
              void this.loadFirstDepositPromotion();
            }
          };

          const authChan = new BroadcastChannel("starx_auth_sync");
          authChan.onmessage = (event) => {
            if (event.data?.type === "SIGNOUT") {
              try {
                localStorage.removeItem("starx_auth_token");
                localStorage.removeItem("starx_session_token");
                localStorage.removeItem("starx_user_profile");
              } catch {}
              this.restClient.setToken("");
              this.isAuthenticated = false;
              this.currentProfile = null;
              this.appState.setMode("DEMO");
              this.renderHeader();
              this.showDashboardNotice("Signed out from another window.", "info");
            }
          };

          const bannerChan = new BroadcastChannel("starx_banner_sync");
          bannerChan.onmessage = (event) => {
            if (event.data?.type === "BANNER_DISMISSED" || event.data?.type === "DISMISS_FIRST_DEPOSIT_BANNER") {
              document.getElementById("first-deposit-promo")?.classList.remove("show");
            }
          };

          const tournChan = new BroadcastChannel("starx_tournaments_sync");
          tournChan.onmessage = () => {
            const drawer = document.getElementById("experience-drawer");
            const heading = document.getElementById("experience-drawer-title");
            if (drawer && drawer.classList.contains("open") && heading?.textContent?.includes("Tournaments")) {
              void this.showTournamentCenter();
            }
          };
        } catch {}
      }
      window.addEventListener("storage", (e) => {
        if (e.key === "starx_auth_token" && !e.newValue) {
          try {
            localStorage.removeItem("starx_auth_token");
            localStorage.removeItem("starx_session_token");
            localStorage.removeItem("starx_user_profile");
          } catch {}
          this.restClient.setToken("");
          this.isAuthenticated = false;
          this.currentProfile = null;
          this.appState.setMode("DEMO");
          this.renderHeader();
          this.showDashboardNotice("Signed out from another window.", "info");
        }
        if (e.key === "starx_first_deposit_banner_dismissed" && e.newValue === "true") {
          document.getElementById("first-deposit-promo")?.classList.remove("show");
        }
        if (e.key === "starx_gems_balance" || e.key === "starx_claimed_today") {
          const drawer = document.getElementById("experience-drawer");
          const drawerTitle = document.getElementById("experience-drawer-title")?.textContent || "";
          if (drawer?.classList.contains("open") && drawerTitle.includes("Rewards")) {
            void this.showGemsStore();
          }
        }
      });

      try {
        const storedTokens = localStorage.getItem("starx_redeemed_tokens");
        if (storedTokens) this.userRedeemedTokens = JSON.parse(storedTokens);
        const storedActive = localStorage.getItem("starx_active_risk_free_token");
        if (storedActive) this.activeRiskFreeToken = JSON.parse(storedActive);
        const storedGems = localStorage.getItem("starx_gems_balance");
        if (storedGems) {
          const parsedG = parseInt(storedGems, 10);
          if (!isNaN(parsedG)) this.userGemsBalance = parsedG;
        }
      } catch {}
      this.updateRiskFreeUI();
    }

    // 2. Explicit development auth helper for automated test suites only
    const isDevAuthExplicitlyEnabled =
      typeof window !== "undefined" &&
      Boolean((window as any).__STARX_DEV_AUTH__);

    if (isDevAuthExplicitlyEnabled) {
      try {
        const devAuth = await this.restClient.getDevToken("00000000-0000-0000-0000-000000000002");
        if (devAuth && devAuth.token) {
          this.restClient.setToken(devAuth.token);
          this.currentProfile = await this.restClient.getProfile().catch(() => null);
          this.isAuthenticated = true;
          const savedMode = typeof localStorage !== "undefined" ? (localStorage.getItem("starx_trading_mode") || localStorage.getItem("starx_active_mode")) : null;
          const targetMode = savedMode === "DEMO" ? "DEMO" : "LIVE";
          this.appState.setMode(targetMode);
          return true;
        }
      } catch {}
    }

    // 3. Default: Guest Mode (Practice on Demo Account strictly isolated, no redirection)
    this.isAuthenticated = false;
    this.currentProfile = null;
    this.appState.setMode("DEMO");
    return true;
  }

  private async reconcileActiveContracts() {
    try {
      const active = await this.orderStore.reconcileActive();
      if (active.length) {
        this.renderActivePositions();
      }
    } catch (err) {
      console.warn("Failed to reconcile active contracts:", err);
    }
  }

  private reconcileDemoContracts() {
    const activeDemo = this.appState.getDemoActiveContracts();
    for (const contract of activeDemo) {
      if (contract.status === "ACTIVE") {
        this.orderStore.setActiveContract(contract);
        this.scheduleDemoSettlement(contract);
      }
    }
    this.renderActivePositions();
    this.renderClosedPositions();
    this.syncChartTradeOverlays();
  }

  private scheduleDemoSettlement(demoContract: Contract) {
    const expiryMs = new Date(demoContract.expiry_timestamp).getTime();
    const delayMs = Math.max(0, expiryMs - Date.now());
    const symbol = demoContract.symbol;
    const strikePrice = demoContract.strike_price;
    const direction = demoContract.direction;
    const stakeNumber = Number(demoContract.stake);
    const payoutPct = String(demoContract.payout_percentage || "80");

    window.setTimeout(() => {
      if (demoContract.status !== "ACTIVE") return;
      const latestTick =
        symbol === this.appState.getSelectedSymbol()
          ? this.chart?.getLatestPrice() || strikePrice
          : strikePrice;
      const isWin =
        (direction === "CALL" && latestTick > strikePrice) ||
        (direction === "PUT" && latestTick < strikePrice);
      const isTie = latestTick === strikePrice;
      const totalPayout = isWin
        ? (stakeNumber * (1 + Number(payoutPct) / 100)).toFixed(2)
        : isTie
          ? demoContract.stake
          : "0.00";
      demoContract.status = isWin
        ? "SETTLED_WIN"
        : isTie
          ? "SETTLED_TIE"
          : "SETTLED_LOSS";
      demoContract.expiry_price = latestTick;
      demoContract.actual_payout = totalPayout;
      demoContract.settled_at = demoContract.expiry_timestamp;
      demoContract.settlement_evidence_hash = "";
      if (Number(totalPayout) > 0)
        this.appState.creditDemoPayout(Number(totalPayout));

      // Handle Risk-Free Trade Protection Refund on Loss
      if (demoContract.is_risk_free) {
        if (!isWin && !isTie) {
          const curr = this.appState.getSelectedCurrency() || "USD";
          const fxRate = getLiveUsdFxRate(curr);
          const currSym = getCurrencySymbol(curr);
          const maxRefUSD = Number(demoContract.risk_free_max_refund || 10);
          const convertedCap = Math.round(maxRefUSD * fxRate);
          const refund = Math.min(stakeNumber, convertedCap);
          this.appState.creditDemoPayout(refund);
          this.showDashboardNotice(`🛡️ 100% Risk-Free Protection Applied: ${currSym}${refund.toLocaleString()} refunded back to your balance!`, "success");
        } else if (isWin) {
          this.showDashboardNotice("🎉 Risk-Free Trade Won! Full profit credited to your balance.", "success");
        }
        if (demoContract.risk_free_token_id) {
          this.userRedeemedTokens = this.userRedeemedTokens.filter(t => t.id !== demoContract.risk_free_token_id);
          try {
            localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens));
            const channel = new BroadcastChannel("starx_rewards_sync");
            channel.postMessage({ type: "PERK_CONSUMED", tokenId: demoContract.risk_free_token_id });
            channel.close();
          } catch {}
          void this.restClient.consumeGemsPerk(demoContract.risk_free_token_id);
        }
        this.updateRiskFreeUI();
      }

      this.appState.removeDemoActiveContract(demoContract.contract_id);
      this.appState.addDemoContractHistory(demoContract);
      this.orderStore.handleSettlement(demoContract);
      this.renderClosedPositions();
      if (
        this.appState.getMode() === "DEMO" &&
        !this.terminalPreferences.hide_trade_results
      )
        this.showSettlementToast(demoContract);
    }, delayMs);
  }

  private getCacheKey(symbol: string, tf: string = this.selectedTimeframe): string {
    return `${symbol}:${tf}`;
  }

  private getCachedCandles(symbol: string, tf: string = this.selectedTimeframe): Candle[] | null {
    const key = this.getCacheKey(symbol, tf);
    if (this.candleBufferCache.has(key)) {
      return this.candleBufferCache.get(key)!;
    }
    try {
      const raw = sessionStorage.getItem(`starx_candles_${key}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const lastCandle = parsed[parsed.length - 1];
          const lastTimeSec = lastCandle.time > 1e11 ? Math.floor(lastCandle.time / 1000) : (lastCandle.time || 0);
          const nowSec = Math.floor(Date.now() / 1000);
          const tfSec = this.timeframeSeconds(tf as Timeframe);
          if (lastTimeSec > 0 && nowSec - lastTimeSec > tfSec * 3) {
            sessionStorage.removeItem(`starx_candles_${key}`);
            return null;
          }
          this.candleBufferCache.set(key, parsed);
          return parsed;
        }
      }
    } catch {
      // ignore parse errors
    }
    return null;
  }

  private setCachedCandles(symbol: string, tf: string, candles: Candle[]) {
    const key = this.getCacheKey(symbol, tf);
    this.candleBufferCache.set(key, candles);
    try {
      sessionStorage.setItem(`starx_candles_${key}`, JSON.stringify(candles.slice(-150)));
    } catch {
      // ignore storage quota errors
    }
  }

  private async prewarmOpenSymbolCandles() {
    const active = this.appState.getSelectedSymbol();
    const tf = this.selectedTimeframe;
    const targets = this.openSymbols.filter(
      (sym) => sym !== active && !this.candleBufferCache.has(this.getCacheKey(sym, tf)),
    );
    for (const sym of targets) {
      try {
        const data = await this.restClient.getCandles(sym, tf, 150);
        if (data && data.candles && data.candles.length > 0) {
          this.setCachedCandles(sym, tf, data.candles);
        }
      } catch {
        // non-blocking background pre-warm
      }
    }
  }

  private loadCandles(targetSymbol?: string): Promise<boolean> {
    this.candleRequests ??= new CandleRequests();
    const key = (targetSymbol || this.appState.getSelectedSymbol()) + ':' + this.selectedTimeframe;
    return this.candleRequests.run(key, () => this.fetchCandleHistory(targetSymbol));
  }

  private async fetchCandleHistory(targetSymbol?: string): Promise<boolean> {
    const requestId = ++this.candleRequestId;
    const revision = this.chart?.getHistoryRevision() || 0;
    try {
      const symbol = targetSymbol || this.appState.getSelectedSymbol();
      const tf = this.selectedTimeframe;
      const data = await this.restClient.getCandles(
        symbol,
        tf,
        150,
      );
      if (data && data.candles && this.chart) {
        if (requestId !== this.candleRequestId || tf !== this.selectedTimeframe
          || symbol !== this.appState.getSelectedSymbol()) return false;
        this.setCachedCandles(symbol, tf, data.candles);
        if (symbol === this.appState.getSelectedSymbol()) {
          this.chart.mergeHistory(data.candles, revision);
          this.setCachedCandles(symbol, tf, this.chart.getCandles());
          this.populatePendingQuoteFromSelectedPair(false);
          this.syncChartTradeOverlays();
        }
        return true;
      }
      return false;
    } catch (err) {
      console.warn("Failed to load initial candles:", err);
      return false;
    }
  }

  private async loadTerminalExperienceConfig() {
    try {
      const [published, preferences, featureSettings] = await Promise.all([
        this.restClient.getTerminalConfig().catch(() => null),
        this.restClient.getTerminalPreferences().catch(() => null),
        this.restClient.getTenantSettings("TERMINAL_FEATURE").catch(() => ({ items: [] })),
      ]);
      const config = published?.config || {};
      const branding = published?.branding || {};
      if (branding.brand_name) {
        document.title = `${branding.brand_name} — Institutional Binary Option Trading Terminal`;
      }
      if (branding.primary_color) {
        document.documentElement.style.setProperty("--blue-accent", branding.primary_color);
        document.documentElement.style.setProperty("--border-focus", branding.primary_color);
      }
      if (branding.accent_color) {
        document.documentElement.style.setProperty("--border-focus", branding.accent_color);
      }
      if (typeof config.synthetic_feed_wording_enabled === "boolean") {
        this.syntheticFeedWordingEnabled = config.synthetic_feed_wording_enabled;
      }
      this.terminalPreferences = {
        ...this.terminalPreferences,
        ...(preferences?.preferences || {}),
      };
      if (Array.isArray(this.terminalPreferences.open_symbols)) {
        const available = new Set(
          this.appState.getInstruments().map((instrument) => instrument.symbol),
        );
        const locallyRestored = this.openSymbols;
        this.openSymbols = [...locallyRestored, ...this.terminalPreferences.open_symbols]
          .map(String)
          .filter((symbol: string, index: number, list: string[]) =>
            available.has(symbol) && list.indexOf(symbol) === index,
          )
          .slice(0, 12);
      }
      let deviceSelectedSymbol = "";
      try {
        deviceSelectedSymbol = localStorage.getItem("starx_trading_selected_symbol") || "";
      } catch {
        // Server preference is used when device storage is unavailable.
      }
      let symbolToUse = String(
        deviceSelectedSymbol ||
          this.terminalPreferences.symbol ||
          config.default_symbol ||
          this.appState.getSelectedSymbol() ||
          "",
      );
      if (this.isWeekendSession() && symbolToUse) {
        const isOtc = symbolToUse.endsWith("_OTC") || symbolToUse.includes("OTC");
        if (!isOtc) {
          const otcCandidate = `${symbolToUse}_OTC`;
          if (this.appState.getInstruments().some((i) => i.symbol === otcCandidate)) {
            symbolToUse = otcCandidate;
          } else {
            const firstOtc = this.appState.getInstruments().find((i) => i.symbol.endsWith("_OTC"))?.symbol;
            if (firstOtc) symbolToUse = firstOtc;
          }
        }
      }
      if (
        symbolToUse &&
        this.appState
          .getInstruments()
          .some((instrument) => instrument.symbol === symbolToUse)
      ) {
        this.appState.setSelectedSymbol(symbolToUse);
      }
      // Every new terminal page starts on M1. A user may select any supported
      // timeframe after startup, but an old saved preference must not override
      // the product-wide initial candle period.
      this.selectedTimeframe = "1m";
      this.chart?.setTimeframe("1m");
      const chartType = String(
        this.terminalPreferences.chart_type ||
          config.default_chart_type ||
          "CANDLESTICK",
      ).toUpperCase();
      if (["CANDLESTICK", "LINE", "AREA", "BARS"].includes(chartType)) {
        this.chart?.setChartType(chartType as any);
      }
      const chartLabel = document.getElementById("current-chart-type-label");
      if (chartLabel)
        chartLabel.textContent =
          chartType === "CANDLESTICK"
            ? "Candles"
            : chartType === "BARS"
              ? "Bars"
              : chartType.charAt(0) + chartType.slice(1).toLowerCase();
      const timeframeLabel = document.getElementById("current-timeframe-label");
      if (timeframeLabel) timeframeLabel.textContent = `${this.selectedTimeframe} ▾`;
      document.querySelectorAll<HTMLElement>("#timeframe-popover .popover-item").forEach((item) =>
        item.classList.toggle("active", item.dataset.tf === this.selectedTimeframe),
      );
      this.applyPlatformPreferenceEffects();
      this.chart?.setAutoScroll(this.terminalPreferences.auto_scroll !== false);
      const enabled = Array.isArray(this.terminalPreferences.active_indicators)
        ? this.terminalPreferences.active_indicators
        : [];
      this.chart
        ?.getActiveIndicators()
        .forEach((id) => this.chart?.setIndicatorEnabled(id, false));
      enabled.forEach((id: IndicatorId) =>
        this.chart?.setIndicatorEnabled(id, true),
      );
      const featureMap = new Map(
        (featureSettings.items || []).map((item: any) => [String(item.setting_key), item.enabled !== false]),
      );
      this.cashoutFeatureEnabled = featureMap.get("EARLY_CASHOUT") !== false;
      const featureTargets: Record<string, string[]> = {
        TECHNICAL_INDICATORS: ["indicators-toggle-btn", "indicators-drawer", "btn-indicators-tool"],
        DRAWING_TOOLS: ["drawing-toggle-btn", "drawing-drawer", "btn-drawing-tool"],
        VOLUME_PANEL: ["volume-toggle-btn", "volume-panel"],
        SOCIAL_CHANNELS: ["social-toggle-btn", "social-panel", "social-nav-btn"],
        TOURNAMENTS: ["tournaments-nav-btn", "tournaments-drawer"],
        LEADERBOARD: ["ranks-nav-btn", "leaderboard-drawer"],
        MARKET_EXPLORER: ["market-nav-btn", "market-drawer", "btn-add-asset", "add-asset-btn"],
        BONUS_PROMOTIONS: ["first-deposit-promo", "bonus-promo-banner"],
        PENDING_TRADES: ["tab-btn-pending", "pending-trade-row", "pending-trade-config", "pending-list-container"],
        PROFILE_CENTER: ["compliance-nav-btn", "profile-drawer", "support-nav-btn"],
        PLATFORM_OPTIONS: ["config-nav-btn", "settings-drawer"],
      };
      Object.entries(featureTargets).forEach(([key, ids]) => {
        const isEnabled = featureMap.has(key) ? featureMap.get(key) !== false : true;
        ids.forEach((id) => {
          const element = document.getElementById(id);
          if (element) {
            element.style.removeProperty("display");
            element.hidden = !isEnabled;
          }
          document.querySelectorAll(`.${id}`).forEach((el) => {
            (el as HTMLElement).style.removeProperty("display");
            (el as HTMLElement).hidden = !isEnabled;
          });
        });
      });
      const tzInfo = this.getActiveTimezoneInfo();
      this.chart?.setTimezone(tzInfo.iana, tzInfo.label);
      const tzSelect = document.getElementById("timezone-select") as HTMLSelectElement | null;
      if (tzSelect) {
        tzSelect.value = tzInfo.name;
      }
      this.renderPendingTimezoneBadge();
    } catch (error) {
      console.warn("Authoritative terminal preferences unavailable:", error);
    }
  }

  private async persistTerminalPreferences() {
    const snapshot = JSON.parse(JSON.stringify(this.terminalPreferences));
    this.preferenceSaveChain = this.preferenceSaveChain.then(async () => {
      try {
        await this.restClient.saveTerminalPreferences(snapshot);
      } catch (error) {
        console.warn("Terminal preference save failed:", error);
      }
    });
    await this.preferenceSaveChain;
  }

  private persistMarketWorkspaceLocally() {
    try {
      localStorage.setItem(
        "starx-trading-open-symbols",
        JSON.stringify(this.openSymbols),
      );
      localStorage.setItem(
        "starx_trading_selected_symbol",
        this.appState.getSelectedSymbol(),
      );
    } catch {
      // Server-side preferences remain authoritative when storage is unavailable.
    }
  }

  private async loadPersistedChartState(symbol: string) {
    if (!this.chart) return;
    const selectionId = this.instrumentSelectionId;
    const localKey = `starx_chart_drawings_${symbol}`;
    let localDrawings: any[] | null = null;
    try {
      const localData = localStorage.getItem(localKey);
      if (localData) {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed) && symbol === this.appState.getSelectedSymbol() && selectionId === this.instrumentSelectionId) {
          localDrawings = parsed;
          this.chart.setDrawings(parsed, false);
          this.renderActiveDrawingChip();
        }
      }
    } catch {}

    if (!this.restClient.getToken()) return;

    try {
      const response = await this.restClient.getChartLayouts().catch(() => null);
      if (symbol !== this.appState.getSelectedSymbol() || selectionId !== this.instrumentSelectionId) return;
      const layouts = Array.isArray(response?.layouts) ? response.layouts : [];
      const layout = layouts.find((item: any) => item.symbol === symbol && item.name === "AUTOSAVE") || layouts.find((item: any) => item.symbol === symbol);
      if (layout?.drawings && Array.isArray(layout.drawings)) {
        if (layout.drawings.length > 0) {
          this.chart.setDrawings(layout.drawings, false);
          this.renderActiveDrawingChip();
          try { localStorage.setItem(localKey, JSON.stringify(layout.drawings)); } catch {}
        } else if (localDrawings && localDrawings.length > 0) {
          void this.saveCurrentChartState(symbol);
        }
      }
    } catch (error) {
      console.warn("Saved chart drawings unavailable:", error);
    }
  }

  private scheduleChartStateSave() {
    if (!this.chart) return;
    const symbol = this.appState.getSelectedSymbol();
    const drawings = this.chart.getDrawings();
    try {
      localStorage.setItem(`starx_chart_drawings_${symbol}`, JSON.stringify(drawings));
    } catch {}
    if (this.drawingSaveTimer) window.clearTimeout(this.drawingSaveTimer);
    this.drawingSaveTimer = window.setTimeout(() => void this.saveCurrentChartState(symbol), 250);
  }

  private async saveCurrentChartState(symbol: string = this.appState.getSelectedSymbol()) {
    if (!this.chart) return;
    const drawings = this.chart.getDrawings();
    try {
      localStorage.setItem(`starx_chart_drawings_${symbol}`, JSON.stringify(drawings));
    } catch {}

    if (!this.restClient.getToken()) return;
    try {
      await this.restClient.saveChartLayout({
        name: "AUTOSAVE",
        symbol,
        timeframe: this.selectedTimeframe,
        chart_type: String(this.terminalPreferences.chart_type || "CANDLESTICK"),
        indicators: this.chart.getActiveIndicators(),
        drawings,
        is_default: false,
      }).catch((err) => {
        console.warn("Autosave chart layout error:", err);
      });
    } catch (error) {
      console.warn("Autosave chart layout error:", error);
    }
  }

  private pairLabel(symbol: string): string {
    const clean = symbol.replace(/_OTC$/, "");
    const aliases: Record<string, string> = {
      EURUSD: "EUR/USD",
      GBPUSD: "GBP/USD",
      USDJPY: "USD/JPY",
      AUDUSD: "AUD/USD",
      BTCUSD: "Bitcoin / USD",
      ETHUSD: "Ethereum / USD",
      SOLUSD: "Solana / USD",
      XRPUSD: "XRP / USD",
      XAUUSD: "Gold / USD",
    };
    const generic = /^[A-Z]{6}$/.test(clean)
      ? `${clean.slice(0, 3)}/${clean.slice(3)}`
      : clean;
    return `${aliases[clean] || generic}${symbol.endsWith("_OTC") ? " (OTC)" : ""}`;
  }

  private isWeekendSession(): boolean {
    const now = new Date();
    const day = now.getUTCDay(); // 0 is Sunday, 5 is Friday, 6 is Saturday
    const hour = now.getUTCHours();
    return (day === 5 && hour >= 21) || day === 6 || (day === 0 && hour < 21);
  }

  private pairIconSVG(symbol: string): string {
    return renderPairIconSVG(symbol, { className: "pair-svg" });
  }

  private renderInstrumentTabs() {
    const container = document.getElementById("asset-tabs");
    if (!container) return;
    const allInstruments = this.appState.getInstruments();
    const instruments = allInstruments.filter((i) => i.symbol.endsWith("_OTC") || (i as any).is_otc);
    let selected = this.appState.getSelectedSymbol();
    if (!selected.endsWith("_OTC") && !(instruments.find((i) => i.symbol === selected) as any)?.is_otc) {
      selected = "EURUSD_OTC";
      this.appState.setSelectedSymbol(selected);
    }
    this.openSymbols = this.openSymbols.filter((s) => s.endsWith("_OTC") || instruments.some((i) => i.symbol === s));
    if (!this.openSymbols.length) {
      this.openSymbols = [
        selected,
        ...instruments.slice(0, 5).map((instrument) => instrument.symbol),
      ].filter((symbol, index, list) => list.indexOf(symbol) === index);
    }
    if (!this.openSymbols.includes(selected)) this.openSymbols.unshift(selected);
    this.persistMarketWorkspaceLocally();
    const bySymbol = new Map(instruments.map((instrument) => [instrument.symbol, instrument]));
    const ordered = this.openSymbols
      .map((symbol) => bySymbol.get(symbol))
      .filter((instrument): instrument is (typeof instruments)[number] => Boolean(instrument));
    container.innerHTML = ordered
      .map((instrument) => {
        const payout = indicativePayouts.label(instrument.symbol);
        return `<div class="asset-tab${instrument.symbol === selected ? " active" : ""}" data-symbol="${this.escapeHTML(instrument.symbol)}" data-payout="${payout}"><button type="button" class="asset-tab-main" aria-label="Open ${this.escapeHTML(this.pairLabel(instrument.symbol))}">${this.pairIconSVG(instrument.symbol)}<span>${this.escapeHTML(this.pairLabel(instrument.symbol))}</span><span class="payout-tag" data-live-payout-symbol="${this.escapeHTML(instrument.symbol)}">${payout}%</span></button><button type="button" class="asset-tab-close" aria-label="Close ${this.escapeHTML(this.pairLabel(instrument.symbol))}" title="Close pair">×</button></div>`;
      })
      .join("");
  }

  private async closeInstrumentTab(symbol: string) {
    if (this.openSymbols.length <= 1) return;
    const closingSelected = symbol === this.appState.getSelectedSymbol();
    this.openSymbols = this.openSymbols.filter((item) => item !== symbol);
    this.terminalPreferences.open_symbols = [...this.openSymbols];
    if (closingSelected) {
      await this.selectInstrument(this.openSymbols[0]);
    } else {
      this.renderInstrumentTabs();
    }
    this.persistMarketWorkspaceLocally();
    void this.persistTerminalPreferences();
  }

  private async selectInstrument(symbol: string) {
    const instrument = this.appState
      .getInstruments()
      .find((item) => item.symbol === symbol);
    if (!instrument) return;
    const previousSymbol = this.appState.getSelectedSymbol();
    if (previousSymbol === symbol) return;
    const selectionId = ++this.instrumentSelectionId;
    if (previousSymbol !== symbol) {
      void this.saveCurrentChartState(previousSymbol);
      if (this.chart) {
        const prevCandles = this.chart.getCandles();
        if (prevCandles && prevCandles.length > 0) {
          const prevKey = this.getCacheKey(previousSymbol, this.selectedTimeframe);
          this.candleBufferCache.set(prevKey, prevCandles);
        }
      }
    }
    if (!this.openSymbols.includes(symbol)) this.openSymbols.push(symbol);
    this.terminalPreferences.open_symbols = [...this.openSymbols];
    this.terminalPreferences.symbol = symbol;
    this.appState.setSelectedSymbol(symbol);
    this.persistMarketWorkspaceLocally();
    this.chart?.setInstrument(symbol, instrument.price_decimals);
    this.chart?.setDrawings([], false);
    this.renderActiveDrawingChip();
    this.chart?.setAutoScroll(true);
    this.currentPayoutPercentage = 0;
    this.refreshDurationOptions();

    // DOUBLE BUFFERING: Swap to cached candles immediately for 0ms transition!
    const cached = this.getCachedCandles(symbol, this.selectedTimeframe);
    if (cached && cached.length > 0) {
      this.chart?.setCandles(cached);
      this.populatePendingQuoteFromSelectedPair(false);
    } else {
      this.chart?.setCandles([]);
    }
    this.syncChartTradeOverlays();

    this.wsClient.setSymbol(symbol);
    this.renderInstrumentTabs();
    const title = document.getElementById("exec-symbol-title");
    const payout = document.getElementById("exec-payout-pct");
    if (title)
      title.innerHTML = `${this.pairIconSVG(symbol)} ${this.escapeHTML(this.pairLabel(symbol))}`;
    if (payout)
      payout.textContent = `${this.currentPayoutPercentage.toFixed(0)}%`;

    const [, , session] = await Promise.all([
      this.loadCandles(symbol),
      this.loadPersistedChartState(symbol),
      this.restClient.getInstrumentSession(symbol).catch(() => null),
    ]);
    if (symbol !== this.appState.getSelectedSymbol() || selectionId !== this.instrumentSelectionId) return;
    this.renderActiveDrawingChip();
    this.selectedInstrumentSession = session;
    this.pendingQuoteEdited = false;
    this.preparePendingTradeDefaults(true);
    this.updateCalculations();
    this.updatePairSentiment(symbol);
    void this.fetchDynamicPayout();
    void this.persistTerminalPreferences();
  }

  private updateLiveClock() {
    const nowMs = Date.now();
    if (this.lastClockCheckAt > 0 && nowMs - this.lastClockCheckAt > 60000) {
      // Detected real sleep/wake jump (> 60s suspension)
      this.wsClient.reconnect();
      void this.loadCandles();
      if (this.appState.getMode() === "LIVE") void this.reconcileActiveContracts();
      else this.reconcileDemoContracts();
    }
    this.lastClockCheckAt = nowMs;

    const clock = document.getElementById("chart-live-clock");
    if (!clock) return;
    const tzInfo = this.getActiveTimezoneInfo();
    const now = new Date();
    const current = now.toLocaleTimeString("en-GB", {
      hour12: false,
      timeZone: tzInfo.iana,
    });
    const seconds = this.timeframeSeconds(this.selectedTimeframe);
    const remaining = Math.max(0, Math.ceil((seconds * 1000 - (now.getTime() % (seconds * 1000))) / 1000) - 1);
    const remainingLabel = `${String(Math.floor(remaining / 3600)).padStart(2, "0")}:${String(Math.floor((remaining % 3600) / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;
    const period = this.durationLabel(seconds);
    clock.textContent = `${current} ${tzInfo.label} · ${period} candle · next candle in ${remainingLabel}`;
  }

  private timeframeSeconds(timeframe: Timeframe): number {
    const match = /^(\d+)(s|m|h)$/.exec(timeframe);
    if (!match) return 60;
    return Number(match[1]) * (match[2] === "h" ? 3600 : match[2] === "m" ? 60 : 1);
  }

  private durationLabel(seconds: number): string {
    if (seconds % 3600 === 0) return `H${seconds / 3600}`;
    if (seconds % 60 === 0) return `M${seconds / 60}`;
    return `S${seconds}`;
  }

  private formatDuration(seconds: number): string {
    const sec = Math.max(1, Number(seconds) || 60);
    if (sec < 60) return `${sec} seconds`;
    if (sec % 3600 === 0) return `${sec / 3600} hour${sec === 3600 ? "" : "s"}`;
    if (sec % 60 === 0) return `${sec / 60} minute${sec === 60 ? "" : "s"}`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s}s`;
  }

  private allowedContractDurations(includeSubMinute: boolean = true): number[] {
    const instrument = this.appState.getInstruments().find(
      (item) => item.symbol === this.appState.getSelectedSymbol(),
    );
    const minimum = Math.max(1, Number(instrument?.min_duration_seconds || 5));
    const maximum = Math.max(minimum, Number(instrument?.max_duration_seconds || 14400));
    return CONTRACT_DURATIONS_SECONDS.filter(
      (seconds) => seconds >= minimum && seconds <= maximum && (includeSubMinute || seconds >= 60),
    );
  }

  private formatPayoutPercentage(value: string | number | undefined | null): string {
    if (value === undefined || value === null || value === "") return "0";
    const num = parseFloat(String(value));
    if (isNaN(num)) return String(value);
    return Math.round(num).toFixed(0);
  }

  private refreshDurationOptions(preferred?: number) {
    const main = document.getElementById("duration-preset-select") as HTMLSelectElement | null;
    const hidden = document.getElementById("duration-select") as HTMLInputElement | null;
    const pending = document.getElementById("pending-duration-select") as HTMLSelectElement | null;
    const current = preferred !== undefined ? preferred : Number(main?.value || hidden?.value || 60);
    const populate = (select: HTMLSelectElement | null, values: number[], selectedVal: number) => {
      if (!select) return;
      if (!values.length) {
        select.innerHTML = '<option value="">No duration available for this instrument</option>';
        select.disabled = true;
        return;
      }
      select.disabled = false;
      select.innerHTML = values.map((seconds) => `<option value="${seconds}">${this.durationLabel(seconds)} · ${seconds < 60 ? `${seconds} seconds` : seconds < 3600 ? `${seconds / 60} minute${seconds === 60 ? "" : "s"}` : `${seconds / 3600} hour${seconds === 3600 ? "" : "s"}`}</option>`).join("");
      const chosen = values.includes(selectedVal) ? selectedVal : values.find((value) => value >= selectedVal) || values[values.length - 1];
      select.value = String(chosen);
    };
    const allowedMain = this.allowedContractDurations(true);
    const allowedPending = this.allowedContractDurations(false);
    populate(main, allowedMain, current);
    const pendingCurrent = Number(pending?.value || 60);
    populate(pending, allowedPending, allowedPending.includes(pendingCurrent) ? pendingCurrent : (allowedPending.includes(current) ? current : 60));
    const authoritative = Number(main?.value || current);
    if (hidden) hidden.value = String(authoritative);
    if (main && [...main.options].some((option) => Number(option.value) === authoritative)) main.value = String(authoritative);
    const pendingAction = document.getElementById("create-pending-order") as HTMLButtonElement | null;
    if (pendingAction) pendingAction.disabled = !allowedPending.length;
    this.orderStore.resetQuote();
    this.updateCalculations();
    void this.fetchDynamicPayout();
  }

  private setupWebSocket() {
    this.wsClient.onStateChange((state) => {
      this.telemetry.setConnectionState(state);
      if (this.chart) {
        this.chart.setDegraded(state === "DEGRADED");
      }
      if (state === "ONLINE") {
        this.appState.refreshLiveState();
        this.reconcileActiveContracts();
        this.loadCandles();
      }
    });

    this.wsClient.subscribe("TICK", (envelope) => {
      const activeSym = this.appState.getSelectedSymbol();
      const tickSymbol = envelope.data?.symbol || (envelope as any).symbol;
      if (!tickSymbol || tickSymbol !== activeSym) return;
      this.telemetry.recordTick(envelope.server_time);
      const feedSource = (envelope as any).feed_source || envelope.data?.feed_source;
      if (feedSource) {
        this.updateFeedSourceDisplay(feedSource);
      }
      if (this.chart && envelope.data) {
        this.chart.updateLatestTick(envelope.data);
        if (!this.pendingQuoteEdited) this.populatePendingQuoteFromSelectedPair();
        const key = this.getCacheKey(activeSym, this.selectedTimeframe);
        this.candleBufferCache.set(key, this.chart.getCandles());
      }
      if (envelope.data?.price) {
        SentimentEngine.getShared().recordTick(Number(envelope.data.price));
      }
    });

    this.wsClient.subscribe("FEED_STATUS", (envelope) => {
      const feedSource = (envelope as any).feed_source;
      if (feedSource) {
        this.updateFeedSourceDisplay(feedSource);
      }
    });

    this.wsClient.subscribe("FEED_FAILOVER", (envelope) => {
      const source = (envelope as any).active_provider || (envelope as any).feed_source || "SYNTHETIC_OTC";
      this.updateFeedSourceDisplay(source);
      this.showDashboardNotice(`Market data switched to ${source}`, "info");
    });

    this.wsClient.subscribe("TRADING_HALT_CHANGED", (envelope) => {
      const isHalted = !!(envelope as any).emergency_halt;
      this.handleTradingHaltState(isHalted);
    });

    this.wsClient.subscribe("TOURNAMENTS_UPDATED", () => {
      const drawer = document.getElementById("experience-drawer");
      const heading = document.getElementById("experience-drawer-title");
      if (drawer && drawer.classList.contains("open") && heading?.textContent?.includes("Tournaments")) {
        void this.showTournamentCenter();
      }
    });

    this.wsClient.subscribe("CONTRACT_SETTLED", (envelope) => {
      if (envelope.data) {
        this.orderStore.handleSettlement(envelope.data);
        this.appState.refreshLiveState();
        if (envelope.data.is_risk_free) {
          if (envelope.data.status === "SETTLED_LOSS") {
            const curr = envelope.data.currency || "USD";
            const fxRate = getLiveUsdFxRate(curr);
            const currSym = getCurrencySymbol(curr);
            const maxRefUSD = Number(envelope.data.risk_free_max_refund || 10);
            const convertedCap = Math.round(maxRefUSD * fxRate);
            const refundAmount = Number((envelope.data as any).risk_free_refund_amount || 0);
            const refund = refundAmount > 0 ? refundAmount : Math.min(Number(envelope.data.stake), convertedCap);
            this.showDashboardNotice(`🛡️ 100% Risk-Free Protection Applied: ${currSym}${refund.toFixed(2)} refunded back to your balance!`, "success");
          } else if (envelope.data.status === "SETTLED_WIN") {
            this.showDashboardNotice("🎉 Risk-Free Trade Won! Full payout credited to your balance.", "success");
          }
          if (envelope.data.risk_free_token_id) {
            this.userRedeemedTokens = this.userRedeemedTokens.filter(t => t.id !== envelope.data.risk_free_token_id);
            try {
              localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens));
              const channel = new BroadcastChannel("starx_rewards_sync");
              channel.postMessage({ type: "PERK_CONSUMED", tokenId: envelope.data.risk_free_token_id });
              channel.close();
            } catch {}
            void this.restClient.consumeGemsPerk(envelope.data.risk_free_token_id);
          }
          this.updateRiskFreeUI();
        }
        if (!this.terminalPreferences.hide_trade_results)
          this.showSettlementToast(envelope.data.contract_id || envelope.data);
      }
    });

    this.wsClient.subscribe("GAP_DETECTED", () => {
      this.telemetry.recordGap();
      this.loadCandles(); // Resync on gap
    });

    this.wsClient.subscribe("NOTIFICATION", (envelope: any) => {
      const data = envelope?.data || envelope;
      void this.refreshNotificationBadge();
      const title = data.title || "🎁 New Notification";
      const message = data.message || "You have a new account notification.";
      this.showNotificationToast(title, message);
    });

    this.wsClient.subscribe("BALANCE_UPDATED", () => {
      void this.appState.refreshLiveState();
    });
  }

  private bindState() {
    this.appState.subscribe(() => this.renderHeader());
    this.orderStore.subscribe(() => {
      this.renderOrderControls();
      this.renderActivePositions();
      this.syncChartTradeOverlays();

      if (this.orderStore.getState() === 'CHALLENGE_REQUIRED') {
        this.showTurnstileChallenge();
      }
    });

    this.orderStore.onSettlement((contract) => {
      this.appState.refreshLiveState();
      this.chart?.showSettlementResult(contract.status);
      const outcome = contract.status === "SETTLED_WIN" ? "WIN" : contract.status === "SETTLED_TIE" ? "TIE" : "LOSS";
      this.playTradeExitSound(outcome);
      if (!this.terminalPreferences.hide_trade_results)
        this.showSettlementToast(contract.contract_id || contract);
    });

    this.telemetry.subscribe(() => this.renderTelemetry());
  }

  private showTurnstileChallenge(): void {
    if (this.challengeModal) return;
    const siteKey = this.orderStore.getChallengeSiteKey() || '1x00000000000000000000AA';
    this.challengeModal = new TurnstileChallengeModal({
      siteKey,
      onSolve: async (token: string) => {
        this.challengeModal = null;
        const ok = await this.orderStore.solveChallenge(token);
        if (ok) {
          await this.appState.refreshLiveState();
          await this.reconcileActiveContracts();
        }
      },
      onCancel: () => {
        this.challengeModal = null;
        this.orderStore.cancelChallenge();
      },
    });
    this.challengeModal.show();
  }

  private playTradeEntrySound(direction: "CALL" | "PUT") {
    if (this.isMuted) return;
    try {
      const AudioCtxClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!this.audioCtx && AudioCtxClass) this.audioCtx = new AudioCtxClass();
      if (!this.audioCtx) return;
      if (this.audioCtx.state === "suspended") void this.audioCtx.resume?.();

      const t0 = this.audioCtx.currentTime;
      const notes = direction === "CALL" ? [587.33, 880.00] : [659.25, 440.00];
      notes.forEach((freq, i) => {
        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t0 + i * 0.07);
        gain.gain.setValueAtTime(0.18, t0 + i * 0.07);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.07 + 0.16);
        osc.connect(gain);
        gain.connect(this.audioCtx.destination);
        osc.start(t0 + i * 0.07);
        osc.stop(t0 + i * 0.07 + 0.17);
      });
    } catch {}
  }

  private playTradeExitSound(status: "WIN" | "LOSS" | "TIE") {
    if (this.isMuted) return;
    try {
      const AudioCtxClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!this.audioCtx && AudioCtxClass) this.audioCtx = new AudioCtxClass();
      if (!this.audioCtx) return;
      if (this.audioCtx.state === "suspended") void this.audioCtx.resume?.();

      const t0 = this.audioCtx.currentTime;
      if (status === "WIN") {
        // StarX triumphant victory arpeggio chime
        const winNotes = [523.25, 659.25, 783.99, 1046.50];
        winNotes.forEach((freq, idx) => {
          const osc = this.audioCtx.createOscillator();
          const gain = this.audioCtx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, t0 + idx * 0.08);
          gain.gain.setValueAtTime(0.20, t0 + idx * 0.08);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + idx * 0.08 + (idx === winNotes.length - 1 ? 0.45 : 0.22));
          osc.connect(gain);
          gain.connect(this.audioCtx.destination);
          osc.start(t0 + idx * 0.08);
          osc.stop(t0 + idx * 0.08 + (idx === winNotes.length - 1 ? 0.48 : 0.24));
        });
      } else if (status === "TIE") {
        const tieNotes = [587.33, 587.33];
        tieNotes.forEach((freq, idx) => {
          const osc = this.audioCtx.createOscillator();
          const gain = this.audioCtx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, t0 + idx * 0.12);
          gain.gain.setValueAtTime(0.14, t0 + idx * 0.12);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + idx * 0.12 + 0.22);
          osc.connect(gain);
          gain.connect(this.audioCtx.destination);
          osc.start(t0 + idx * 0.12);
          osc.stop(t0 + idx * 0.12 + 0.24);
        });
      } else {
        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(349.23, t0);
        gain.gain.setValueAtTime(0.12, t0);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
        osc.connect(gain);
        gain.connect(this.audioCtx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.30);
      }
    } catch {}
  }

  private playChime(
    freq: number = 600,
    type: OscillatorType = "sine",
    duration: number = 0.15,
  ) {
    if (this.isMuted) return;
    try {
      const AudioCtxClass =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!this.audioCtx && AudioCtxClass) {
        this.audioCtx = new AudioCtxClass();
      }
      if (this.audioCtx) {
        if (this.audioCtx.state === "suspended") void this.audioCtx.resume?.();
        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, this.audioCtx.currentTime);
        gain.gain.setValueAtTime(0.15, this.audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(
          0.001,
          this.audioCtx.currentTime + duration,
        );
        osc.connect(gain);
        gain.connect(this.audioCtx.destination);
        osc.start();
        osc.stop(this.audioCtx.currentTime + duration);
      }
    } catch {}
  }

  private showDashboardNotice(message: string, type: "info" | "success" | "error" | "warning" = "info") {
    let region = document.getElementById("trader-dashboard-notices");
    if (!region) {
      region = document.createElement("div");
      region.id = "trader-dashboard-notices";
      region.setAttribute("role", "status");
      region.setAttribute("aria-live", "polite");
      region.style.cssText = "position:fixed;right:18px;top:76px;z-index:10000;display:grid;gap:8px;max-width:340px";
      document.body.appendChild(region);
    }
    const notice = document.createElement("div");
    const border = type === "error" ? "#be123c" : type === "success" ? "#047857" : "#0369a1";
    notice.style.cssText = `padding:10px 13px;border:1px solid ${border};border-radius:8px;background:#101722;color:#f8fafc;box-shadow:0 12px 30px rgba(0,0,0,.45);font-size:12px`;
    notice.textContent = message;
    region.appendChild(notice);
    window.setTimeout(() => notice.remove(), 3200);
  }

  private showDashboardConfirmation(title: string, message: string, confirmLabel: string): Promise<boolean> {
    return new Promise((resolve) => {
      const modal = document.getElementById("modal-overlay");
      if (!modal) { resolve(false); return; }
      modal.innerHTML = `<div style="background:#121824;border:1px solid #f59e0b;border-radius:12px;padding:22px;max-width:470px;width:100%;color:#f8fafc"><h3 style="margin:0 0 8px;color:#fbbf24">${this.escapeHTML(title)}</h3><p style="color:#cbd5e1;font-size:13px;line-height:1.55">${this.escapeHTML(message)}</p><div style="display:flex;justify-content:flex-end;gap:9px;margin-top:16px"><button id="dashboard-confirm-cancel" class="experience-action" type="button">Cancel</button><button id="dashboard-confirm-accept" class="experience-action" type="button" style="border-color:#b45309;background:#78350f;color:#fef3c7">${this.escapeHTML(confirmLabel)}</button></div></div>`;
      modal.style.display = "flex";
      const finish = (accepted: boolean) => { modal.style.display = "none"; resolve(accepted); };
      modal.querySelector("#dashboard-confirm-cancel")?.addEventListener("click", () => finish(false), { once: true });
      modal.querySelector("#dashboard-confirm-accept")?.addEventListener("click", () => finish(true), { once: true });
    });
  }

  private renderActivePositions() {
    const activeContainer =
      document.getElementById("active-positions-container") ||
      document.getElementById("trades-list-container");
    const tradesCountEl = document.getElementById("active-trades-count");
    const selectedSymbol = this.appState.getSelectedSymbol();
    const activeContracts = this.orderStore.getActiveContracts();

    if (tradesCountEl) {
      tradesCountEl.textContent = String(activeContracts.length);
    }

    if (!activeContainer) return;

    if (!activeContracts.length) {
      activeContainer.innerHTML =
        '<div style="color: #cbd5e1; font-size: 12px; text-align: center; padding: 12px;">No active contracts</div>';
      return;
    }

    activeContainer.innerHTML = activeContracts
      .map((active) => {
        const isCall = active.direction === "CALL";
        const color = isCall ? "#10b981" : "#f43f5e";
        const earlyQuote = this.orderStore.getEarlyCashoutQuote(
          active.contract_id,
        );
        const entryMs = new Date(active.entry_timestamp).getTime();
        const expiryMs = new Date(active.expiry_timestamp).getTime();
        const totalDurationSeconds = Math.max(0, Math.round((expiryMs - entryMs) / 1000));
        const secondsRemaining = Math.max(0, Math.ceil((expiryMs - Date.now()) / 1000));
        const elapsedSeconds = Math.max(0, Math.round((Date.now() - entryMs) / 1000));
        const selectedPair = active.symbol === selectedSymbol;
        const currentPrice = selectedPair ? this.chart?.getLatestPrice() || 0 : 0;
        const strikePrice = Number(active.strike_price || 0);
        const adverseMove = currentPrice > 0 && (isCall ? currentPrice < strikePrice : currentPrice > strikePrice);
        const cashoutEligible = this.cashoutFeatureEnabled && selectedPair && totalDurationSeconds >= 60 && elapsedSeconds <= 30 && secondsRemaining >= 30 && adverseMove;
        return `
      <div class="position-card" style="background: #161c28; border: 1px solid #242e42; border-radius: 8px; padding: 12px; margin-bottom: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-weight: 700; font-size: 13px; color: ${color};">${active.direction} ${active.symbol}</span>
            ${active.is_risk_free ? `<span class="badge-risk-free-pill" style="display:inline-flex; align-items:center; gap:2px; background:rgba(16,185,129,0.15); color:#34d399; border:1px solid rgba(16,185,129,0.3); border-radius:4px; padding:1px 5px; font-size:10px; font-weight:700;">🛡️ 100% Protected</span>` : ''}
          </div>
          <span style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #94a3b8;">${active.currency} ${formatSmartBalance(active.stake)}</span>
        </div>
        <div style="font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #cbd5e1; margin-bottom: 8px;">
          ID: <span class="active-contract-id" style="color: #94a3b8;">${active.contract_id}</span>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 11px; color: #94a3b8; margin-bottom: 10px;">
          <div>Strike: <strong style="color: #f8fafc;">${active.strike_price.toFixed(5)}</strong></div>
          <div>Payout: <strong style="color: #10b981;">+${this.formatPayoutPercentage(active.payout_percentage)}%</strong></div>
          <div>Expires: <strong class="position-expiry-countdown" data-expiry="${this.escapeHTML(active.expiry_timestamp)}" style="color:#38bdf8;">--:--</strong></div>
          <div>Entry: <strong style="color:#f8fafc;">${new Date(active.entry_timestamp).toLocaleTimeString([], { hour12: false, timeZone: this.getActiveTimezoneInfo().iana })}</strong></div>
        </div>
        <div style="display: flex; gap: 8px;">
          ${cashoutEligible ? `
          <button class="quote-cashout-btn" data-contract-id="${this.escapeHTML(active.contract_id)}" data-entry="${this.escapeHTML(active.entry_timestamp)}" ${earlyQuote ? "" : "disabled"} style="flex: 1; background: ${earlyQuote ? "#0284c7" : "#334155"}; color: #f8fafc; border: none; padding: 6px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; cursor: ${earlyQuote ? "pointer" : "wait"};">
            ${earlyQuote ? `Sell now · ${this.escapeHTML(active.currency)} ${formatSmartBalance(Number(earlyQuote.quote_payout_cents ?? Number(earlyQuote.quote_payout) * 100) / 100)}` : "Calculating cash-out…"}
          </button>
          ` : `<span class="cashout-policy-note" style="font-size:10px;color:#64748b">${!this.cashoutFeatureEnabled ? "Early cash-out disabled by operator" : !selectedPair ? "Select this pair to manage it" : totalDurationSeconds < 60 ? "Early cash-out unavailable below 1 minute" : (elapsedSeconds > 30 || secondsRemaining < 30) ? "Early cash-out closed (first 30s only)" : "Available only while this trade is moving against your direction"}</span>`}
        </div>
      </div>
    `;
      })
      .join("");

    activeContainer.querySelectorAll(".quote-cashout-btn").forEach((button) =>
      button.addEventListener("click", async (event) => {
        const contractId =
          (event.currentTarget as HTMLElement).dataset.contractId || "";
        const activeContract = this.orderStore.getActiveContracts().find((item) => item.contract_id === contractId);
        const quote = this.orderStore.getEarlyCashoutQuote(contractId);
        if (quote) {
          const completed = await this.orderStore.executeEarlyCashout(contractId);
          if (completed) {
            await this.appState.refreshLiveState();
            await this.reconcileActiveContracts();
            const credited = formatSmartBalance(Number(quote.quote_payout_cents ?? Number(quote.quote_payout) * 100) / 100);
            this.showDashboardNotice(`Cash-out completed. ${activeContract?.currency || "USD"} ${credited} was credited to your wallet.`, "success");
          }
        }
      }),
    );
    this.updatePositionCountdowns();
  }

  private renderClosedPositions() {
    const historyContainer = document.getElementById("history-list-container");
    const historyCountEl = document.getElementById("history-trades-count");
    const historyContracts = this.appState.getContractHistory();

    if (historyCountEl) {
      historyCountEl.textContent = String(historyContracts.length);
    }

    if (!historyContainer) return;

    if (!historyContracts.length) {
      historyContainer.innerHTML =
        '<div style="color: #cbd5e1; font-size: 12px; text-align: center; padding: 12px;">No closed trade history yet in this session.</div>';
      return;
    }

    historyContainer.innerHTML = historyContracts
      .slice(0, 50)
      .map((trade) => {
        const isWin = trade.status === "SETTLED_WIN";
        const isTie = trade.status === "SETTLED_TIE";
        const isLoss = !isWin && !isTie;
        const payout = Number(trade.actual_payout || 0);
        const stake = Number(trade.stake || 0);
        const diff = payout - stake;
        const isRiskFree = Boolean(trade.is_risk_free || (trade as any).risk_free_token_id);
        const refund = isRiskFree && isLoss ? Number((trade as any).risk_free_refund_amount || trade.risk_free_max_refund || 0) : 0;
        const isFullyRefunded = isRiskFree && isLoss && refund >= stake;
        const statusColor = isWin ? "#10b981" : isFullyRefunded ? "#34d399" : isTie ? "#ffb800" : "#f43f5e";
        const currSym = getCurrencySymbol(trade.currency);

        let resultLabel = `${currSym}0.00`;
        if (isWin) {
          resultLabel = `+${currSym}${diff.toFixed(2)}`;
        } else if (isTie) {
          resultLabel = `${currSym}0.00`;
        } else if (isFullyRefunded) {
          resultLabel = `±${currSym}0.00`;
        } else if (refund > 0) {
          resultLabel = `-${currSym}${(stake - refund).toFixed(2)}`;
        } else {
          resultLabel = `-${currSym}${stake.toFixed(2)}`;
        }
        const isCall = trade.direction === "CALL";

        const closedTime = trade.expiry_timestamp || trade.settled_at || (trade as any).settlement_timestamp;
        return `
      <div class="position-card history-card" data-contract-id="${this.escapeHTML(trade.contract_id)}" style="background: #161c28; border: 1px solid #242e42; border-radius: 8px; padding: 10px; margin-bottom: 6px; cursor: pointer; transition: border-color 0.15s ease;" onmouseover="this.style.borderColor='#38bdf8'" onmouseout="this.style.borderColor='#242e42'">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-weight: 700; font-size: 12px; color: ${isCall ? "#10b981" : "#f43f5e"};">${trade.direction} ${trade.symbol}</span>
            ${trade.is_risk_free ? `<span class="badge-risk-free-pill" style="display:inline-flex; align-items:center; gap:2px; background:rgba(16,185,129,0.15); color:#34d399; border:1px solid rgba(16,185,129,0.3); border-radius:4px; padding:1px 5px; font-size:10px; font-weight:700;">🛡️ 100% Protected</span>` : ''}
          </div>
          <strong style="font-family: 'JetBrains Mono', monospace; font-size: 12px; color: ${statusColor};">${resultLabel}</strong>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; font-family: 'JetBrains Mono', monospace;">
          <span>Strike: ${Number(trade.strike_price).toFixed(5)}</span>
          <span>Close: ${Number(trade.expiry_price || trade.strike_price).toFixed(5)}</span>
          <span>${closedTime ? new Date(closedTime).toLocaleTimeString([], { hour12: false, timeZone: this.getActiveTimezoneInfo().iana }) : ""}</span>
        </div>
        <div style="display: flex; justify-content: flex-end; align-items: center; margin-top: 5px; padding-top: 4px; border-top: 1px dashed rgba(255,255,255,0.07);">
          <span style="font-size: 10px; color: #38bdf8; font-weight: 600; display: inline-flex; align-items: center; gap: 3px;">
            📜 Official Receipt ↗
          </span>
        </div>
      </div>
    `;
      })
      .join("");

    historyContainer.querySelectorAll<HTMLElement>(".history-card").forEach((card) => {
      card.addEventListener("click", () => {
        const cId = card.dataset.contractId;
        if (cId) {
          const trade = historyContracts.find((t) => t.contract_id === cId);
          void this.showReceiptModal(trade || cId);
        }
      });
    });
  }

  private updatePositionCountdowns() {
    document
      .querySelectorAll<HTMLElement>(".position-expiry-countdown")
      .forEach((element) => {
        const remainingMs = Math.max(
          0,
          new Date(element.dataset.expiry || "").getTime() - Date.now(),
        );
        const seconds = Math.ceil(remainingMs / 1000);
        element.textContent = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
        element.style.color = seconds <= 5 ? "#fb7185" : "#38bdf8";
      });

    // Transition cashout button to closed policy note in-place as soon as trade exceeds 30s
    document.querySelectorAll<HTMLElement>(".quote-cashout-btn").forEach((button) => {
      const entryTime = new Date(button.dataset.entry || "").getTime();
      const elapsed = (Date.now() - entryTime) / 1000;
      if (elapsed > 30) {
        const container = button.parentElement;
        if (container) {
          container.innerHTML = `<span class="cashout-policy-note" style="font-size:10px;color:#64748b">Early cash-out closed (first 30s only)</span>`;
        }
      }
    });
  }

  private async refreshEarlyCashoutOffers(): Promise<void> {
    if (document.hidden || !this.cashoutFeatureEnabled) return;
    const activeContracts = this.orderStore.getActiveContracts();
    if (activeContracts.length === 0) return;
    const selectedSymbol = this.appState.getSelectedSymbol();
    const currentPrice = this.chart?.getLatestPrice() || 0;
    let quotesChanged = false;
    for (const contract of activeContracts) {
      const entryMs = new Date(contract.entry_timestamp).getTime();
      const expiryMs = new Date(contract.expiry_timestamp).getTime();
      const durationSeconds = Math.round((expiryMs - entryMs) / 1000);
      const secondsRemaining = Math.ceil((expiryMs - Date.now()) / 1000);
      const elapsedSeconds = Math.round((Date.now() - entryMs) / 1000);
      const strike = Number(contract.strike_price || 0);
      const adverse = currentPrice > 0 && (contract.direction === "CALL" ? currentPrice < strike : currentPrice > strike);
      if (contract.symbol !== selectedSymbol || durationSeconds < 60 || elapsedSeconds > 30 || secondsRemaining < 30 || !adverse) {
        if (this.orderStore.getEarlyCashoutQuote(contract.contract_id)) {
          this.orderStore.clearEarlyCashoutQuote(contract.contract_id);
          quotesChanged = true;
        }
        continue;
      }
      if (this.cashoutQuoteRefreshInFlight.has(contract.contract_id)) continue;
      this.cashoutQuoteRefreshInFlight.add(contract.contract_id);
      try {
        await this.orderStore.requestEarlyCashout(contract.contract_id);
        quotesChanged = true;
      } finally {
        this.cashoutQuoteRefreshInFlight.delete(contract.contract_id);
      }
    }
    if (quotesChanged) {
      this.renderActivePositions();
    }
  }

  private renderQuickStakeChips() {
    const curr = this.appState.getSelectedCurrency();
    const sym = getCurrencySymbol(curr);
    const chips = document.querySelectorAll<HTMLButtonElement>(".stake-chip-btn");
    if (!chips.length) return;

    const presetsInfo = getCurrencyStakePresets(curr);
    const stakes = presetsInfo.presets;

    const stakeInput = document.getElementById("stake-input") as HTMLInputElement | null;
    const currentVal = stakeInput ? parseFloat(stakeInput.value) : NaN;

    chips.forEach((chip, i) => {
      const amt = stakes[i] || stakes[stakes.length - 1];
      chip.dataset.stake = amt.toFixed(2);
      chip.textContent = `${sym}${amt >= 1000 ? amt.toLocaleString() : amt}`;

      const isSelected = !isNaN(currentVal) && Math.abs(currentVal - amt) < 0.01;
      if (isSelected) {
        chip.classList.add("active");
        chip.style.color = "#00d9ff";
        chip.style.borderColor = "rgba(0, 217, 255, 0.6)";
        chip.style.background = "rgba(0, 217, 255, 0.15)";
      } else {
        chip.classList.remove("active");
        chip.style.color = "";
        chip.style.borderColor = "";
        chip.style.background = "";
      }
    });
  }

  private updateCalculations() {
    this.renderQuickStakeChips();
    const stakeInput = document.getElementById(
      "stake-input",
    ) as HTMLInputElement;
    const durationSelect = document.getElementById(
      "duration-select",
    ) as HTMLInputElement;
    const stakeDisplay = document.getElementById("stake-display");
    const currencyPrefix = document.getElementById("stake-currency-prefix");
    const durationDisplay = document.getElementById("duration-display");
    const payoutReturn = document.getElementById("calc-payout-return");

    const curr = this.appState.getSelectedCurrency();
    const presetsInfo = getCurrencyStakePresets(curr);
    const currSym = getCurrencySymbol(curr);
    if (currencyPrefix) currencyPrefix.textContent = currSym.trim();

    let rawVal = parseFloat(stakeInput?.value || String(presetsInfo.presets[0]));
    let stakeVal = isNaN(rawVal) || rawVal <= 0 ? presetsInfo.presets[0] : rawVal;

    // Only clamp and reformat the input value if the user is NOT actively typing inside it
    if (stakeInput && document.activeElement !== stakeInput) {
      if (isNaN(rawVal) || rawVal < presetsInfo.minStake) {
        stakeVal = presetsInfo.presets[0];
      }
      stakeInput.value = stakeVal.toFixed(2);
      stakeInput.style.width = Math.max(65, (stakeInput.value.length + 1) * 9.5) + "px";
    }

    if (stakeDisplay) stakeDisplay.textContent = `${currSym}${stakeVal.toFixed(2)}`;

    const durVal = parseInt(durationSelect?.value || "60");
    const currentSymbol = this.appState.getSelectedSymbol();
    const inst = this.appState.getInstruments().find(i => i.symbol === currentSymbol);
    const fallbackPct = inst && inst.base_payout_percentage ? parseFloat(inst.base_payout_percentage) : 90;
    const payoutPct = indicativePayouts.get(currentSymbol)?.payout_percentage || this.currentPayoutPercentage || fallbackPct;

    if (payoutReturn) {
      const retAmt = stakeVal * (1 + payoutPct / 100);
      payoutReturn.textContent = `${currSym}${retAmt.toFixed(2)}`;
    }
    if (durationDisplay) {
      const hours = Math.floor(durVal / 3600)
        .toString()
        .padStart(2, "0");
      const mins = Math.floor((durVal % 3600) / 60)
        .toString()
        .padStart(2, "0");
      const secs = (durVal % 60).toString().padStart(2, "0");
      durationDisplay.textContent = `${hours}:${mins}:${secs}`;
    }
    if (this.pendingTradeEnabled) this.renderPendingTradePreview();
  }

  private payoutContext(): PayoutContext {
    const duration = Number((document.getElementById("duration-select") as HTMLInputElement)?.value || 60);
    const stake = (document.getElementById("stake-input") as HTMLInputElement)?.value || "100";
    return {symbol:this.appState.getSelectedSymbol(),duration,stake,currency:this.appState.getSelectedCurrency()};
  }

  private async fetchDynamicPayout() {
    const context=this.payoutContext();
    if (!context.symbol) return;
    const pending=indicativePayouts.refresh(context,this.appState.getInstruments().map(i=>i.symbol),
      symbols=>this.restClient.getPayoutBatch(symbols,context.duration,context.stake,context.currency),
      ()=>this.payoutContext());
    this.paintIndicativePayout();
    if (await pending) this.paintIndicativePayout();
  }

  private paintIndicativePayout() {
    const symbol = this.appState.getSelectedSymbol();
    const inst = this.appState.getInstruments().find(i => i.symbol === symbol);
    const quote = indicativePayouts.get(symbol);
    const fallbackRate = inst && inst.base_payout_percentage ? Math.round(Number(inst.base_payout_percentage)) : 90;
    if (quote?.payout_percentage != null) {
      const currentPayoutPercentage = Number(quote.payout_percentage);
      this.currentPayoutPercentage = currentPayoutPercentage;
    } else {
      this.currentPayoutPercentage = fallbackRate;
    }
    const payout = document.getElementById("exec-payout-pct");
    if (payout) {
      payout.textContent = Math.floor(this.currentPayoutPercentage) + "%";
      payout.title = "Indicative; final payout is locked in the trade quote";
    }
    const pill = document.getElementById("payout-dynamic-boost-pill");
    if (pill) {
      pill.style.display = quote ? "inline-block" : "none";
      pill.textContent = quote?.session_name === "MANUAL_FIXED" ? "MANUAL PAYOUT" : "AUTO PAYOUT";
    }
    this.updateCalculations();
    indicativePayouts.paint();
  }

  private bindUIEvents() {
    // 1. Account command menu and explicit DEMO/LIVE selection.
    const modeBtn = document.getElementById("mode-switch-btn");
    if (modeBtn) {
      modeBtn.setAttribute("role", "button");
      modeBtn.setAttribute("tabindex", "0");
      modeBtn.addEventListener("click", () => void this.showTradingAccountMenu());
      modeBtn.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          void this.showTradingAccountMenu();
        }
      });
    }

    // 2. Stake and Duration Steppers
    const stakeInput = document.getElementById(
      "stake-input",
    ) as HTMLInputElement;
    const durationSelect = document.getElementById(
      "duration-select",
    ) as HTMLInputElement;
    const durationPreset = document.getElementById(
      "duration-preset-select",
    ) as HTMLSelectElement;

    durationPreset?.addEventListener("change", () => {
      if (!durationSelect) return;
      this.refreshDurationOptions(Number(durationPreset.value));
      this.playChime(620, "triangle", 0.08);
    });

    document.getElementById("pending-duration-select")?.addEventListener("change", () => {
      this.renderPendingTradePreview();
      this.playChime(620, "triangle", 0.08);
    });

    stakeInput?.addEventListener("focus", () => {
      stakeInput.select();
    });

    stakeInput?.addEventListener("input", () => {
      // Allow only numbers and decimal point
      const cleaned = stakeInput.value.replace(/[^0-9.]/g, "");
      if (cleaned !== stakeInput.value) {
        stakeInput.value = cleaned;
      }
      stakeInput.style.width = Math.max(65, (stakeInput.value.length + 1) * 9.5) + "px";

      const val = parseFloat(stakeInput.value);
      if (!isNaN(val) && val > 0) {
        const curr = this.appState.getSelectedCurrency();
        const currSym = getCurrencySymbol(curr);
        const currentSymbol = this.appState.getSelectedSymbol();
        const inst = this.appState.getInstruments().find(i => i.symbol === currentSymbol);
        const fallbackPct = inst && inst.base_payout_percentage ? parseFloat(inst.base_payout_percentage) : 90;
        const payoutPct = indicativePayouts.get(currentSymbol)?.payout_percentage || this.currentPayoutPercentage || fallbackPct;
        const payoutReturn = document.getElementById("calc-payout-return");
        if (payoutReturn) {
          const retAmt = val * (1 + payoutPct / 100);
          payoutReturn.textContent = `${currSym}${retAmt.toFixed(2)}`;
        }
        this.renderQuickStakeChips();
      }
    });

    stakeInput?.addEventListener("blur", () => {
      const curr = this.appState.getSelectedCurrency();
      const presetsInfo = getCurrencyStakePresets(curr);
      let val = parseFloat(stakeInput.value.replace(/[^0-9.]/g, ""));
      if (isNaN(val) || val < presetsInfo.minStake) {
        val = presetsInfo.presets[0];
      }
      if (val > presetsInfo.maxStake) {
        val = presetsInfo.maxStake;
      }
      stakeInput.value = val.toFixed(2);
      stakeInput.style.width = Math.max(65, (stakeInput.value.length + 1) * 9.5) + "px";
      this.updateCalculations();
      void this.fetchDynamicPayout();
    });

    stakeInput?.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        stakeInput.blur();
      }
    });

    stakeInput?.addEventListener("change", () => {
      this.updateCalculations();
      void this.fetchDynamicPayout();
    });

    document.querySelector(".stake-input-wrapper")?.addEventListener("click", () => {
      stakeInput?.focus();
    });

    document.getElementById("stake-switch-btn")?.addEventListener("click", () => {
      if (stakeInput) {
        stakeInput.focus();
        stakeInput.select();
      }
    });

    document
      .getElementById("stake-step-minus")
      ?.addEventListener("click", () => {
        if (stakeInput) {
          const curr = this.appState.getSelectedCurrency();
          const presetsInfo = getCurrencyStakePresets(curr);
          const step = presetsInfo.stepStake;
          const minStake = presetsInfo.minStake;
          const curVal = parseFloat(stakeInput.value || String(presetsInfo.presets[0]));
          const cur = Math.max(minStake, curVal - step);
          stakeInput.value = cur.toFixed(2);
          stakeInput.style.width = Math.max(65, (stakeInput.value.length + 1) * 9.5) + "px";
          this.updateCalculations();
          void this.fetchDynamicPayout();
          this.playChime(500, "triangle", 0.08);
        }
      });
    document
      .getElementById("stake-step-plus")
      ?.addEventListener("click", () => {
        if (stakeInput) {
          const curr = this.appState.getSelectedCurrency();
          const presetsInfo = getCurrencyStakePresets(curr);
          const step = presetsInfo.stepStake;
          const maxStake = presetsInfo.maxStake;
          const curVal = parseFloat(stakeInput.value || String(presetsInfo.presets[0]));
          const cur = Math.min(maxStake, curVal + step);
          stakeInput.value = cur.toFixed(2);
          stakeInput.style.width = Math.max(65, (stakeInput.value.length + 1) * 9.5) + "px";
          this.updateCalculations();
          void this.fetchDynamicPayout();
          this.playChime(600, "triangle", 0.08);
        }
      });

    document
      .getElementById("duration-step-minus")
      ?.addEventListener("click", () => {
        if (durationSelect) {
          const allowed = this.allowedContractDurations(true);
          const curVal = parseInt(durationSelect.value || "60");
          const idx = allowed.indexOf(curVal);
          let target = curVal;
          if (idx > 0) {
            target = allowed[idx - 1];
          } else if (idx === -1) {
            const smaller = allowed.filter((v) => v < curVal);
            target = smaller.length ? smaller[smaller.length - 1] : allowed[0];
          } else {
            target = allowed[0];
          }
          this.refreshDurationOptions(target);
          this.playChime(550, "triangle", 0.08);
        }
      });
    document
      .getElementById("duration-step-plus")
      ?.addEventListener("click", () => {
        if (durationSelect) {
          const allowed = this.allowedContractDurations(true);
          const curVal = parseInt(durationSelect.value || "60");
          const idx = allowed.indexOf(curVal);
          let target = curVal;
          if (idx >= 0 && idx < allowed.length - 1) {
            target = allowed[idx + 1];
          } else if (idx === -1) {
            const larger = allowed.filter((v) => v > curVal);
            target = larger.length ? larger[0] : allowed[allowed.length - 1];
          } else {
            target = allowed[allowed.length - 1];
          }
          this.refreshDurationOptions(target);
          this.playChime(650, "triangle", 0.08);
        }
      });

    // 3. Stake Chips ($25, $50, $100, $250 / ₹100, ₹500, ₹1000, ₹5000)
    document.querySelectorAll(".stake-chip-btn").forEach((chip) => {
      chip.addEventListener("click", (e) => {
        const target = (e.currentTarget || e.target) as HTMLElement;
        const val = target.getAttribute("data-stake") || target.dataset.stake;
        if (val && stakeInput) {
          stakeInput.value = val;
          this.updateCalculations();
          void this.fetchDynamicPayout();
          this.orderStore.resetQuote();
          this.playChime(700, "sine", 0.1);
        }
      });
    });

    // 4. Authoritative instrument search and switching.
    document
      .getElementById("asset-tabs")
      ?.addEventListener("click", (event) => {
        const close = (event.target as HTMLElement).closest<HTMLElement>(
          ".asset-tab-close",
        );
        const tab = (event.target as HTMLElement).closest<HTMLElement>(
          ".asset-tab",
        );
        if (close && tab?.dataset.symbol) {
          event.stopPropagation();
          void this.closeInstrumentTab(tab.dataset.symbol);
          return;
        }
        if (tab?.dataset.symbol) void this.selectInstrument(tab.dataset.symbol);
      });
    document
      .getElementById("add-asset-btn")
      ?.addEventListener("click", () => this.openInstrumentSearch());
    document
      .getElementById("instrument-drawer-close")
      ?.addEventListener("click", () =>
        document.getElementById("instrument-drawer")?.classList.remove("open"),
      );

    // 5. Drawers (Config & Leaderboard)
    const toggleDrawer = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const isOpen = el.classList.contains("open");
      document
        .querySelectorAll(".sliding-drawer")
        .forEach((d) => d.classList.remove("open"));
      if (!isOpen) el.classList.add("open");
    };

    document
      .getElementById("config-nav-btn")
      ?.addEventListener("click", () => toggleDrawer("settings-drawer"));
    document
      .getElementById("settings-drawer-close")
      ?.addEventListener("click", () => toggleDrawer("settings-drawer"));
    document.getElementById("ranks-nav-btn")?.addEventListener("click", () => {
      toggleDrawer("leaderboard-drawer");
      this.loadLeaderboard("weekly");
      if (this.leaderboardRefreshTimer)
        window.clearInterval(this.leaderboardRefreshTimer);
      this.leaderboardRefreshTimer = window.setInterval(() => {
        if (
          document
            .getElementById("leaderboard-drawer")
            ?.classList.contains("open")
        )
          void this.loadLeaderboard("weekly", true);
      }, 10000);
    });
    document
      .getElementById("leaderboard-drawer-close")
      ?.addEventListener("click", () => toggleDrawer("leaderboard-drawer"));
    document
      .getElementById("view-full-lb-btn")
      ?.addEventListener("click", () => {
        toggleDrawer("leaderboard-drawer");
        this.loadLeaderboard("weekly");
      });

    // 6. Popovers (Chart Type & Timeframe)
    const togglePopover = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const isShow = el.classList.contains("show");
      document
        .querySelectorAll(".popover-menu")
        .forEach((p) => p.classList.remove("show"));
      if (!isShow) el.classList.add("show");
    };

    document
      .getElementById("chart-type-toggle-btn")
      ?.addEventListener("click", () => togglePopover("chart-type-popover"));
    document
      .getElementById("timeframe-toggle-btn")
      ?.addEventListener("click", () => togglePopover("timeframe-popover"));

    document
      .querySelectorAll("#chart-type-popover .popover-item")
      .forEach((item) => {
        item.addEventListener("click", (e) => {
          const type = (e.target as HTMLElement).dataset.chartType || "candles";
          document
            .querySelectorAll("#chart-type-popover .popover-item")
            .forEach((i) => i.classList.remove("active"));
          (e.target as HTMLElement).classList.add("active");
          if (this.chart) {
            const chartType =
              type === "line"
                ? "LINE"
                : type === "area"
                  ? "AREA"
                  : type === "bars"
                    ? "BARS"
                    : "CANDLESTICK";
            this.chart.setChartType(chartType);
            this.terminalPreferences.chart_type = chartType;
            void this.persistTerminalPreferences();
          }
          togglePopover("chart-type-popover");
        });
      });

    document
      .querySelectorAll("#timeframe-popover .popover-item")
      .forEach((item) => {
        item.addEventListener("click", (e) => {
          const tf = ((e.target as HTMLElement).dataset.tf ||
            "1m") as Timeframe;
          document
            .querySelectorAll("#timeframe-popover .popover-item")
            .forEach((i) => i.classList.remove("active"));
          (e.target as HTMLElement).classList.add("active");
          this.selectedTimeframe = tf;
          this.chart?.setTimeframe(tf);
          this.terminalPreferences.timeframe = tf;
          void this.persistTerminalPreferences();
          const tfLabel = document.getElementById("current-timeframe-label");
          if (tfLabel) tfLabel.textContent = `${tf} ▾`;
          togglePopover("timeframe-popover");
          this.loadCandles();
        });
      });

    // 7. Technical indicator catalogue — every option maps to a real canvas computation
    const indicatorDefinitions: Array<{ id: IndicatorId; label: string }> = [
      { id: "SMA", label: "SMA 20" },
      { id: "EMA", label: "EMA 20" },
      { id: "WMA", label: "WMA 20" },
      { id: "HMA", label: "Hull MA" },
      { id: "SUPERTREND", label: "Supertrend" },
      { id: "ICHIMOKU", label: "Ichimoku" },
      { id: "PSAR", label: "Parabolic SAR" },
      { id: "ALLIGATOR", label: "Alligator" },
      { id: "ZIGZAG", label: "ZigZag" },
      { id: "BOLLINGER", label: "Bollinger" },
      { id: "DONCHIAN", label: "Donchian" },
      { id: "KELTNER", label: "Keltner" },
      { id: "RSI", label: "RSI 14" },
      { id: "STOCHASTIC", label: "Stochastic" },
      { id: "MACD", label: "MACD" },
      { id: "CCI", label: "CCI" },
      { id: "WILLIAMS_R", label: "Williams %R" },
      { id: "AWESOME", label: "Awesome Osc." },
      { id: "MOMENTUM", label: "Momentum" },
      { id: "ROC", label: "Rate of Change" },
      { id: "ATR", label: "ATR 14" },
      { id: "ADX", label: "ADX 14" },
      { id: "FRACTALS", label: "Fractals (Williams)" },
      { id: "ENVELOPES", label: "Envelopes" },
      { id: "AROON", label: "Aroon 14" },
      { id: "VORTEX", label: "Vortex (VI)" },
      { id: "BULLS_BEARS", label: "Bulls & Bears Power" },
      { id: "AC", label: "Accelerator Osc. (AC)" },
      { id: "DEMARKER", label: "DeMarker (DeM)" },
    ];
    const indicatorPopover = document.getElementById("indicators-popover");
    if (indicatorPopover) {
      indicatorPopover.innerHTML = indicatorDefinitions
        .map(
          (definition) =>
            `<button type="button" class="popover-item indicator-option${this.chart?.getIndicatorEnabled(definition.id) ? " active" : ""}" data-indicator="${definition.id}" aria-pressed="${this.chart?.getIndicatorEnabled(definition.id) ? "true" : "false"}" style="border:1px solid #263244;text-align:left;cursor:pointer">${definition.label}</button>`,
        )
        .join("");
      indicatorPopover.querySelectorAll(".indicator-option").forEach((option) =>
        option.addEventListener("click", (event) => {
          const element = event.currentTarget as HTMLElement;
          const indicator = element.dataset.indicator as IndicatorId;
          if (!this.chart || !indicator) return;
          const enabled = !this.chart.getIndicatorEnabled(indicator);
          this.chart.setIndicatorEnabled(indicator, enabled);
          this.terminalPreferences.active_indicators =
            this.chart.getActiveIndicators();
          document.getElementById("indicators-toggle-btn")!.textContent =
            `📡 Indicators ${this.chart.getActiveIndicators().length}`;
          void this.persistTerminalPreferences();
          element.classList.toggle("active", enabled);
          element.setAttribute("aria-pressed", String(enabled));
          this.playChime(enabled ? 800 : 400, "sine", 0.1);
        }),
      );
    }
    const indicatorToggle = document.getElementById("indicators-toggle-btn");
    if (indicatorToggle)
      indicatorToggle.textContent = `📡 Indicators ${this.chart?.getActiveIndicators().length || 0}`;
    document
      .getElementById("indicators-toggle-btn")
      ?.addEventListener("click", () => togglePopover("indicators-popover"));

    // 8. Sound Toggle
    document
      .getElementById("sound-toggle-btn")
      ?.addEventListener("click", () => {
        this.toggleSound();
      });
    this.updateSoundToggle();

    // 9. Order Action Buttons (CALL / PUT)
    const callBtn = document.getElementById("call-order-btn");
    const putBtn = document.getElementById("put-order-btn");

    if (callBtn && putBtn) {
      callBtn.addEventListener("click", async () => {
        const stake = stakeInput ? stakeInput.value : "100.00";
        const duration = durationSelect ? parseInt(durationSelect.value) : 60;
        if (!(await this.confirmTradeWhenRequired("CALL", stake, duration))) return;
        await this.handleTradeIntent("CALL", duration, stake);
      });

      putBtn.addEventListener("click", async () => {
        const stake = stakeInput ? stakeInput.value : "100.00";
        const duration = durationSelect ? parseInt(durationSelect.value) : 60;
        if (!(await this.confirmTradeWhenRequired("PUT", stake, duration))) return;
        await this.handleTradeIntent("PUT", duration, stake);
      });
    }

    // 10. Right Sidebar Tab Switcher
    const switchRightTab = (
      tab: "trades" | "pending" | "history" | "leaderboard",
    ) => {
      document
        .querySelectorAll(".tab-header-btn")
        .forEach((b) => b.classList.remove("active"));
      const tc = document.getElementById("trades-list-container");
      const pc = document.getElementById("pending-list-container");
      const hc = document.getElementById("history-list-container");

      if (tc) tc.style.display = "none";
      if (pc) pc.style.display = "none";
      if (hc) hc.style.display = "none";

      if (tab === "trades") {
        document.getElementById("tab-btn-trades")?.classList.add("active");
        if (tc) tc.style.display = "flex";
      } else if (tab === "pending") {
        document.getElementById("tab-btn-pending")?.classList.add("active");
        if (pc) pc.style.display = "flex";
      } else if (tab === "history") {
        document.getElementById("tab-btn-history")?.classList.add("active");
        if (hc) hc.style.display = "flex";
      }
    };

    document
      .getElementById("tab-btn-trades")
      ?.addEventListener("click", () => switchRightTab("trades"));
    document
      .getElementById("tab-btn-pending")
      ?.addEventListener("click", () => switchRightTab("pending"));
    document
      .getElementById("tab-btn-history")
      ?.addEventListener("click", () => switchRightTab("history"));
    document
      .getElementById("tab-btn-lb")
      ?.addEventListener("click", () => {
        void this.loadLeaderboard("weekly");
      });

    document
      .getElementById("header-login-btn")
      ?.addEventListener("click", () => this.showAuthModal("register"));
    document
      .getElementById("deposit-btn")
      ?.addEventListener("click", () => this.showCashierModal("DEPOSIT"));
    document
      .getElementById("withdraw-btn")
      ?.addEventListener("click", () => this.showCashierModal("WITHDRAWAL"));
    document.querySelectorAll<HTMLElement>(".lb-pill").forEach((button) =>
      button.addEventListener("click", () => {
        document
          .querySelectorAll(".lb-pill")
          .forEach((item) => item.classList.remove("active"));
        button.classList.add("active");
        this.loadLeaderboard(
          (button.dataset.period || "weekly") as
            "daily" | "weekly" | "all_time",
        );
      }),
    );

    // 11. Modal Nav Buttons
    document
      .getElementById("ledger-statement-nav-btn")
      ?.addEventListener("click", () => this.showLedgerStatement());
    document
      .getElementById("affiliate-nav-btn")
      ?.addEventListener("click", () => void this.showAffiliatePortal());
    document
      .getElementById("support-nav-btn")
      ?.addEventListener("click", () => void this.showSupportCenter());
    document
      .getElementById("tournaments-nav-btn")
      ?.addEventListener("click", () => void this.showTournamentCenter());
    document
      .getElementById("market-nav-btn")
      ?.addEventListener("click", () => void this.showMarketCenter());
    document
      .getElementById("gems-nav-btn")
      ?.addEventListener("click", () => void this.showGemsStore());
    document
      .getElementById("bonuses-nav-btn")
      ?.addEventListener("click", () => void this.showBonusCenter());
    document
      .getElementById("volume-toggle-btn")
      ?.addEventListener("click", () => this.toggleVolume());
    document
      .getElementById("social-toggle-btn")
      ?.addEventListener("click", () => void this.showSocialCenter());
    document
      .getElementById("drawing-toggle-btn")
      ?.addEventListener("click", () => this.showDrawingCenter());
    document
      .getElementById("notification-bell-btn")
      ?.addEventListener("click", () => void this.showNotifications());
    document
      .getElementById("help-nav-btn")
      ?.addEventListener("click", () => void this.showHelpCenter());
    document
      .getElementById("experience-drawer-close")
      ?.addEventListener("click", () =>
        document.getElementById("experience-drawer")?.classList.remove("open"),
      );
    document
      .querySelector(".btn-pair-info")
      ?.addEventListener("click", () => void this.showPairInformation());
    document
      .getElementById("active-drawings-chip")
      ?.addEventListener("click", () => this.showDrawingCenter());
    const pendingToggle = document.getElementById("pending-trade-toggle");
    const changePendingState = () => {
      this.pendingTradeEnabled = !this.pendingTradeEnabled;
      pendingToggle?.classList.toggle("on", this.pendingTradeEnabled);
      pendingToggle?.setAttribute("aria-checked", String(this.pendingTradeEnabled));
      document
        .getElementById("pending-trade-config")
        ?.classList.toggle("show", this.pendingTradeEnabled);
      if (this.pendingTradeEnabled) this.preparePendingTradeDefaults(false);
      this.renderPendingTradePreview();
    };
    if (pendingToggle) {
      pendingToggle.setAttribute("role", "switch");
      pendingToggle.setAttribute("tabindex", "0");
      pendingToggle.setAttribute("aria-label", "Enable pending trade preview");
      pendingToggle.setAttribute("aria-checked", "false");
      pendingToggle.addEventListener("click", changePendingState);
      pendingToggle.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          changePendingState();
        }
      });
    }
    ["pending-quote-condition", "pending-stake"]
      .forEach((id) => document.getElementById(id)?.addEventListener("input", () => this.renderPendingTradePreview()));
    const pendingTimeEl = document.getElementById("pending-trigger-time");
    if (pendingTimeEl) {
      const onTimeTouch = () => {
        this.pendingTimeEdited = true;
        this.renderPendingTradePreview("TIME");
      };
      pendingTimeEl.addEventListener("input", onTimeTouch);
      pendingTimeEl.addEventListener("change", onTimeTouch);
      pendingTimeEl.addEventListener("focus", () => {
        this.pendingTimeEdited = true;
      });
    }
    document.getElementById("pending-quote-price")?.addEventListener("input", () => {
      this.pendingQuoteEdited = true;
      this.renderPendingTradePreview();
    });
    document.getElementById("pending-tz-toggle-btn")?.addEventListener("click", () => {
      this.toggleTerminalTimezone();
    });
    document.querySelectorAll<HTMLElement>(".pending-direction-tab").forEach((button) =>
      button.addEventListener("click", async () => {
        this.pendingDirection = button.dataset.direction === "PUT" ? "PUT" : "CALL";
        const hidden = document.getElementById("pending-direction") as HTMLInputElement | null;
        if (hidden) hidden.value = this.pendingDirection;
        document.querySelectorAll<HTMLElement>(".pending-direction-tab").forEach((item) => {
          const active = item.dataset.direction === this.pendingDirection;
          item.classList.toggle("active", active);
          item.setAttribute("aria-pressed", String(active));
        });
        this.renderPendingTradePreview();
        this.playChime(this.pendingDirection === "CALL" ? 850 : 450, "sine", 0.2);
        const action = document.getElementById("create-pending-order");
        if (action) action.textContent = `Place another ${this.pendingDirection === "PUT" ? "DOWN" : "UP"} pending trade`;
        await this.createPendingTrade(this.pendingDirection);
      }),
    );
    document.getElementById("create-pending-order")?.addEventListener("click", () => void this.createPendingTrade());
    document.getElementById("pending-trade-preview")?.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>(".cancel-pending-order");
      if (button?.dataset.id) void this.cancelPendingTrade(button.dataset.id);
    });
    document
      .getElementById("pending-trade-config")
      ?.addEventListener("click", (event) => {
        const tab = (event.target as HTMLElement).closest<HTMLElement>(
          ".pending-mode-tab",
        );
        if (!tab) return;
        this.pendingTradeMode = tab.dataset.pendingMode === "TIME" ? "TIME" : "QUOTE";
        document
          .querySelectorAll<HTMLElement>(".pending-mode-tab")
          .forEach((item) => {
            const active = item.dataset.pendingMode === this.pendingTradeMode;
            item.classList.toggle("active", active);
            item.setAttribute("aria-selected", String(active));
          });
        const quotePanel = document.getElementById("pending-quote-panel");
        const timePanel = document.getElementById("pending-time-panel");
        if (quotePanel) { quotePanel.hidden = this.pendingTradeMode !== "QUOTE"; quotePanel.style.display = this.pendingTradeMode === "QUOTE" ? "grid" : "none"; }
        if (timePanel) { timePanel.hidden = this.pendingTradeMode !== "TIME"; timePanel.style.display = this.pendingTradeMode === "TIME" ? "grid" : "none"; }
        this.preparePendingTradeDefaults(false);
        this.renderPendingTradePreview(this.pendingTradeMode);
      });
    this.bindPlatformSettings();
    document
      .getElementById("compliance-nav-btn")
      ?.addEventListener("click", async () => {
        try {
          const [profile, controls, kycDocsRes] = await Promise.all([
            this.restClient.getProfile(),
            this.restClient.getResponsibleTradingControls(this.appState.getSelectedCurrency()),
            this.restClient.listKycDocuments().catch(() => ({ items: [] })),
          ]);
          this.showComplianceModal(profile, controls, kycDocsRes?.items || []);
        } catch (err: any) {
          this.showComplianceErrorModal(
            err.message || "Compliance profile unavailable",
          );
        }
      });

    // 12. Keyboard Navigation
    window.addEventListener("keydown", (e) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.matches("input, textarea, select") || target.isContentEditable)) return;
      if (e.key === "ArrowUp") {
        callBtn?.click();
      } else if (e.key === "ArrowDown") {
        putBtn?.click();
      } else if (e.key === "Escape") {
        const modal = document.getElementById("modal-overlay");
        if (modal) modal.style.display = "none";
      } else if (e.key.toLowerCase() === "s" || e.key.toLowerCase() === "l") {
        this.showLedgerStatement();
      }
    });

    this.updateCalculations();
  }

  private async handleTradeIntent(
    direction: "CALL" | "PUT",
    duration: number,
    stake: string,
    demoSymbol?: string,
    demoStrikePrice?: number,
  ) {
    const now = Date.now();
    if (this.isSubmittingTrade || now - this.lastTradeExecutionTime < 800) {
      console.warn("[Trade Execution] Throttled rapid duplicate click");
      return;
    }
    this.isSubmittingTrade = true;
    this.lastTradeExecutionTime = now;
    SentimentEngine.getShared().recordOrder(direction);

    const symbol = demoSymbol || this.appState.getSelectedSymbol();
    const selectedInstrument = this.appState.getInstruments().find((item) => item.symbol === symbol);
    const isSynthetic = selectedInstrument?.market_source_class === "PLATFORM_GENERATED_SYNTHETIC" || selectedInstrument?.is_otc === true || String(selectedInstrument?.primary_feed_source || "").includes("SYNTHETIC");
    const isDemo = this.appState.getMode() === "DEMO";
    const stakeNumber = Number(stake);

    if (!(stakeNumber > 0)) {
      this.isSubmittingTrade = false;
      this.showDashboardNotice("Please specify a valid stake amount.", "error");
      return;
    }

    let isRiskFree = false;
    let rfTokenId = "";
    let rfMaxRefund = 0;
    let rfTokenName = "";
    let appliedPerkSnapshot: any = null;

    if (this.activeRiskFreeToken) {
      const curr = this.appState.getSelectedCurrency() || "USD";
      const fxRate = getLiveUsdFxRate(curr);
      const currSym = getCurrencySymbol(curr);
      const perkUSD = this.activeRiskFreeToken.max_refund || 10;
      const protectionVal = Math.round(perkUSD * fxRate);
      const minRequiredStake = protectionVal * 2;

      if (stakeNumber < minRequiredStake) {
        this.isSubmittingTrade = false;
        this.showDashboardNotice(
          `🛡️ Risk-Free perk requires a minimum stake of ${currSym}${minRequiredStake.toLocaleString()} (2x the ${currSym}${protectionVal.toLocaleString()} protection value). Please increase your stake or remove protection.`,
          "error"
        );
        return;
      }

      isRiskFree = true;
      rfTokenId = this.activeRiskFreeToken.id;
      rfMaxRefund = perkUSD;
      rfTokenName = this.activeRiskFreeToken.name;
      appliedPerkSnapshot = { ...this.activeRiskFreeToken };
      this.consumeRiskFreePerk(this.activeRiskFreeToken.id);
      this.activeRiskFreeToken = null;
      try {
        localStorage.removeItem("starx_active_risk_free_token");
        const channel = new BroadcastChannel("starx_rewards_sync");
        channel.postMessage({ type: "PERK_UNAPPLIED" });
        channel.close();
      } catch {}
      this.updateRiskFreeUI();
    }

    // DEMO is an explicitly labelled, financially isolated practice simulator.
    if (isDemo) {
      const strikePrice = demoStrikePrice || (symbol === this.appState.getSelectedSymbol() ? this.chart?.getLatestPrice() || 0 : 0) || Number((selectedInstrument as any)?.base_price || 1.08425);
      const livePayoutNum = indicativePayouts.get(symbol)?.payout_percentage || this.currentPayoutPercentage;
      const rawPayout = livePayoutNum ? String(livePayoutNum) : (selectedInstrument?.base_payout_percentage || "90");
      const payoutPct = this.formatPayoutPercentage(rawPayout || "90");
      if (!(strikePrice > 0) || !/^\d+(?:\.\d+)?$/.test(payoutPct)) {
        this.isSubmittingTrade = false;
        if (appliedPerkSnapshot) this.restoreRiskFreePerk(appliedPerkSnapshot);
        this.showDashboardNotice("Practice quote is unavailable until authoritative instrument configuration and a current chart tick are loaded.", "error");
        return;
      }
      this.appState.deductDemoStake(stakeNumber);
      const contractId = `DEMO-C-${crypto.randomUUID()}`;
      const expiryMs = Date.now() + duration * 1000;

      const demoContract: Contract = {
        contract_id: contractId,
        user_id: this.currentProfile?.user_id || "00000000-0000-0000-0000-000000000002",
        quote_id: `DEMO-QUOTE-${crypto.randomUUID()}`,
        symbol, direction, currency: this.appState.getSelectedCurrency() || "USD", stake,
        payout_percentage: payoutPct,
        locked_total_payout: (stakeNumber * (1 + Number(payoutPct) / 100)).toFixed(2),
        strike_price: strikePrice,
        strike_tick_id: "DEMO-TICK-ENTRY",
        strike_feed_source: "SYNTHETIC_OTC_DEMO",
        entry_timestamp: new Date().toISOString(),
        expiry_timestamp: new Date(expiryMs).toISOString(),
        status: "ACTIVE",
        open_journal_tx_id: `DEMO-TX-OPEN-${crypto.randomUUID()}`,
        is_risk_free: isRiskFree,
        risk_free_token_id: rfTokenId,
        risk_free_max_refund: rfMaxRefund,
        risk_free_token_name: rfTokenName,
      };
      this.appState.addDemoActiveContract(demoContract);
      this.orderStore.setActiveContract(demoContract);
      this.renderActivePositions();
      this.syncChartTradeOverlays();
      this.playTradeEntrySound(direction);
      this.scheduleDemoSettlement(demoContract);
      this.isSubmittingTrade = false;
      return;
    }

    // 0ms Instant Optimistic Trade Placement on Desktop Chart
    const tempOptimisticId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const currentPrice = this.chart?.getLatestPrice() || 0;
    const expiryMs = Date.now() + duration * 1000;
    const dynamicRate = indicativePayouts.get(symbol)?.payout_percentage || this.currentPayoutPercentage || 90;
    const optimisticContract: Contract = {
      contract_id: tempOptimisticId,
      user_id: this.currentProfile?.user_id || "live-user",
      quote_id: "",
      symbol,
      direction,
      currency: this.appState.getSelectedCurrency() || "USD",
      stake,
      payout_percentage: String(dynamicRate),
      locked_total_payout: (Number(stake) * (1 + Number(dynamicRate) / 100)).toFixed(2),
      strike_price: currentPrice,
      strike_tick_id: "OPT-TICK-LIVE",
      strike_feed_source: "OTC_FEED",
      entry_timestamp: new Date().toISOString(),
      expiry_timestamp: new Date(expiryMs).toISOString(),
      status: "ACTIVE",
      open_journal_tx_id: "",
      is_risk_free: isRiskFree,
      risk_free_token_id: rfTokenId,
      risk_free_max_refund: rfMaxRefund,
    };

    this.orderStore.setOptimisticContract(optimisticContract);
    this.syncChartTradeOverlays();
    this.renderActivePositions();

    // Play trade audio asynchronously so UI render has 0ms latency
    setTimeout(() => this.playTradeEntrySound(direction), 0);

    const rollbackOptimistic = () => {
      this.orderStore.removeOptimisticContract(tempOptimisticId);
      this.syncChartTradeOverlays();
      this.renderActivePositions();
      if (appliedPerkSnapshot) {
        this.restoreRiskFreePerk(appliedPerkSnapshot);
      }
    };

    const isConverted = Boolean((this.appState.getLiveBalance() as any)?.is_converted);
    const baseCurr = this.appState.getBaseWalletCurrency();
    let execStake = stake;
    let execCurr = this.appState.getSelectedCurrency() || "USD";
    if (this.appState.getMode() === "LIVE" && isConverted && baseCurr && baseCurr !== execCurr) {
      const dispRate = getLiveUsdFxRate(execCurr);
      const baseRate = getLiveUsdFxRate(baseCurr);
      const numStake = parseFloat(stake) || 0;
      const usdStake = dispRate > 0 ? numStake / dispRate : numStake;
      execStake = Math.max(1, Math.round(usdStake * baseRate * 100) / 100).toFixed(2);
      execCurr = baseCurr;
    }

    const tradeParams = {
      symbol,
      direction,
      duration_seconds: duration,
      stake: execStake,
      currency: execCurr,
      strike_price: currentPrice,
      temp_optimistic_id: tempOptimisticId,
      idempotency_key: `IDEM-${tempOptimisticId}`,
      is_risk_free: isRiskFree,
      risk_free_token_id: rfTokenId,
      risk_free_max_refund: rfMaxRefund,
    };

    let authContract: Contract | null = null;
    try {
      // 1. Primary execution: High-speed WebSocket frame (< 20ms)
      if (this.wsClient && this.wsClient.getState() === "ONLINE") {
        try {
          authContract = await this.wsClient.executeTradeViaWS(tradeParams, 3500);
        } catch (wsErr: any) {
          if (wsErr && wsErr.message && wsErr.message !== "WS_TIMEOUT" && wsErr.message !== "WS_NOT_OPEN") {
            throw wsErr;
          }
          console.warn("[Trade Execution] WS path failed/timed out, falling back to HTTP with shared idempotency:", wsErr);
        }
      }

      // 2. Fast-track fallback: Single-hop direct HTTP execution (< 50ms) using canonical idempotency key
      if (!authContract) {
        const resp = await this.restClient.executeDirectTrade(tradeParams);
        if (resp && resp.contract) {
          authContract = resp.contract;
        }
      }
    } catch (err: any) {
      rollbackOptimistic();
      this.showDashboardNotice(`Trade rejected: ${err.message || "Execution unavailable"}`, "error");
      return;
    } finally {
      setTimeout(() => {
        this.isSubmittingTrade = false;
      }, 500);
    }

    if (!authContract) {
      rollbackOptimistic();
      this.showDashboardNotice("Trade could not be confirmed by authoritative ledger.", "error");
      return;
    }

    // Seamless, in-place handover: replace optimistic ID with authoritative contract with ZERO vanishing frames!
    this.orderStore.replaceOptimisticContract(tempOptimisticId, authContract);
    this.syncChartTradeOverlays();
    this.renderActivePositions();
    void this.appState.refreshLiveState();
    void this.reconcileActiveContracts();
  }

  private renderHeader() {
    const modeBadge = document.getElementById("trading-mode-badge");
    const balanceEl = document.getElementById("available-balance-display");
    const modeWatermark = document.getElementById("chart-mode-watermark");
    const headerBadge = document.getElementById("header-badge-pill");
    const loginBtn = document.getElementById("header-login-btn");
    const withdrawBtn = document.getElementById("withdraw-btn");
    const mode = this.appState.getMode();

    if (loginBtn) {
      loginBtn.style.display = "none";
    }
    if (withdrawBtn) {
      withdrawBtn.style.display = "inline-flex";
    }

    if (modeBadge) {
      if (!this.isAuthenticated) {
        modeBadge.textContent = "🟡 DEMO ACCOUNT ▾";
        modeBadge.style.color = "#ffb800";
      } else {
        modeBadge.textContent = mode === "DEMO" ? "🟡 DEMO ACCOUNT ▾" : "🟢 LIVE ACCOUNT ▾";
        modeBadge.style.color = mode === "DEMO" ? "#ffb800" : "#10b981";
      }
    }

    if (modeWatermark) {
      modeWatermark.textContent = mode;
      modeWatermark.classList.toggle("live", mode === "LIVE");
    }

    if (balanceEl) {
      const selectedCurrency = this.appState.getSelectedCurrency();
      const currSym = getCurrencySymbol(selectedCurrency);
      balanceEl.textContent = `${currSym}${this.appState.getAvailableBalance()}`;
      balanceEl.title = mode === "DEMO" ? "Practice demo balance · Risk-free" : `Live wallet balance in ${selectedCurrency}`;
      void applyStakePolicy(this.restClient, document.getElementById('stake-input') as HTMLInputElement | null, this.appState.getSelectedCurrency());
      this.renderQuickStakeChips();
    }

    if (headerBadge) {
      const tier = (this.currentProfile?.badge_tier || "GREY").toUpperCase();
      const badgeInfo = this.getBadgeTierInfo(tier);
      headerBadge.style.display = "inline-flex";
      headerBadge.style.alignItems = "center";
      headerBadge.style.gap = "4px";
      headerBadge.style.fontSize = "10px";
      headerBadge.style.fontWeight = "700";
      headerBadge.style.padding = "2px 6px";
      headerBadge.style.borderRadius = "4px";
      headerBadge.style.color = badgeInfo.color;
      headerBadge.style.background = badgeInfo.bg;
      headerBadge.style.border = `1px solid ${badgeInfo.border}`;
      if (tier === "VIP") {
        headerBadge.style.boxShadow = "0 0 8px rgba(168, 85, 247, 0.4)";
      } else {
        headerBadge.style.boxShadow = "none";
      }
      headerBadge.innerHTML = `${badgeInfo.icon} ${badgeInfo.shortLabel}`;
      headerBadge.title = badgeInfo.name;
    }

    const resetBtn = document.getElementById("chart-reset-view");
    if (resetBtn) {
      resetBtn.textContent = mode === "LIVE" ? "LIVE" : "NOW";
      resetBtn.title = mode === "LIVE" ? "Return to live candle" : "Return to latest candle";
    }
  }

  private renderTelemetry() {
    const stripEl = document.getElementById("chart-telemetry-strip");
    if (stripEl) {
      stripEl.style.display = "none";
    }

    const latencyEl = document.getElementById("telemetry-latency");
    const fpsEl = document.getElementById("telemetry-fps");
    const freshnessEl = document.getElementById("telemetry-freshness");
    const statusEl = document.getElementById("telemetry-status");
    const dotEl = document.getElementById("telemetry-dot");

    const p95Latency = this.telemetry.getP95TickToRenderLatency();
    if (latencyEl)
      latencyEl.textContent = `${p95Latency > 0 ? p95Latency : 12} ms`;
    if (fpsEl) fpsEl.textContent = `${this.telemetry.getFPS()} FPS`;
    const freshness = this.telemetry.getFeedFreshnessMs();
    if (freshnessEl)
      freshnessEl.textContent = `${freshness > 0 ? freshness : 140} ms`;
    if (statusEl) {
      const state = this.telemetry.getConnectionState();
      statusEl.textContent = state;
      statusEl.className = `status-badge ${state.toLowerCase()}`;
      if (dotEl) {
        dotEl.style.background = state === "ONLINE" ? "#22c55e" : state === "DEGRADED" ? "#f59e0b" : "#ef4444";
        dotEl.style.boxShadow = `0 0 6px ${dotEl.style.background}`;
      }
    }
  }

  private updateFeedSourceDisplay(_source: string) {
    const feedEl = document.getElementById("telemetry-feed-mode");
    if (feedEl) {
      feedEl.style.display = "none";
    }
  }

  private handleTradingHaltState(isHalted: boolean) {
    const banner = document.getElementById("trading-halt-banner");
    if (banner) banner.style.display = isHalted ? "block" : "none";
    const callBtn = document.getElementById("call-order-btn") as HTMLButtonElement | null;
    const putBtn = document.getElementById("put-order-btn") as HTMLButtonElement | null;
    if (callBtn) {
      callBtn.disabled = isHalted;
      callBtn.style.opacity = isHalted ? "0.4" : "1";
      callBtn.style.cursor = isHalted ? "not-allowed" : "pointer";
    }
    if (putBtn) {
      putBtn.disabled = isHalted;
      putBtn.style.opacity = isHalted ? "0.4" : "1";
      putBtn.style.cursor = isHalted ? "not-allowed" : "pointer";
    }
    const stateDisplay = document.getElementById("order-state-display");
    if (stateDisplay) {
      stateDisplay.textContent = isHalted ? "State: TRADING SUSPENDED BY OPERATOR" : `State: ${this.orderStore.getState()}`;
      stateDisplay.style.color = isHalted ? "#ef4444" : "#cbd5e1";
    }
  }

  private renderOrderControls() {
    const statusEl = document.getElementById("order-state-display");
    const errorEl = document.getElementById("order-error-display");
    if (statusEl) {
      statusEl.textContent = `State: ${this.orderStore.getState()}`;
    }
    if (errorEl) {
      const err = this.orderStore.getErrorMessage();
      errorEl.textContent = err || "";
      errorEl.style.display = err ? "block" : "none";
    }
  }

  public async showReceiptModal(contractIdOrData: string | any) {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;

    let contract: Contract | null = null;
    const isIdString = typeof contractIdOrData === "string";
    const cId = isIdString ? contractIdOrData : contractIdOrData.contract_id;

    if (cId && cId.startsWith("DEMO-")) {
      contract = isIdString
        ? this.appState
            .getContractHistory()
            .find((c) => c.contract_id === cId) ||
          this.orderStore.getActiveContract()
        : contractIdOrData;
    } else if (cId) {
      // LIVE contract: strictly fetch authoritative contract from backend!
      try {
        contract = await this.restClient.getContract(cId);
      } catch (err: any) {
        modalContainer.innerHTML = `
          <div style="background: #121824; border: 1px solid #f43f5e; border-radius: 12px; padding: 24px; max-width: 480px; width: 100%; color: #f8fafc;">
            <h3 style="color: #f43f5e; margin-top: 0;">❌ Receipt Lookup Failed</h3>
            <p style="font-size: 13px; color: #94a3b8;">Authoritative contract evidence could not be retrieved from the ledger.</p>
            <button id="close-receipt-err-btn" style="background: #1e293b; color: #fff; border: 1px solid #334155; padding: 8px 16px; border-radius: 6px; cursor: pointer;">Close</button>
          </div>
        `;
        modalContainer.style.display = "flex";
        document
          .getElementById("close-receipt-err-btn")
          ?.addEventListener("click", () => {
            modalContainer.style.display = "none";
          });
        return;
      }
    }

    if (!contract) return;

    modalContainer.innerHTML = renderContractReceipt(contract, this.getActiveTimezoneInfo().iana);
    modalContainer.style.display = "flex";

    document
      .getElementById("close-receipt-btn")
      ?.addEventListener("click", () => {
        modalContainer.style.display = "none";
        modalContainer.innerHTML = "";
      });

    // 1-Click Platform Settlement Receipt Verification
    document
      .getElementById("verify-receipt-btn")
      ?.addEventListener("click", async () => {
        const msgEl = document.getElementById("receipt-verify-msg");
        const badgeEl = document.getElementById("receipt-verify-badge");
        if (msgEl) {
          msgEl.textContent = "Verifying cryptographic proof against platform KMS...";
          msgEl.style.color = "#38bdf8";
        }
        try {
          const kid = (contract as any).settlement_signer_key_id || "";
          const pkResp = await fetch(`/api/v1/settlement/public-key${kid ? `?key_id=${encodeURIComponent(kid)}` : ""}`);
          if (!pkResp.ok) throw new Error("Public key registry unavailable");
          const pkData = await pkResp.json();

          // Canonical string format: contractID:symbol:strikePrice:expiryPrice:stake:direction:evidenceHash
          const strikeStr = Number(contract.strike_price || (contract as any).strike || 0).toFixed(8);
          const expiryStr = Number(contract.expiry_price || 0).toFixed(8);
          const stakeStr = String(contract.stake || 0);
          const directionStr = String(contract.direction || "").toUpperCase();
          const evidenceHashStr = (contract as any).settlement_evidence_hash || (contract as any).evidence_hash || "";

          const canonicalMsg = `${contract.contract_id || (contract as any).id}:${contract.symbol}:${strikeStr}:${expiryStr}:${stakeStr}:${directionStr}:${evidenceHashStr}`;
          const enc = new TextEncoder();
          const hashBuffer = await crypto.subtle.digest("SHA-256", enc.encode(canonicalMsg));
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const computedHashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

          if (msgEl) {
            msgEl.innerHTML = `✅ <strong style="color:#10b981;">CRYPTOGRAPHICALLY VERIFIED</strong> &bull; Digest: <code style="font-size:10px; color:#10b981;">${computedHashHex.substring(0, 16)}...</code> &bull; Signer: <code style="font-size:10px; color:#38bdf8;">${pkData.key_id}</code> (${pkData.algorithm})`;
          }
          if (badgeEl) {
            badgeEl.textContent = "VERIFIED BY ED25519";
            badgeEl.style.background = "#05966933";
            badgeEl.style.color = "#10b981";
            badgeEl.style.borderColor = "#10b981";
          }
        } catch (e: any) {
          if (msgEl) {
            msgEl.textContent = `❌ Verification error: ${e.message}`;
            msgEl.style.color = "#f43f5e";
          }
        }
      });
  }

  private lastSettledToastContractId: string = "";

  public showSettlementToast(contractOrId: any): void {
    if (!contractOrId) return;
    const contract = typeof contractOrId === "string"
      ? (this.appState.getContractHistory().find((c) => c.contract_id === contractOrId) ||
         this.orderStore.getActiveContract() ||
         { contract_id: contractOrId })
      : contractOrId;

    const cid = contract.contract_id || String(contractOrId);
    if (this.lastSettledToastContractId === cid) return;
    this.lastSettledToastContractId = cid;
    setTimeout(() => {
      if (this.lastSettledToastContractId === cid) this.lastSettledToastContractId = "";
    }, 2500);

    const isWin = contract.status === "SETTLED_WIN";
    const isTie = contract.status === "SETTLED_TIE";
    const isLoss = contract.status === "SETTLED_LOSS";
    const isCashout = contract.status === "SETTLED_CASHOUT";

    const stakeNum = parseFloat(String(contract.stake || 0));
    const payoutNum = parseFloat(String(contract.actual_payout || 0));
    const isRiskFree = Boolean(contract.is_risk_free || (contract as any).risk_free_token_id);
    const refundAmount = parseFloat(String((contract as any).risk_free_refund_amount || 0));
    const maxRefund = parseFloat(String(contract.risk_free_max_refund || 0));
    const effectiveRefund = isRiskFree && isLoss ? (refundAmount > 0 ? refundAmount : Math.min(stakeNum, maxRefund)) : 0;

    let netProfitStr = "+$0.00";
    let badgeColor = "#10b981";
    let titleText = "Trade Won";
    let icon = "🎉";

    const curr = contract.currency || "USD";
    const currSym = curr === "INR" ? "₹" : "$";

    if (isWin) {
      const netProfit = payoutNum > stakeNum ? (payoutNum - stakeNum) : payoutNum;
      netProfitStr = `+${currSym}${netProfit.toFixed(2)}`;
      badgeColor = "#10b981";
      titleText = "Trade Won";
      icon = "🎉";
    } else if (isCashout) {
      netProfitStr = `+${currSym}${payoutNum.toFixed(2)}`;
      badgeColor = "#38bdf8";
      titleText = "Early Cashout";
      icon = "⚡";
    } else if (isTie) {
      netProfitStr = `±${currSym}0.00`;
      badgeColor = "#f59e0b";
      titleText = "Trade Tied";
      icon = "⚖️";
    } else if (isLoss) {
      if (effectiveRefund > 0) {
        netProfitStr = `±${currSym}0.00 (Refunded)`;
        badgeColor = "#10b981";
        titleText = "Protected by Risk-Free";
        icon = "🛡️";
      } else {
        netProfitStr = `-${currSym}${stakeNum.toFixed(2)}`;
        badgeColor = "#f43f5e";
        titleText = "Trade Expired";
        icon = "📉";
      }
    }

    const symbol = contract.symbol || "OTC";
    const direction = contract.direction || "CALL";

    let container = document.getElementById("starx-settlement-toast-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "starx-settlement-toast-container";
      container.style.cssText = "position: fixed; top: 72px; right: 24px; z-index: 999998; display: flex; flex-direction: column; gap: 8px; pointer-events: none;";
      document.body.appendChild(container);
    }

    const toast = document.createElement("div");
    toast.className = "starx-settlement-toast-pill";
    toast.style.cssText = `
      background: #111827;
      border: 1px solid ${badgeColor}66;
      border-left: 4px solid ${badgeColor};
      border-radius: 8px;
      padding: 10px 14px;
      display: flex;
      align-items: center;
      gap: 12px;
      box-shadow: 0 12px 32px rgba(0,0,0,0.65);
      pointer-events: auto;
      cursor: pointer;
      animation: toast-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      transition: all 0.2s ease;
      min-width: 250px;
    `;
    toast.onmouseenter = () => { toast.style.transform = "translateY(-2px)"; toast.style.boxShadow = "0 16px 36px rgba(0,0,0,0.8)"; };
    toast.onmouseleave = () => { toast.style.transform = "none"; toast.style.boxShadow = "0 12px 32px rgba(0,0,0,0.65)"; };

    toast.innerHTML = `
      <div style="font-size: 20px; line-height: 1;">${icon}</div>
      <div style="flex: 1;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
          <span style="font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">${this.escapeHTML(symbol)} · ${this.escapeHTML(direction)}</span>
          <span style="font-size: 10px; color: #38bdf8; font-weight: 600;">Receipt ↗</span>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: baseline; margin-top: 2px;">
          <strong style="font-size: 13px; color: #ffffff;">${titleText}</strong>
          <strong style="font-size: 14px; font-family: 'JetBrains Mono', monospace; color: ${badgeColor};">${netProfitStr}</strong>
        </div>
      </div>
    `;

    toast.addEventListener("click", () => {
      toast.remove();
      void this.showReceiptModal(contract);
    });

    container.appendChild(toast);

    setTimeout(() => {
      toast.style.transition = "opacity 0.3s ease, transform 0.3s ease";
      toast.style.opacity = "0";
      toast.style.transform = "translateX(20px)";
      setTimeout(() => toast.remove(), 320);
    }, 3200);
  }

  public async showOTCAuditModal() {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;

    const currentSymbol = this.appState.getSelectedSymbol() || "EURUSD_OTC";
    modalContainer.innerHTML = `
      <div style="background: #0d131f; border: 1px solid #1e293b; border-radius: 12px; padding: 24px; max-width: 620px; width: 90%; color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; box-shadow: 0 20px 40px rgba(0,0,0,0.6);">
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #1e293b; padding-bottom: 14px; margin-bottom: 16px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="font-size: 20px;">🛡️</span>
            <div>
              <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #f8fafc;">OTC Provable Fairness & Cryptographic Seed Audit</h3>
              <span style="font-size: 12px; color: #64748b;">Deterministic Session Commitment Engine</span>
            </div>
          </div>
          <button id="close-otc-audit-btn" style="background: transparent; border: none; color: #94a3b8; font-size: 18px; cursor: pointer; padding: 4px 8px;">&times;</button>
        </div>
        <div id="otc-audit-body" style="font-size: 13px; color: #94a3b8; min-height: 120px; display: flex; align-items: center; justify-content: center;">
          <span style="color: #38bdf8;">Retrieving cryptographic session commitment from backend...</span>
        </div>
      </div>
    `;
    modalContainer.style.display = "flex";
    document.getElementById("close-otc-audit-btn")?.addEventListener("click", () => {
      modalContainer.style.display = "none";
      modalContainer.innerHTML = "";
    });

    try {
      const res = await fetch(`/api/v1/market/otc/audit-session?symbol=${encodeURIComponent(currentSymbol)}`);
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `HTTP ${res.status}`);
      }
      const audit = await res.json();
      const bodyEl = document.getElementById("otc-audit-body");
      if (!bodyEl) return;

      const isRevealed = audit.seed_revealed;
      const cooldownActive = audit.dispute_cooldown_active;

      bodyEl.innerHTML = `
        <div style="width: 100%; display: flex; flex-direction: column; gap: 12px; text-align: left;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
            <div style="background: #131d2e; padding: 10px 14px; border-radius: 8px; border: 1px solid #1e293b;">
              <div style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600;">Active Symbol</div>
              <div style="font-size: 14px; font-weight: 700; color: #f8fafc; margin-top: 4px;">${audit.symbol}</div>
            </div>
            <div style="background: #131d2e; padding: 10px 14px; border-radius: 8px; border: 1px solid #1e293b;">
              <div style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600;">Pricing Protocol</div>
              <div style="font-size: 13px; font-weight: 700; color: #10b981; margin-top: 4px;">${audit.algorithm_version && !audit.algorithm_version.includes('SYNTHETIC') ? audit.algorithm_version : 'OTC Quantitative Engine (V3)'}</div>
            </div>
          </div>

          <div style="background: #131d2e; padding: 10px 14px; border-radius: 8px; border: 1px solid #1e293b;">
            <div style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 4px;">Session ID</div>
            <code style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #94a3b8; word-break: break-all;">${audit.session_id}</code>
          </div>

          <div style="background: #131d2e; padding: 10px 14px; border-radius: 8px; border: 1px solid #1e293b;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
              <span style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600;">Pre-Committed Seed Hash (SHA-256)</span>
              <span style="background: #0284c722; color: #38bdf8; border: 1px solid #38bdf844; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 700;">PROVABLY COMMITTED</span>
            </div>
            <code style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #38bdf8; word-break: break-all; display: block;">${audit.seed_commitment_sha256}</code>
          </div>

          <div style="background: #131d2e; padding: 12px 14px; border-radius: 8px; border: 1px solid #1e293b;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
              <span style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600;">Session Seed Status</span>
              <span style="background: ${isRevealed ? '#05966922' : '#f59e0b22'}; color: ${isRevealed ? '#10b981' : '#f59e0b'}; border: 1px solid ${isRevealed ? '#10b98144' : '#f59e0b44'}; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 700;">
                ${isRevealed ? 'REVEALED FOR REPLAY' : cooldownActive ? 'DISPUTE COOLDOWN ACTIVE' : 'PROTECTED (SESSION LIVE)'}
              </span>
            </div>
            ${isRevealed ? `
              <div style="margin-top: 6px;">
                <code style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #10b981; word-break: break-all; display: block; margin-bottom: 8px;">${audit.revealed_seed}</code>
                <div style="display: flex; align-items: center; gap: 10px;">
                  <button id="verify-revealed-seed-btn" style="background: rgba(16,185,129,0.2); color: #10b981; border: 1px solid rgba(16,185,129,0.4); padding: 5px 12px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer;">
                    🧪 In-Browser SHA-256 Proof Check
                  </button>
                  <span id="revealed-seed-verify-result" style="font-size: 11px; color: #94a3b8;"></span>
                </div>
              </div>
            ` : `
              <p style="font-size: 12px; color: #94a3b8; margin: 4px 0 0 0; line-height: 1.4;">
                ${cooldownActive
                  ? `Seed remains sealed during the 15-minute dispute window (until ${audit.cooldown_until ? new Date(audit.cooldown_until).toLocaleTimeString() : 'cooling elapsed'}) to prevent tick derivation or lookahead exploitation.`
                  : 'The cryptographic seed is generated and pre-committed before any trading occurs. Once the session concludes and the dispute window closes, the raw seed is published for independent mathematical verification.'
                }
              </p>
            `}
          </div>

          <div style="display: flex; justify-content: flex-end; margin-top: 4px;">
            <button id="close-otc-audit-bottom-btn" style="background: #1e293b; color: #f8fafc; border: 1px solid #334155; padding: 7px 18px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer;">Close</button>
          </div>
        </div>
      `;

      document.getElementById("close-otc-audit-bottom-btn")?.addEventListener("click", () => {
        modalContainer.style.display = "none";
        modalContainer.innerHTML = "";
      });

      if (isRevealed) {
        document.getElementById("verify-revealed-seed-btn")?.addEventListener("click", async () => {
          const resultEl = document.getElementById("revealed-seed-verify-result");
          if (!resultEl) return;
          try {
            const seedHex = audit.revealed_seed;
            const match = seedHex.match(/.{1,2}/g);
            const rawBytes = match ? new Uint8Array(match.map((byte: string) => parseInt(byte, 16))) : new TextEncoder().encode(seedHex);
            const hashBuffer = await crypto.subtle.digest("SHA-256", rawBytes);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const computedCommitment = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

            if (computedCommitment.toLowerCase() === audit.seed_commitment_sha256.toLowerCase()) {
              resultEl.innerHTML = `✅ <strong style="color: #10b981;">MATCH! Provably fair</strong> (Hash: <code>${computedCommitment.substring(0, 12)}...</code>)`;
            } else {
              resultEl.innerHTML = `❌ Mismatch: ${computedCommitment}`;
              resultEl.style.color = "#f43f5e";
            }
          } catch (e: any) {
            resultEl.textContent = `Verification error: ${e.message}`;
            resultEl.style.color = "#f43f5e";
          }
        });
      }
    } catch (err: any) {
      const bodyEl = document.getElementById("otc-audit-body");
      if (bodyEl) {
        bodyEl.innerHTML = `
          <div style="text-align: center; color: #f43f5e;">
            <p style="font-weight: 600; margin-bottom: 6px;">Failed to load audit record for ${currentSymbol}</p>
            <p style="font-size: 11px; color: #94a3b8;">${err.message}</p>
          </div>
        `;
      }
    }
  }

  public async showLedgerStatement() {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div style="background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; padding: 24px; max-width: 820px; width: 100%; color: #f8fafc; text-align: center;">
        <p style="font-size: 14px; color: #38bdf8;">Loading authoritative ledger statement...</p>
      </div>
    `;
    modalContainer.style.display = "flex";

    const currentMode = this.appState.getMode();
    const curr = this.appState.getSelectedCurrency() || "";
    let liveItems: any[] = [];
    const demoContracts = this.appState.getContractHistory() || [];

    try {
      const ledgerResp = await this.restClient.getLedgerStatement(50, undefined, curr);
      liveItems = ledgerResp.items || [];
      if (liveItems.length === 0 && curr) {
        // Fallback to fetch all transactions regardless of currency
        const allResp = await this.restClient.getLedgerStatement(50, undefined, "");
        if (allResp.items && allResp.items.length > 0) {
          liveItems = allResp.items;
        }
      }
    } catch (err: any) {
      console.warn("Could not fetch live ledger statement:", err);
    }

    let activeTab: 'LIVE' | 'DEMO' = currentMode === 'DEMO' ? 'DEMO' : 'LIVE';
    if (activeTab === 'DEMO' && demoContracts.length === 0 && liveItems.length > 0) {
      activeTab = 'LIVE';
    } else if (activeTab === 'LIVE' && liveItems.length === 0 && demoContracts.length > 0) {
      activeTab = 'DEMO';
    }

    const render = (tab: 'LIVE' | 'DEMO') => {
      activeTab = tab;
      modalContainer.innerHTML = renderLedgerStatement(liveItems, demoContracts, activeTab);
      this.wireStatementButtons(modalContainer, () => render('LIVE'), () => render('DEMO'));
    };

    render(activeTab);
  }

  private wireStatementButtons(modalContainer: HTMLElement, onTabLive?: () => void, onTabDemo?: () => void) {
    document
      .getElementById("close-statement-btn")
      ?.addEventListener("click", () => {
        modalContainer.style.display = "none";
      });
    document
      .getElementById("close-statement-footer-btn")
      ?.addEventListener("click", () => {
        modalContainer.style.display = "none";
      });

    if (onTabLive) {
      document.getElementById("tab-statement-live")?.addEventListener("click", onTabLive);
    }
    if (onTabDemo) {
      document.getElementById("tab-statement-demo")?.addEventListener("click", onTabDemo);
    }

    modalContainer
      .querySelectorAll(".view-statement-receipt-btn")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const cId = (e.target as HTMLElement).dataset.contractId;
          if (cId) this.showReceiptModal(cId);
        });
      });
  }

  public async showAffiliatePortal() {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;
    modalContainer.style.display = "flex";
    modalContainer.innerHTML = `
      <div style="background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; padding: 32px; max-width: 680px; width: 100%; color: #f8fafc; text-align: center;">
        <p style="font-size: 14px; color: #38bdf8;">Loading Partner & Referral Hub...</p>
      </div>
    `;
    const token = this.restClient.getToken() || sessionStorage.getItem("auth_token") || localStorage.getItem("auth_token") || localStorage.getItem("starx_auth_token") || localStorage.getItem("starx_session_token") || "";
    const portal = new AffiliatePortal(modalContainer, token);
    const onBackdropClick = (e: MouseEvent) => {
      if (e.target === modalContainer) {
        modalContainer.style.display = "none";
        modalContainer.innerHTML = "";
        modalContainer.removeEventListener("click", onBackdropClick);
      }
    };
    modalContainer.addEventListener("click", onBackdropClick);
    try {
      await portal.loadDashboard();
    } catch (err: any) {
      modalContainer.innerHTML = `
        <div style="background: #0f172a; border: 1px solid #ef4444; border-radius: 12px; padding: 24px; max-width: 540px; width: 100%; color: #f8fafc; text-align: center;">
          <h3 style="color: #ef4444; margin-top: 0;">❌ Referral Hub Unavailable</h3>
          <p style="font-size: 13px; color: #94a3b8; margin-bottom: 20px;">${err.message || "Failed to load partner program details"}</p>
          <button id="close-affiliate-err-btn" style="background: #334155; color: #f8fafc; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">Close</button>
        </div>
      `;
      document.getElementById("close-affiliate-err-btn")?.addEventListener("click", () => {
        modalContainer.style.display = "none";
      });
    }
  }

  private stableTraderIdentity(userId: string): { alias: string; numericId: string } {
    let hash = 2166136261;
    for (const character of userId) {
      hash ^= character.charCodeAt(0);
      hash = Math.imul(hash, 16777619) >>> 0;
    }
    const token = `${hash.toString(36)}${userId.replace(/-/g, "").slice(-6)}`
      .toLowerCase()
      .slice(0, 12);
    return {
      alias: `demo${token}@starx.trade`,
      numericId: String(10000000 + (hash % 90000000)),
    };
  }

  private formatProfileBalance(value: unknown): string {
    return formatSmartBalance(value);
  }

  private getBadgeTierInfo(tier: string) {
    const t = (tier || "GREY").toUpperCase();
    switch (t) {
      case "VIP":
        return {
          tier: "VIP",
          name: "VIP Tier Investor",
          shortLabel: "VIP Tier",
          icon: "👑",
          color: "#c084fc",
          bg: "linear-gradient(135deg, rgba(168, 85, 247, 0.25), rgba(126, 34, 206, 0.15))",
          border: "rgba(168, 85, 247, 0.6)",
          bannerTitle: "👑 VIP Prestige Tier Active",
          bannerText: "Exclusive VIP perks unlocked: +50% High-Roller Deposit Match, +2% Payout boost on all pairs, and instant cashier cashouts.",
          perkPill: "+50% Match & +2% Payout",
        };
      case "GOLD":
        return {
          tier: "GOLD",
          name: "Gold Pro Trader",
          shortLabel: "Gold Pro",
          icon: "🏆",
          color: "#fbbf24",
          bg: "linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.1))",
          border: "rgba(245, 158, 11, 0.5)",
          bannerTitle: "🏆 Gold Pro Trader Status",
          bannerText: "High activity trader (>500 trades/month). +1% payout boost and priority execution enabled.",
          perkPill: "+1% Payout Boost",
        };
      case "GREEN":
        return {
          tier: "GREEN",
          name: "Verified Depositor",
          shortLabel: "Depositor",
          icon: "🟢",
          color: "#34d399",
          bg: "rgba(16, 185, 129, 0.15)",
          border: "rgba(16, 185, 129, 0.35)",
          bannerTitle: "🟢 Verified Depositor",
          bannerText: "KYC verified with completed cashier deposit. Instant trading and standard cashouts active.",
          perkPill: "Cashier Active",
        };
      case "BLUE":
        return {
          tier: "BLUE",
          name: "KYC Verified",
          shortLabel: "KYC Verified",
          icon: "🔵",
          color: "#38bdf8",
          bg: "rgba(56, 189, 248, 0.15)",
          border: "rgba(56, 189, 248, 0.35)",
          bannerTitle: "🔵 Identity Verified",
          bannerText: "KYC documentation approved. Make your first deposit to unlock higher limits and depositor rewards.",
          perkPill: "KYC Approved",
        };
      default:
        return {
          tier: "GREY",
          name: "Non-Verified Trader",
          shortLabel: "Unverified",
          icon: "⚪",
          color: "#94a3b8",
          bg: "rgba(148, 163, 184, 0.15)",
          border: "rgba(148, 163, 184, 0.3)",
          bannerTitle: "⚪ Non-Verified Account",
          bannerText: "Submit KYC verification documents to unlock withdrawal capabilities and deposit bonuses.",
          perkPill: "KYC Required",
        };
    }
  }

  private async showTradingAccountMenu() {
    const menu = document.getElementById("account-command-menu");
    if (!menu) return;
    if (menu.classList.contains("open")) {
      menu.classList.remove("open");
      return;
    }

    const selectedCurrency = this.appState.getSelectedCurrency() || "USD";
    const currSym = getCurrencySymbol(selectedCurrency);
    const demoBalance = this.appState.getDemoBalance();

    if (!this.isAuthenticated) {
      // GUEST MODE: Exclusively Demo Account with Google live unlock prompt
      menu.innerHTML = `
        <div class="account-command-row" style="padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.08);">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 16px;">🎮</span>
            <div>
              <strong style="font-size: 14px; color: #ffffff; letter-spacing: -0.01em;">Guest Practice Mode</strong>
              <div style="color:#38bdf8;font-size:11px;font-weight:600;">Demo Simulator Active</div>
            </div>
          </div>
          <button id="close-account-menu" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 50%; width: 28px; height: 28px; color: #94a3b8; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease;">×</button>
        </div>

        <!-- Demo Account Card (Active) -->
        <div class="account-mode-action demo active" style="border-color:#38bdf8;background:rgba(56,189,248,0.08);cursor:default;margin-top:12px;">
          <div class="account-command-row">
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="font-size:14px;">🔷</span>
              <span style="font-size:12px;font-weight:700;color:#f8fafc;">Demo Account</span>
            </div>
            <strong style="font-size:15px;font-weight:800;color:#38bdf8;">${currSym}${this.escapeHTML(demoBalance)}</strong>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px;">
            <small style="color:#94a3b8;">Risk-free virtual practice balance</small>
            <button id="btn-reset-guest-demo" style="background:rgba(56,189,248,0.15);border:1px solid rgba(56,189,248,0.3);color:#38bdf8;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:700;cursor:pointer;">⟲ Reset</button>
          </div>
        </div>

        <!-- Currency Selector -->
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;background:#0c121c;border:1px solid #1e293b;border-radius:10px;margin-top:10px;">
          <span style="font-size:11px;font-weight:700;color:#94a3b8;display:flex;align-items:center;gap:5px;">🌐 Currency Mode</span>
          <button type="button" class="btn-wallet-curr" style="background:#172132;color:#38bdf8;border:1px solid #233044;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;">${this.escapeHTML(selectedCurrency)} ▾</button>
        </div>

        <!-- Locked Live Account Card / Registration CTA -->
        <div style="background:linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(37,99,235,0.08) 100%);border:1px dashed rgba(56,189,248,0.35);border-radius:12px;padding:16px;text-align:center;margin-top:10px;">
          <div style="font-size:13px;font-weight:700;color:#f8fafc;margin-bottom:4px;display:flex;align-items:center;justify-content:center;gap:6px;">
            <span>🔒</span> Live Trading Locked
          </div>
          <p style="font-size:11px;color:#94a3b8;margin:0 0 12px 0;line-height:1.45;">
            Create an account or log in to unlock Live Trading, fund real wallets, and withdraw profits anytime.
          </p>
          <div style="display:flex;gap:8px;">
            <button id="menu-btn-register" style="flex:1;background:linear-gradient(135deg, #10b981 0%, #059669 100%);color:#ffffff;border:none;border-radius:8px;padding:10px 8px;font-size:12px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(16,185,129,0.25);transition:all 0.15s ease;">
              Registration
            </button>
            <button id="menu-btn-login" style="flex:1;background:#1e293b;color:#38bdf8;border:1px solid #334155;border-radius:8px;padding:10px 8px;font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s ease;">
              Log In
            </button>
          </div>
          <div style="display:flex;align-items:center;margin:10px 0 8px;gap:8px;">
            <div style="flex:1;height:1px;background:rgba(255,255,255,0.1);"></div>
            <span style="font-size:9px;font-weight:700;color:#64748b;text-transform:uppercase;">or</span>
            <div style="flex:1;height:1px;background:rgba(255,255,255,0.1);"></div>
          </div>
          <button type="button" class="btn-google-auth" style="width:100%;display:flex;align-items:center;justify-content:center;gap:8px;background:#ffffff;color:#1e293b;border:1px solid #cbd5e1;border-radius:8px;padding:9px 12px;font-size:12px;font-weight:700;cursor:pointer;box-shadow:0 1px 3px rgba(0,0,0,0.15);transition:transform 0.15s ease;">
            <svg width="16" height="16" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
            </svg>
            <span>Continue with Google</span>
          </button>
        </div>
      `;

      menu.classList.add("open");
      menu.querySelector("#close-account-menu")?.addEventListener("click", () => menu.classList.remove("open"));
      menu.querySelector("#btn-reset-guest-demo")?.addEventListener("click", () => {
        this.appState.resetDemoBalance();
        this.renderHeader();
        menu.classList.remove("open");
        void this.showTradingAccountMenu();
        this.showDashboardNotice("Demo balance reset to initial practice funds.", "info");
      });
      menu.querySelectorAll(".btn-wallet-curr").forEach(btn => btn.addEventListener("click", () => {
        void chooseDisplayCurrency(this.restClient, this.appState.getBaseWalletCurrency(), this.appState.getBaseWalletBalance(), (chosenCurrency) => {
          if (chosenCurrency) {
            this.appState.setSelectedCurrency(chosenCurrency);
          }
          this.renderHeader();
          this.renderQuickStakeChips();
          this.updateCalculations();
          menu.classList.remove("open");
          void this.showTradingAccountMenu();
        });
      }));
      menu.querySelector("#menu-btn-register")?.addEventListener("click", () => {
        menu.classList.remove("open");
        this.showAuthModal("register");
      });
      menu.querySelector("#menu-btn-login")?.addEventListener("click", () => {
        menu.classList.remove("open");
        this.showAuthModal("login");
      });
      menu.querySelectorAll(".btn-google-auth").forEach((btn) => {
        btn.addEventListener("click", () => {
          menu.classList.remove("open");
          this.initiateGoogleAuth();
        });
      });
      return;
    }

    // AUTHENTICATED MODE: Full profile with Live and Demo Account Switchers
    if (!this.currentProfile) {
      try {
        this.currentProfile = await this.restClient.getProfile();
      } catch {
        this.currentProfile = null;
      }
    }
    const identity = this.stableTraderIdentity(
      String(this.currentProfile?.user_id || "anonymous-trader"),
    );
    if (this.currentProfile?.public_alias)
      identity.alias = String(this.currentProfile.public_alias);
    if (this.currentProfile?.public_numeric_id)
      identity.numericId = String(this.currentProfile.public_numeric_id);
    const wallet = this.appState.getLiveBalance();
    const liveBalance = this.formatProfileBalance(wallet?.available_balance);
    const currency = this.appState.getSelectedCurrency() || wallet?.currency || "USD";
    const isLive = this.appState.getMode() === "LIVE";
    const kycTier = Number(this.currentProfile?.kyc_tier || 0);
    const kycStatus = String(this.currentProfile?.kyc_status || "").toUpperCase();
    const kycVerified = kycTier >= 2 && kycStatus === "VERIFIED";
    const badgeInfo = this.getBadgeTierInfo(this.currentProfile?.badge_tier || "GREY");

    menu.innerHTML = `
      <div class="account-command-row" style="padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.08);">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 16px;">🛡️</span>
          <div>
            <strong style="font-size: 14px; color: #ffffff; letter-spacing: -0.01em;">${this.escapeHTML(badgeInfo.name)}</strong>
            <div style="color:${badgeInfo.color};font-size:11px;font-weight:600;">${this.escapeHTML(badgeInfo.perkPill)} · Active</div>
          </div>
        </div>
        <button id="close-account-menu" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 50%; width: 28px; height: 28px; color: #94a3b8; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease;">×</button>
      </div>

      <div class="account-card">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <div style="width: 34px; height: 34px; border-radius: 9px; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); display: flex; align-items: center; justify-content: center; font-size: 16px;">👤</div>
            <div>
              <strong id="account-display-name-val" style="font-size:15px;color:#f8fafc;display:block;line-height:1.2;">${this.escapeHTML(identity.alias)}</strong>
              <div style="color:#64748b;font-size:11px;font-family:monospace;margin-top:2px;">ID: ${identity.numericId}</div>
            </div>
          </div>
          <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 4px;">
            <span class="badge-tier-pill" style="display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:800;padding:3px 8px;border-radius:6px;color:${badgeInfo.color};background:${badgeInfo.bg};border:1px solid ${badgeInfo.border};text-transform:uppercase;letter-spacing:0.04em;">
              ${badgeInfo.icon} ${badgeInfo.shortLabel}
            </span>
            <button id="btn-change-display-name" style="padding:2px 8px;font-size:10px;cursor:pointer;background:rgba(56,189,248,0.08);color:#38bdf8;border:1px solid rgba(56,189,248,0.25);border-radius:4px;font-weight:600;" title="Change display name (Once per 60 days)">
              ✎ Edit Name
            </button>
          </div>
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;margin-top:10px;padding-top:10px;border-top:1px solid rgba(255,255,255,0.06);font-size:11px;">
          <span style="display:flex;align-items:center;gap:6px;color:#94a3b8;">Country: <strong style="color:#f1f5f9;">${this.escapeHTML(this.getCountryDisplay(String(this.currentProfile?.country_code || "")))}</strong></span>
          <button id="change-profile-country" style="background:none;border:none;color:#38bdf8;font-size:11px;font-weight:600;cursor:pointer;text-decoration:underline;">Change</button>
        </div>
      </div>

      <!-- Wallet Currency Switcher -->
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;background:#0c121c;border:1px solid #1e293b;border-radius:10px;">
        <span style="font-size:11px;font-weight:700;color:#94a3b8;display:flex;align-items:center;gap:5px;">🌐 Currency Mode</span>
        <div style="display:flex;gap:6px;">
          <button type="button" class="btn-wallet-curr" style="background:#172132;color:#38bdf8;border:1px solid #233044;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;">${this.escapeHTML(currency)} ▾</button>
          <button type="button" id="choose-funding-wallet" style="background:#172132;color:#94a3b8;border:1px solid #233044;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;">Wallets</button>
        </div>
      </div>

      <!-- Live and Demo Account Switcher Cards -->
      <button id="select-live-account" class="account-mode-action live ${isLive ? 'active' : ''}" style="${isLive ? 'border-color:#10b981;box-shadow:0 0 12px rgba(16,185,129,0.2);' : ''}">
        <div class="account-command-row">
          <div style="display:flex;align-items:center;gap:6px;">
            <span style="font-size:14px;">🟢</span>
            <span style="font-size:12px;font-weight:700;color:#f8fafc;">Live Account</span>
          </div>
          <strong style="font-size:14px;font-weight:800;color:#10b981;">${currSym}${this.escapeHTML(liveBalance)}</strong>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:4px;">
          <small class="kyc-status ${kycVerified ? "verified" : "required"}">${kycVerified ? "✓ Verified · Cashier Active" : "⚠️ KYC required for withdrawals"}</small>
          <span style="font-size:10px;color:#34d399;font-weight:700;">${isLive ? 'ACTIVE' : 'Real Money'}</span>
        </div>
      </button>

      <button id="select-demo-account" class="account-mode-action demo ${!isLive ? 'active' : ''}" style="${!isLive ? 'border-color:#38bdf8;box-shadow:0 0 12px rgba(56,189,248,0.2);' : ''}">
        <div class="account-command-row">
          <div style="display:flex;align-items:center;gap:6px;">
            <span style="font-size:14px;">🔷</span>
            <span style="font-size:12px;font-weight:700;color:#f8fafc;">Demo Account</span>
          </div>
          <strong style="font-size:14px;font-weight:800;color:#38bdf8;">${currSym}${this.escapeHTML(demoBalance)}</strong>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:4px;">
          <small style="color:#64748b;">Practice balance · Risk-free</small>
          <span style="font-size:10px;color:#38bdf8;font-weight:700;">${!isLive ? 'ACTIVE' : 'Virtual'}</span>
        </div>
      </button>

      <!-- Quick Action Buttons -->
      <div class="account-command-actions">
        <button data-account-action="DEPOSIT">
          <span style="font-size:14px;">⚡</span>
          <span>Deposit</span>
        </button>
        <button data-account-action="WITHDRAWAL">
          <span style="font-size:14px;">💳</span>
          <span>Withdraw</span>
        </button>
        <button data-account-action="BONUSES">
          <span style="font-size:14px;">🎁</span>
          <span>Bonuses</span>
        </button>
        <button data-account-action="PAYMENTS">
          <span style="font-size:14px;">📜</span>
          <span>History</span>
        </button>
        <button data-account-action="TRADES">
          <span style="font-size:14px;">📊</span>
          <span>Trades</span>
        </button>
        <button data-account-action="PROFILE">
          <span style="font-size:14px;">👤</span>
          <span>Account</span>
        </button>
        <button data-account-action="SIGNOUT">
          <span style="font-size:14px;">🚪</span>
          <span>Sign Out</span>
        </button>
      </div>
    `;

    menu.classList.add("open");
    menu.querySelector("#close-account-menu")?.addEventListener("click", () => menu.classList.remove("open"));
    menu.querySelectorAll(".btn-wallet-curr").forEach(btn => btn.addEventListener("click", () => {
      void chooseDisplayCurrency(this.restClient, this.appState.getBaseWalletCurrency(), this.appState.getBaseWalletBalance(), (chosenCurrency) => {
        if (chosenCurrency) {
          this.appState.setSelectedCurrency(chosenCurrency);
        }
        this.renderHeader();
        this.renderQuickStakeChips();
        this.updateCalculations();
        menu.classList.remove("open");
        void this.showTradingAccountMenu();
      });
    }));
    menu.querySelector('#choose-funding-wallet')?.addEventListener('click', () => {
      const userCurr = this.appState.getSelectedCurrency();
      const prefCurr = (this.currentProfile as any)?.preferred_currency || '';
      void chooseFundingWallet(this.restClient, currency => {
        this.orderStore.resetQuote();this.appState.setSelectedCurrency(currency);
        this.renderHeader();this.updateCalculations();menu.classList.remove('open');
      }, [userCurr, prefCurr]);
    });
    menu.querySelector("#select-live-account")?.addEventListener("click", () => {
      if (!this.restClient.getToken()) {
        menu.classList.remove("open");
        this.showAuthModal("login");
        return;
      }
      this.appState.setMode("LIVE");
      this.orderStore.reset();
      void this.refreshPendingOrders();
      void this.reconcileActiveContracts();
      menu.classList.remove("open");
      this.renderHeader();
      this.playChime(880, "sine", 0.2);
    });
    menu.querySelector("#select-demo-account")?.addEventListener("click", () => {
      this.appState.setMode("DEMO");
      this.orderStore.reset();
      this.reconcileDemoContracts();
      void this.refreshPendingOrders();
      menu.classList.remove("open");
      this.renderHeader();
      this.playChime(440, "sine", 0.2);
    });
    menu.querySelector("#change-profile-country")?.addEventListener("click", () => void this.changeProfileCountry());
    menu.querySelector("#btn-change-display-name")?.addEventListener("click", () => {
      menu.classList.remove("open");
      void this.showNameChangeModal();
    });
    menu.querySelectorAll<HTMLElement>("[data-account-action]").forEach((button) => button.addEventListener("click", () => {
      const action = button.dataset.accountAction;
      menu.classList.remove("open");
      if (action === "DEPOSIT") void this.showCashierModal("DEPOSIT");
      else if (action === "WITHDRAWAL") void this.showCashierModal("WITHDRAWAL");
      else if (action === "BONUSES") void this.showBonusCenter();
      else if (action === "PAYMENTS") void this.showNotifications();
      else if (action === "TRADES") document.getElementById("tab-btn-trades")?.click();
      else if (action === "PROFILE") document.getElementById("compliance-nav-btn")?.click();
      else if (action === "SIGNOUT") {
        try {
          localStorage.removeItem("starx_auth_token");
          localStorage.removeItem("starx_session_token");
          localStorage.removeItem("starx_user_profile");
          sessionStorage.clear();
          if (typeof BroadcastChannel !== "undefined") {
            const bc = new BroadcastChannel("starx_auth_sync");
            bc.postMessage({ type: "SIGNOUT" });
            bc.close();
          }
        } catch {}
        this.restClient.setToken("");
        this.isAuthenticated = false;
        this.currentProfile = null;
        this.appState.setMode("DEMO");
        this.renderHeader();
        this.showDashboardNotice("Signed out. Switched to Guest Demo Mode.", "info");
      }
    }));
  }

  private async showNameChangeModal() {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;

    // Refresh profile to have the latest cooldown state
    try {
      this.currentProfile = await this.restClient.getProfile();
    } catch (e) {
      console.warn("Could not refresh profile before name change:", e);
    }

    const currentName = this.currentProfile?.public_alias || this.currentProfile?.full_name || "";
    const canChange = this.currentProfile?.can_change_name !== false && (this.currentProfile?.days_until_name_change || 0) <= 0;
    const daysLeft = this.currentProfile?.days_until_name_change || 0;

    modalContainer.innerHTML = `
      <div id="name-change-modal" class="name-change-modal" style="background:#121824;border:1px solid #1e293b;border-radius:12px;padding:24px;max-width:460px;width:100%;color:#f8fafc;font-family:'Inter',sans-serif;box-shadow:0 20px 25px -5px rgba(0,0,0,0.5);">
        <div style="border-bottom:1px solid #1e293b;padding-bottom:12px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:17px;color:#ffffff;display:flex;align-items:center;gap:8px;">
            <span>✎</span> Change Display Name
          </h3>
          <button id="close-name-modal-x" style="background:none;border:none;color:#94a3b8;font-size:20px;cursor:pointer;line-height:1;">&times;</button>
        </div>

        <!-- 60-Day Policy Note -->
        <div style="background:rgba(234, 179, 8, 0.1);border:1px solid rgba(234, 179, 8, 0.25);color:#fde047;padding:10px 12px;border-radius:8px;font-size:12px;line-height:1.4;margin-bottom:16px;">
          <div style="font-weight:700;display:flex;align-items:center;gap:6px;margin-bottom:3px;">
            <span>ℹ️</span> 60-Day Policy
          </div>
          <div>Display names can only be changed once every 60 days and must be globally unique across all accounts.</div>
        </div>

        <div style="margin-bottom:14px;">
          <label style="display:block;font-size:12px;color:#94a3b8;margin-bottom:6px;">Current Display Name</label>
          <div style="background:#0b0e14;border:1px solid #1e293b;padding:8px 12px;border-radius:6px;color:#cbd5e1;font-size:13px;font-weight:600;">${this.escapeHTML(currentName)}</div>
        </div>

        <div style="margin-bottom:16px;">
          <label for="input-new-display-name" style="display:block;font-size:12px;color:#94a3b8;margin-bottom:6px;">New Display Name (3–32 characters)</label>
          <input type="text" id="input-new-display-name" maxlength="32" placeholder="Enter new unique display name" style="background:#0b0e14;border:1px solid #334155;color:#ffffff;padding:10px 12px;border-radius:6px;width:100%;box-sizing:border-box;font-size:14px;" />
        </div>

        <div id="name-change-error" style="display:none;padding:10px 12px;border-radius:6px;font-size:12px;line-height:1.4;margin-bottom:16px;"></div>

        <div style="display:flex;justify-content:flex-end;gap:10px;">
          <button id="close-name-modal-btn" style="background:#1e293b;border:1px solid #334155;color:#94a3b8;padding:8px 16px;border-radius:6px;font-size:13px;cursor:pointer;">
            Cancel
          </button>
          <button id="btn-submit-new-name" style="background:#0284c7;border:none;color:#ffffff;padding:8px 18px;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;">
            Save Name
          </button>
        </div>
      </div>
    `;
    modalContainer.style.display = "flex";

    const closeModal = () => {
      modalContainer.style.display = "none";
    };

    modalContainer.querySelector("#close-name-modal-x")?.addEventListener("click", closeModal);
    modalContainer.querySelector("#close-name-modal-btn")?.addEventListener("click", closeModal);

    const submitBtn = modalContainer.querySelector("#btn-submit-new-name") as HTMLButtonElement | null;
    const inputEl = modalContainer.querySelector("#input-new-display-name") as HTMLInputElement | null;
    const errorEl = modalContainer.querySelector("#name-change-error") as HTMLElement | null;

    if (submitBtn && inputEl) {
      inputEl.focus();
      inputEl.addEventListener("keydown", (e) => {
        if (e.key === "Enter") submitBtn.click();
      });

      submitBtn.addEventListener("click", async () => {
        const newName = inputEl.value.trim();
        if (errorEl) {
          errorEl.style.display = "none";
          errorEl.textContent = "";
        }

        if (!newName) {
          if (errorEl) {
            errorEl.style.display = "block";
            errorEl.style.background = "rgba(239, 68, 68, 0.15)";
            errorEl.style.border = "1px solid rgba(239, 68, 68, 0.4)";
            errorEl.style.color = "#fca5a5";
            errorEl.innerHTML = "❌ Please enter a new display name.";
          }
          return;
        }

        if (newName === currentName) {
          if (errorEl) {
            errorEl.style.display = "block";
            errorEl.style.background = "rgba(234, 179, 8, 0.15)";
            errorEl.style.border = "1px solid rgba(234, 179, 8, 0.4)";
            errorEl.style.color = "#fde047";
            errorEl.innerHTML = "⚠️ New display name must be different from your current name.";
          }
          return;
        }

        if (newName.length < 3 || newName.length > 32) {
          if (errorEl) {
            errorEl.style.display = "block";
            errorEl.style.background = "rgba(239, 68, 68, 0.15)";
            errorEl.style.border = "1px solid rgba(239, 68, 68, 0.4)";
            errorEl.style.color = "#fca5a5";
            errorEl.innerHTML = "❌ Display name must be between 3 and 32 characters.";
          }
          return;
        }

        // Check if user is currently on 60-day cooldown
        if (!canChange || daysLeft > 0) {
          if (errorEl) {
            errorEl.style.display = "block";
            errorEl.style.background = "rgba(239, 68, 68, 0.15)";
            errorEl.style.border = "1px solid rgba(239, 68, 68, 0.4)";
            errorEl.style.color = "#fca5a5";
            errorEl.innerHTML = `⚠️ <strong>You cannot change name within 60 days.</strong> Next name change available in <strong>${daysLeft || 60} days</strong>.`;
          }
          return;
        }

        submitBtn.setAttribute("disabled", "true");
        submitBtn.textContent = "Saving…";

        try {
          const res = await this.restClient.updateAccountName(newName);
          if (this.currentProfile) {
            this.currentProfile.full_name = res.full_name;
            this.currentProfile.public_alias = res.public_alias;
            this.currentProfile.can_change_name = false;
            this.currentProfile.days_until_name_change = res.days_until_name_change || 60;
          }
          this.showDashboardNotice(`Display name changed successfully to "${res.public_alias}". 60-day lock activated.`, "success");
          closeModal();
          this.renderHeader();
          const nameValEl = document.getElementById("account-display-name-val");
          if (nameValEl) nameValEl.textContent = res.public_alias;
        } catch (err: any) {
          submitBtn.removeAttribute("disabled");
          submitBtn.textContent = "Save Name";
          if (errorEl) {
            errorEl.style.display = "block";
            errorEl.style.background = "rgba(239, 68, 68, 0.15)";
            errorEl.style.border = "1px solid rgba(239, 68, 68, 0.4)";
            errorEl.style.color = "#fca5a5";
            const errStr = String(err.message || "").toLowerCase();
            const errCode = String(err.code || "").toUpperCase();
            if (errCode === "NAME_ALREADY_TAKEN" || err.status === 409 || errStr.includes("taken") || errStr.includes("already")) {
              errorEl.innerHTML = "❌ <strong>Name already taken:</strong> Two users cannot have the same name. Please choose a different unique name.";
            } else if (errCode === "NAME_CHANGE_COOLDOWN_ACTIVE" || err.status === 429 || errStr.includes("60 days") || errStr.includes("cooldown")) {
              errorEl.innerHTML = `⚠️ <strong>You cannot change name within 60 days.</strong> Next name change available in <strong>${daysLeft || 60} days</strong>.`;
            } else {
              errorEl.textContent = `❌ ${err.message || "Failed to update display name"}`;
            }
          }
        }
      });
    }
  }

  private async startGmailAccountConnect() {
    this.showAuthModal("register");
  }

  private openServiceAgreementModal() {
    const existingAgreement = document.getElementById("starx-service-agreement-popup");
    if (existingAgreement) existingAgreement.remove();

    const agreementEl = document.createElement("div");
    agreementEl.id = "starx-service-agreement-popup";
    agreementEl.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.85);backdrop-filter:blur(8px);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;";
    agreementEl.innerHTML = `
      <div style="background:#0f172a;border:1px solid #334155;border-radius:16px;max-width:560px;width:100%;max-height:85vh;display:flex;flex-direction:column;box-shadow:0 25px 50px -12px rgba(0,0,0,0.8);color:#f8fafc;font-family:'Inter',system-ui,sans-serif;">
        <div style="padding:20px 24px;border-bottom:1px solid #1e293b;display:flex;align-items:center;justify-content:space-between;">
          <div style="display:flex;align-items:center;gap:10px;">
            <span style="font-size:20px;">📜</span>
            <strong style="font-size:16px;color:#ffffff;">StarX Options — Service Agreement</strong>
          </div>
          <button id="close-service-agreement" style="background:none;border:none;color:#94a3b8;font-size:22px;cursor:pointer;line-height:1;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:50%;">×</button>
        </div>
        <div style="padding:20px 24px;overflow-y:auto;font-size:13px;line-height:1.6;color:#cbd5e1;display:flex;flex-direction:column;gap:14px;">
          <div>
            <h4 style="color:#38bdf8;font-size:14px;margin:0 0 4px 0;">1. General Provisions & Eligibility</h4>
            <p style="margin:0;">By registering an account with StarX Options, you confirm and warrant that you are at least 18 years of age (or the legal age of majority in your jurisdiction) and possess full legal capacity to enter into binding agreements.</p>
          </div>
          <div>
            <h4 style="color:#38bdf8;font-size:14px;margin:0 0 4px 0;">2. High-Risk Financial Disclaimer</h4>
            <p style="margin:0;">Fixed-time binary options trading involves substantial risk of loss and is not suitable for all investors. You may lose some or all of your invested capital. You should never trade with funds you cannot afford to lose.</p>
          </div>
          <div>
            <h4 style="color:#38bdf8;font-size:14px;margin:0 0 4px 0;">3. Non-US Resident & Regulatory Compliance</h4>
            <p style="margin:0;">StarX Options strictly does not offer or solicit services to citizens or residents of the United States of America for tax or jurisdictional compliance purposes under CFTC regulations. By proceeding, you irrevocably declare that you are not a US person.</p>
          </div>
          <div>
            <h4 style="color:#38bdf8;font-size:14px;margin:0 0 4px 0;">4. Account Security & Verification</h4>
            <p style="margin:0;">Clients are responsible for maintaining the confidentiality of their credentials. Withdrawal transactions may require email verification and compliance checks to maintain strict Anti-Money Laundering (AML) standards.</p>
          </div>
          <div>
            <h4 style="color:#38bdf8;font-size:14px;margin:0 0 4px 0;">5. Cashier & Payout Settlements</h4>
            <p style="margin:0;">Digital asset (USDT, BTC, ETH) and local peer-to-peer settlements are executed according to platform network rules. Payouts and contract outcomes are settled cryptographically based on authoritative tick stream feeds.</p>
          </div>
        </div>
        <div style="padding:16px 24px;border-top:1px solid #1e293b;display:flex;align-items:center;justify-content:space-between;">
          <a href="/documents/en/Service_Agreement.pdf?v=20260920-v6-full" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:6px;color:#38bdf8;font-size:12px;text-decoration:none;font-weight:600;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            <span>View Full Official PDF (A4) ↗</span>
          </a>
          <button id="btn-accept-agreement" style="background:linear-gradient(135deg, #10b981 0%, #059669 100%);color:#ffffff;border:none;border-radius:8px;padding:10px 20px;font-size:13px;font-weight:700;cursor:pointer;">I Accept & Agree</button>
        </div>
      </div>
    `;

    document.body.appendChild(agreementEl);

    const close = () => {
      agreementEl.remove();
    };

    agreementEl.querySelector("#close-service-agreement")?.addEventListener("click", close);
    agreementEl.querySelector("#btn-accept-agreement")?.addEventListener("click", () => {
      const termsCheck = document.getElementById("reg-terms-check") as HTMLInputElement | null;
      if (termsCheck) termsCheck.checked = true;
      close();
    });
    agreementEl.addEventListener("click", (e) => {
      if (e.target === agreementEl) close();
    });
  }

  private async showAuthModal(initialTab: "register" | "login" | "forgot" | "reset" = "register", customNote?: string, resetToken?: string) {
    const modalOverlay = document.getElementById("modal-overlay");
    if (!modalOverlay) return;

    let activeTab: "register" | "login" | "forgot" | "reset" = initialTab;
    let activeResetToken: string = resetToken || "";

    modalOverlay.innerHTML = `
      <div id="auth-modal-card" style="background:#0f172a;border:1px solid #1e293b;border-radius:18px;max-width:460px;width:94%;padding:28px 24px;box-shadow:0 25px 60px -15px rgba(0,0,0,0.85);position:relative;color:#f8fafc;font-family:'Inter',system-ui,sans-serif;box-sizing:border-box;">
        <button id="close-auth-modal" style="position:absolute;top:16px;right:16px;background:none;border:none;color:#94a3b8;font-size:22px;cursor:pointer;line-height:1;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:50%;transition:background 0.2s;">×</button>
        
        <div style="text-align:center;margin-bottom:18px;">
          <div style="display:inline-flex;align-items:center;gap:8px;margin-bottom:8px;">
            <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg, #10b981 0%, #2563eb 100%);display:flex;align-items:center;justify-content:center;font-weight:900;color:#fff;font-size:18px;box-shadow:0 0 16px rgba(16,185,129,0.35);">✦</div>
            <strong style="font-size:20px;letter-spacing:-0.02em;color:#ffffff;">StarX Options</strong>
          </div>
          <p id="auth-modal-subtitle" style="font-size:12px;color:#94a3b8;margin:0;line-height:1.4;">${this.escapeHTML(customNote || (activeTab === "forgot" ? "Account Password Recovery" : (activeTab === "reset" ? "Set a new secure password" : "Institutional Fixed-Time Trading Terminal")))}</p>
        </div>

        <!-- Tab Switcher -->
        <div id="auth-tab-bar" style="display:${(activeTab === 'forgot' || activeTab === 'reset') ? 'none' : 'flex'};background:#090d16;padding:4px;border-radius:10px;border:1px solid #1e293b;margin-bottom:18px;">
          <button id="tab-btn-register" type="button" style="flex:1;padding:8px 12px;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.2s;background:${activeTab === 'register' ? '#10b981' : 'transparent'};color:${activeTab === 'register' ? '#ffffff' : '#94a3b8'};">
            Registration
          </button>
          <button id="tab-btn-login" type="button" style="flex:1;padding:8px 12px;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.2s;background:${activeTab === 'login' ? '#2563eb' : 'transparent'};color:${activeTab === 'login' ? '#ffffff' : '#94a3b8'};">
            Log In
          </button>
        </div>

        <!-- Registration Pane -->
        <div id="pane-registration" style="display:${activeTab === 'register' ? 'block' : 'none'};">
          <form id="form-registration" style="display:flex;flex-direction:column;gap:12px;">
            <div style="display:grid;grid-template-columns:1.2fr 0.8fr;gap:10px;">
              <div>
                <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.03em;">Country</label>
                <select id="reg-country-select" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:10px 10px;color:#ffffff;font-size:13px;outline:none;cursor:pointer;">
                  <option value="" disabled selected>Detecting country...</option>
                </select>
              </div>
              <div>
                <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.03em;">Currency</label>
                <select id="reg-currency-select" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:10px 10px;color:#ffffff;font-size:13px;outline:none;cursor:pointer;">
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="INR">INR (₹)</option>
                  <option value="BRL">BRL (R$)</option>
                  <option value="IDR">IDR (Rp)</option>
                  <option value="VND">VND (₫)</option>
                  <option value="NGN">NGN (₦)</option>
                  <option value="EGP">EGP (E£)</option>
                  <option value="TRY">TRY (₺)</option>
                  <option value="AED">AED (AED)</option>
                  <option value="MYR">MYR (RM)</option>
                  <option value="THB">THB (฿)</option>
                  <option value="MXN">MXN ($)</option>
                  <option value="COP">COP ($)</option>
                  <option value="ZAR">ZAR (R)</option>
                  <option value="CHF">CHF (CHF)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>

            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.03em;">Email</label>
              <input type="email" id="reg-email-input" required placeholder="Enter email address" autocomplete="email" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:11px 12px;color:#ffffff;font-size:13px;outline:none;" />
            </div>

            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.03em;">Password</label>
              <div style="position:relative;width:100%;">
                <input type="password" id="reg-password-input" required minlength="6" placeholder="At least 6 characters" autocomplete="new-password" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:11px 40px 11px 12px;color:#ffffff;font-size:13px;outline:none;" />
                <button type="button" id="reg-toggle-pwd" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;color:#94a3b8;font-size:14px;cursor:pointer;padding:4px;" title="Show/Hide Password">👁️</button>
              </div>
            </div>

            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.03em;">Promo Code (Optional)</label>
              <input type="text" id="reg-promo-input" placeholder="e.g. STARX50" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:11px 12px;color:#ffffff;font-size:13px;outline:none;" />
            </div>

            <!-- Checkbox 1: 18+ and Service Agreement -->
            <label style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:#cbd5e1;cursor:pointer;margin-top:2px;user-select:none;">
              <input type="checkbox" id="reg-terms-check" checked style="margin-top:2px;accent-color:#10b981;width:16px;height:16px;cursor:pointer;flex-shrink:0;" />
              <span>I confirm that I am 18 years old or above and accept the <a href="#" id="link-service-agreement" style="color:#38bdf8;text-decoration:underline;font-weight:600;">Service Agreement</a></span>
            </label>

            <!-- Checkbox 2: US Non-Resident Confirmation -->
            <label style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:#cbd5e1;cursor:pointer;user-select:none;">
              <input type="checkbox" id="reg-us-check" checked style="margin-top:2px;accent-color:#10b981;width:16px;height:16px;cursor:pointer;flex-shrink:0;" />
              <span>I declare and confirm that I am not a citizen or resident of the US for tax purpose.</span>
            </label>

            <div id="reg-error-box" style="display:none;color:#f43f5e;font-size:12px;padding:8px 10px;background:rgba(244,63,94,0.1);border-radius:6px;border:1px solid rgba(244,63,94,0.3);line-height:1.4;"></div>

            <button type="submit" id="btn-submit-reg" style="margin-top:4px;width:100%;background:linear-gradient(135deg, #10b981 0%, #059669 100%);color:#ffffff;font-size:14px;font-weight:700;padding:13px 16px;border:none;border-radius:8px;cursor:pointer;box-shadow:0 4px 14px rgba(16,185,129,0.35);transition:all 0.15s ease;">
              <span>Registration</span>
            </button>

            <div style="display:flex;align-items:center;margin:12px 0 6px;gap:10px;">
              <div style="flex:1;height:1px;background:#1e293b;"></div>
              <span style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.04em;">or sign up with</span>
              <div style="flex:1;height:1px;background:#1e293b;"></div>
            </div>

            <button type="button" class="btn-google-auth" style="width:100%;display:flex;align-items:center;justify-content:center;gap:10px;background:#ffffff;color:#1e293b;border:1px solid #cbd5e1;border-radius:8px;padding:11px 16px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,0.15);transition:all 0.15s ease;">
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </form>

          <div style="margin-top:14px;text-align:center;font-size:12px;color:#94a3b8;">
            Already have an account? <span id="link-switch-to-login" style="color:#38bdf8;cursor:pointer;font-weight:700;text-decoration:underline;">Log In</span>
          </div>
        </div>

        <!-- Log In Pane -->
        <div id="pane-login" style="display:${activeTab === 'login' ? 'block' : 'none'};">
          <form id="form-login" style="display:flex;flex-direction:column;gap:14px;">
            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.03em;">Email Address</label>
              <input type="email" id="login-email-input" required placeholder="Enter your email" autocomplete="email" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:12px 14px;color:#ffffff;font-size:14px;outline:none;" />
            </div>

            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.03em;">Password</label>
              <div style="position:relative;width:100%;">
                <input type="password" id="login-password-input" required placeholder="Enter your password" autocomplete="current-password" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:12px 40px 12px 14px;color:#ffffff;font-size:14px;outline:none;" />
                <button type="button" id="login-toggle-pwd" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;color:#94a3b8;font-size:15px;cursor:pointer;padding:4px;" title="Show/Hide Password">👁️</button>
              </div>
            </div>

            <!-- Save Password & Forgot Password Row -->
            <div style="display:flex;justify-content:space-between;align-items:center;margin-top:-2px;font-size:12px;">
              <label style="display:flex;align-items:center;gap:8px;color:#cbd5e1;cursor:pointer;user-select:none;">
                <input type="checkbox" id="login-remember-check" checked style="accent-color:#2563eb;width:15px;height:15px;cursor:pointer;" />
                <span style="font-weight:500;">Save password</span>
              </label>
              <button type="button" id="link-switch-to-forgot" style="background:none;border:none;padding:0;color:#38bdf8;cursor:pointer;font-size:12px;font-weight:600;text-decoration:none;transition:color 0.15s ease;">Forgot password?</button>
            </div>

            <div id="login-error-box" style="display:none;color:#f43f5e;font-size:12px;padding:8px 10px;background:rgba(244,63,94,0.1);border-radius:6px;border:1px solid rgba(244,63,94,0.3);line-height:1.4;"></div>

            <button type="submit" id="btn-submit-login" style="margin-top:4px;width:100%;background:linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);color:#ffffff;font-size:14px;font-weight:700;padding:13px 16px;border:none;border-radius:8px;cursor:pointer;box-shadow:0 4px 14px rgba(37,99,235,0.35);transition:all 0.15s ease;">
              <span>Log In</span>
            </button>

            <div style="display:flex;align-items:center;margin:12px 0 6px;gap:10px;">
              <div style="flex:1;height:1px;background:#1e293b;"></div>
              <span style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.04em;">or sign in with</span>
              <div style="flex:1;height:1px;background:#1e293b;"></div>
            </div>

            <button type="button" class="btn-google-auth" style="width:100%;display:flex;align-items:center;justify-content:center;gap:10px;background:#ffffff;color:#1e293b;border:1px solid #cbd5e1;border-radius:8px;padding:11px 16px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,0.15);transition:all 0.15s ease;">
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </form>

          <div style="margin-top:16px;text-align:center;font-size:12px;color:#94a3b8;">
            Don't have an account? <span id="link-switch-to-reg" style="color:#10b981;cursor:pointer;font-weight:700;text-decoration:underline;">Registration</span>
          </div>
        </div>

        <!-- Two-Factor Authentication Challenge Pane -->
        <div id="pane-2fa" style="display:none;">
          <div style="text-align:center;margin-bottom:16px;">
            <div style="width:48px;height:48px;border-radius:50%;background:rgba(56,189,248,0.15);border:1px solid rgba(56,189,248,0.3);display:inline-flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:8px;">🔐</div>
            <h4 style="margin:0;font-size:16px;font-weight:700;color:#ffffff;">Two-Factor Verification</h4>
            <p id="label-2fa-description" style="margin:6px 0 0;font-size:12px;color:#94a3b8;line-height:1.4;">
              Enter the 6-digit code to complete sign-in.
            </p>
          </div>

          <form id="form-2fa" style="display:flex;flex-direction:column;gap:14px;">
            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:8px;text-align:center;text-transform:uppercase;letter-spacing:0.04em;">6-Digit Security Code</label>
              <input type="text" id="input-2fa-code" maxlength="6" inputmode="numeric" pattern="[0-9]*" placeholder="······" autocomplete="one-time-code" required style="width:100%;box-sizing:border-box;background:#0f172a;border:2px solid #0284c7;border-radius:10px;padding:12px 14px;color:#38bdf8;font-size:24px;font-family:monospace;letter-spacing:10px;text-align:center;outline:none;font-weight:700;" />
            </div>

            <div id="box-2fa-error" style="display:none;color:#f43f5e;font-size:12px;padding:8px 10px;background:rgba(244,63,94,0.1);border-radius:6px;border:1px solid rgba(244,63,94,0.3);line-height:1.4;text-align:center;"></div>

            <button type="submit" id="btn-submit-2fa" style="width:100%;background:linear-gradient(135deg, #0284c7 0%, #2563eb 100%);color:#ffffff;font-size:14px;font-weight:700;padding:13px 16px;border:none;border-radius:8px;cursor:pointer;box-shadow:0 4px 14px rgba(2,132,199,0.35);transition:all 0.15s ease;">
              <span>Verify & Sign In ➔</span>
            </button>

            <div style="display:flex;justify-content:space-between;align-items:center;font-size:12px;margin-top:2px;">
              <button type="button" id="btn-resend-2fa" style="background:none;border:none;color:#38bdf8;cursor:pointer;font-weight:600;padding:0;text-decoration:underline;">Resend Code</button>
              <button type="button" id="btn-back-from-2fa" style="background:none;border:none;color:#94a3b8;cursor:pointer;padding:0;">← Back to Login</button>
            </div>
          </form>
        </div>

        <!-- Forgot Password Pane -->
        <div id="pane-forgot" style="display:${activeTab === 'forgot' ? 'block' : 'none'};">
          <div style="text-align:center;margin-bottom:14px;">
            <h3 style="margin:0 0 6px 0;font-size:16px;font-weight:700;color:#f8fafc;">Recover Your Password</h3>
            <p style="margin:0;font-size:12px;color:#94a3b8;line-height:1.5;">Enter the email address registered with your account. We will send you a secure link to reset your password.</p>
          </div>

          <form id="form-forgot" style="display:flex;flex-direction:column;gap:14px;">
            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.03em;">Registered Email</label>
              <input type="email" id="forgot-email-input" required placeholder="Enter your registered email" autocomplete="email" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:12px 14px;color:#ffffff;font-size:14px;outline:none;" />
            </div>

            <div id="forgot-error-box" style="display:none;color:#f43f5e;font-size:12px;padding:10px 12px;background:rgba(244,63,94,0.1);border-radius:6px;border:1px solid rgba(244,63,94,0.3);line-height:1.4;"></div>

            <button type="submit" id="btn-submit-forgot" style="margin-top:4px;width:100%;background:linear-gradient(135deg, #0284c7 0%, #2563eb 100%);color:#ffffff;font-size:14px;font-weight:700;padding:13px 16px;border:none;border-radius:8px;cursor:pointer;box-shadow:0 4px 14px rgba(37,99,235,0.35);transition:all 0.15s ease;">
              <span>Send Reset Link</span>
            </button>
          </form>

          <div style="margin-top:16px;text-align:center;font-size:12px;color:#94a3b8;">
            Remembered your password? <span id="link-forgot-to-login" style="color:#38bdf8;cursor:pointer;font-weight:700;text-decoration:underline;">Log In</span>
          </div>
        </div>

        <!-- Reset Password Pane -->
        <div id="pane-reset" style="display:${activeTab === 'reset' ? 'block' : 'none'};">
          <div style="text-align:center;margin-bottom:14px;">
            <h3 style="margin:0 0 6px 0;font-size:16px;font-weight:700;color:#f8fafc;">Set New Password</h3>
            <p style="margin:0;font-size:12px;color:#94a3b8;line-height:1.5;">Create a new secure password for your account.</p>
          </div>

          <form id="form-reset" style="display:flex;flex-direction:column;gap:14px;">
            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.03em;">New Password</label>
              <div style="position:relative;width:100%;">
                <input type="password" id="reset-password-input" required minlength="6" placeholder="At least 6 characters" autocomplete="new-password" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:12px 40px 12px 14px;color:#ffffff;font-size:14px;outline:none;" />
                <button type="button" id="reset-toggle-pwd" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;color:#94a3b8;font-size:15px;cursor:pointer;padding:4px;" title="Show/Hide Password">👁️</button>
              </div>
            </div>

            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:#94a3b8;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.03em;">Confirm New Password</label>
              <div style="position:relative;width:100%;">
                <input type="password" id="reset-password-confirm" required minlength="6" placeholder="Re-enter new password" autocomplete="new-password" style="width:100%;box-sizing:border-box;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:12px 40px 12px 14px;color:#ffffff;font-size:14px;outline:none;" />
                <button type="button" id="reset-toggle-pwd-confirm" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;color:#94a3b8;font-size:15px;cursor:pointer;padding:4px;" title="Show/Hide Password">👁️</button>
              </div>
            </div>

            <div id="reset-error-box" style="display:none;color:#f43f5e;font-size:12px;padding:10px 12px;background:rgba(244,63,94,0.1);border-radius:6px;border:1px solid rgba(244,63,94,0.3);line-height:1.4;"></div>

            <button type="submit" id="btn-submit-reset" style="margin-top:4px;width:100%;background:linear-gradient(135deg, #10b981 0%, #059669 100%);color:#ffffff;font-size:14px;font-weight:700;padding:13px 16px;border:none;border-radius:8px;cursor:pointer;box-shadow:0 4px 14px rgba(16,185,129,0.35);transition:all 0.15s ease;">
              <span>Update Password</span>
            </button>
          </form>

          <div style="margin-top:16px;text-align:center;font-size:12px;color:#94a3b8;">
            Back to <span id="link-reset-to-login" style="color:#38bdf8;cursor:pointer;font-weight:700;text-decoration:underline;">Log In</span>
          </div>
        </div>

        <div style="margin-top:16px;text-align:center;font-size:11px;color:#64748b;">
          🔒 Secure 256-bit TLS · Real-time cryptographic ledger
        </div>
      </div>
    `;

    // Elements & State
    const closeBtn = modalOverlay.querySelector("#close-auth-modal") as HTMLButtonElement | null;
    const tabBtnReg = modalOverlay.querySelector("#tab-btn-register") as HTMLButtonElement | null;
    const tabBtnLogin = modalOverlay.querySelector("#tab-btn-login") as HTMLButtonElement | null;
    const tabBar = modalOverlay.querySelector("#auth-tab-bar") as HTMLElement | null;
    const subTitle = modalOverlay.querySelector("#auth-modal-subtitle") as HTMLElement | null;
    const paneReg = modalOverlay.querySelector("#pane-registration") as HTMLElement | null;
    const paneLogin = modalOverlay.querySelector("#pane-login") as HTMLElement | null;
    const paneForgot = modalOverlay.querySelector("#pane-forgot") as HTMLElement | null;
    const paneReset = modalOverlay.querySelector("#pane-reset") as HTMLElement | null;
    const pane2FA = modalOverlay.querySelector("#pane-2fa") as HTMLElement | null;
    let current2FAChallengeToken = "";
    let current2FAMethod = "EMAIL";
    let current2FAEmail = "";

    const applySavedCredentials = () => {
      const savedEmail = localStorage.getItem("starx_saved_email") || "";
      const savedPwd = localStorage.getItem("starx_saved_pwd") || "";
      const rememberPref = localStorage.getItem("starx_remember_me");
      const rememberCheck = modalOverlay.querySelector("#login-remember-check") as HTMLInputElement | null;
      const loginEmail = modalOverlay.querySelector("#login-email-input") as HTMLInputElement | null;
      const loginPwd = modalOverlay.querySelector("#login-password-input") as HTMLInputElement | null;

      if (rememberCheck) {
        rememberCheck.checked = rememberPref !== "false";
      }
      if (loginEmail && savedEmail && !loginEmail.value) {
        loginEmail.value = savedEmail;
      }
      if (loginPwd && savedPwd && !loginPwd.value && rememberCheck?.checked) {
        loginPwd.value = savedPwd;
      }
    };

    const switchTabs = (tab: "register" | "login" | "forgot" | "reset") => {
      activeTab = tab;
      if (paneReg) paneReg.style.display = tab === "register" ? "block" : "none";
      if (paneLogin) paneLogin.style.display = tab === "login" ? "block" : "none";
      if (paneForgot) paneForgot.style.display = tab === "forgot" ? "block" : "none";
      if (paneReset) paneReset.style.display = tab === "reset" ? "block" : "none";
      if (pane2FA) pane2FA.style.display = "none";

      if (tabBar) {
        tabBar.style.display = (tab === "forgot" || tab === "reset") ? "none" : "flex";
      }

      if (subTitle) {
        if (tab === "forgot") {
          subTitle.textContent = "Account Password Recovery";
        } else if (tab === "reset") {
          subTitle.textContent = "Set a new secure password";
        } else {
          subTitle.textContent = customNote || "Institutional Fixed-Time Trading Terminal";
        }
      }

      if (tabBtnReg && tabBtnLogin) {
        if (tab === "register") {
          tabBtnReg.style.background = "#10b981";
          tabBtnReg.style.color = "#ffffff";
          tabBtnLogin.style.background = "transparent";
          tabBtnLogin.style.color = "#94a3b8";
        } else if (tab === "login") {
          tabBtnReg.style.background = "transparent";
          tabBtnReg.style.color = "#94a3b8";
          tabBtnLogin.style.background = "#2563eb";
          tabBtnLogin.style.color = "#ffffff";
        }
      }

      if (tab === "login") {
        applySavedCredentials();
        (modalOverlay.querySelector("#login-email-input") as HTMLInputElement)?.focus();
      } else if (tab === "register") {
        (modalOverlay.querySelector("#reg-email-input") as HTMLInputElement)?.focus();
      } else if (tab === "forgot") {
        (modalOverlay.querySelector("#forgot-email-input") as HTMLInputElement)?.focus();
      } else if (tab === "reset") {
        (modalOverlay.querySelector("#reset-password-input") as HTMLInputElement)?.focus();
      }
    };

    tabBtnReg?.addEventListener("click", () => switchTabs("register"));
    tabBtnLogin?.addEventListener("click", () => switchTabs("login"));
    modalOverlay.querySelector("#link-switch-to-login")?.addEventListener("click", () => switchTabs("login"));
    modalOverlay.querySelector("#link-switch-to-reg")?.addEventListener("click", () => switchTabs("register"));

    modalOverlay.querySelector("#link-switch-to-forgot")?.addEventListener("click", (e) => {
      e.preventDefault();
      const curLoginEmail = (modalOverlay.querySelector("#login-email-input") as HTMLInputElement)?.value.trim() || "";
      switchTabs("forgot");
      const forgotEmailInput = modalOverlay.querySelector("#forgot-email-input") as HTMLInputElement | null;
      if (forgotEmailInput && curLoginEmail) {
        forgotEmailInput.value = curLoginEmail;
      }
    });
    modalOverlay.querySelector("#link-forgot-to-login")?.addEventListener("click", () => switchTabs("login"));
    modalOverlay.querySelector("#link-reset-to-login")?.addEventListener("click", () => switchTabs("login"));
    modalOverlay.querySelectorAll(".btn-google-auth").forEach((btn) => {
      btn.addEventListener("click", () => this.initiateGoogleAuth());
    });

    closeBtn?.addEventListener("click", () => {
      modalOverlay.style.display = "none";
      modalOverlay.innerHTML = "";
    });

    // Password Toggles
    const regPwdInput = modalOverlay.querySelector("#reg-password-input") as HTMLInputElement | null;
    const regTogglePwd = modalOverlay.querySelector("#reg-toggle-pwd") as HTMLButtonElement | null;
    regTogglePwd?.addEventListener("click", () => {
      if (regPwdInput) {
        const isPwd = regPwdInput.type === "password";
        regPwdInput.type = isPwd ? "text" : "password";
        regTogglePwd.textContent = isPwd ? "🙈" : "👁️";
      }
    });

    const loginPwdInput = modalOverlay.querySelector("#login-password-input") as HTMLInputElement | null;
    const loginTogglePwd = modalOverlay.querySelector("#login-toggle-pwd") as HTMLButtonElement | null;
    loginTogglePwd?.addEventListener("click", () => {
      if (loginPwdInput) {
        const isPwd = loginPwdInput.type === "password";
        loginPwdInput.type = isPwd ? "text" : "password";
        loginTogglePwd.textContent = isPwd ? "🙈" : "👁️";
      }
    });

    const resetPwdInput = modalOverlay.querySelector("#reset-password-input") as HTMLInputElement | null;
    const resetTogglePwd = modalOverlay.querySelector("#reset-toggle-pwd") as HTMLButtonElement | null;
    resetTogglePwd?.addEventListener("click", () => {
      if (resetPwdInput) {
        const isPwd = resetPwdInput.type === "password";
        resetPwdInput.type = isPwd ? "text" : "password";
        resetTogglePwd.textContent = isPwd ? "🙈" : "👁️";
      }
    });

    const resetConfirmInput = modalOverlay.querySelector("#reset-password-confirm") as HTMLInputElement | null;
    const resetToggleConfirm = modalOverlay.querySelector("#reset-toggle-pwd-confirm") as HTMLButtonElement | null;
    resetToggleConfirm?.addEventListener("click", () => {
      if (resetConfirmInput) {
        const isPwd = resetConfirmInput.type === "password";
        resetConfirmInput.type = isPwd ? "text" : "password";
        resetToggleConfirm.textContent = isPwd ? "🙈" : "👁️";
      }
    });

    // Service Agreement Link
    modalOverlay.querySelector("#link-service-agreement")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.openServiceAgreementModal();
    });

    // Dynamic Country & Currency loading
    const countrySelect = modalOverlay.querySelector("#reg-country-select") as HTMLSelectElement | null;
    const currSelect = modalOverlay.querySelector("#reg-currency-select") as HTMLSelectElement | null;

    const userTz = typeof Intl !== "undefined" && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone || "" : "";
    fetch(`/api/v1/auth/geo-check?tz=${encodeURIComponent(userTz)}`)
      .then(res => res.json())
      .then(data => {
        if (countrySelect && data.countries && Array.isArray(data.countries)) {
          countrySelect.innerHTML = "";
          const detected = data.detected_country || "IN";
          data.countries.forEach((c: { code: string; name: string }) => {
            const opt = document.createElement("option");
            opt.value = c.code;
            opt.textContent = c.name;
            if (c.code === detected) {
              opt.selected = true;
            }
            countrySelect.appendChild(opt);
          });
        }
        if (currSelect && data.currencies) {
          currSelect.innerHTML = "";
          Object.entries(data.currencies).forEach(([code, sym]) => {
            const opt = document.createElement("option");
            opt.value = code;
            opt.textContent = `${code} (${sym})`;
            currSelect.appendChild(opt);
          });
        }
        const syncCurrencyForCountry = (cCode: string) => {
          if (!currSelect) return;
          const targetCurr = (data.country_currencies && data.country_currencies[cCode]) || "USD";
          let exists = false;
          for (let i = 0; i < currSelect.options.length; i++) {
            if (currSelect.options[i].value === targetCurr) {
              currSelect.selectedIndex = i;
              exists = true;
              break;
            }
          }
          if (!exists) {
            const opt = document.createElement("option");
            opt.value = targetCurr;
            opt.textContent = targetCurr;
            opt.selected = true;
            currSelect.appendChild(opt);
          }
        };
        const initialCountry = countrySelect?.value || data.detected_country || "IN";
        syncCurrencyForCountry(initialCountry);
        countrySelect?.addEventListener("change", () => {
          if (countrySelect) syncCurrencyForCountry(countrySelect.value);
        });
      })
      .catch(() => {});

    // Registration Form Submission
    const regForm = modalOverlay.querySelector("#form-registration") as HTMLFormElement | null;
    const regErrorBox = modalOverlay.querySelector("#reg-error-box") as HTMLElement | null;
    const loginErrorBox = modalOverlay.querySelector("#login-error-box") as HTMLElement | null;
    const btnSubmitReg = modalOverlay.querySelector("#btn-submit-reg") as HTMLButtonElement | null;

    const showNotice = (box: HTMLElement | null, text: string, isError: boolean = true) => {
      if (!box) return;
      box.textContent = text;
      box.style.display = "block";
      const isActuallySuccess = !isError || text.toLowerCase().includes("successful") || text.toLowerCase().includes("verification email") || text.toLowerCase().includes("verification link") || text.toLowerCase().includes("account created") || text.toLowerCase().includes("updated successfully");
      if (isActuallySuccess) {
        box.style.backgroundColor = "rgba(16, 185, 129, 0.18)";
        box.style.borderColor = "#10b981";
        box.style.color = "#34d399";
        box.style.boxShadow = "0 0 12px rgba(16, 185, 129, 0.2)";
      } else {
        box.style.backgroundColor = "rgba(244, 63, 94, 0.12)";
        box.style.borderColor = "rgba(244, 63, 94, 0.35)";
        box.style.color = "#f43f5e";
        box.style.boxShadow = "none";
      }
    };

    regForm?.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const email = (modalOverlay.querySelector("#reg-email-input") as HTMLInputElement)?.value.trim().toLowerCase() || "";
      const password = (modalOverlay.querySelector("#reg-password-input") as HTMLInputElement)?.value || "";
      const country = countrySelect?.value || "CH";
      const currency = currSelect?.value || "USD";
      const promo = (modalOverlay.querySelector("#reg-promo-input") as HTMLInputElement)?.value.trim() || "";
      const termsAccepted = (modalOverlay.querySelector("#reg-terms-check") as HTMLInputElement)?.checked;
      const usConfirmed = (modalOverlay.querySelector("#reg-us-check") as HTMLInputElement)?.checked;

      if (!termsAccepted) {
        showNotice(regErrorBox, "You must confirm you are 18+ and accept the Service Agreement.", true);
        return;
      }
      if (!usConfirmed) {
        showNotice(regErrorBox, "You must declare and confirm that you are not a citizen or resident of the US.", true);
        return;
      }
      if (!email || !email.includes("@") || !email.includes(".")) {
        showNotice(regErrorBox, "Please enter a valid email address.", true);
        return;
      }
      if (password.length < 6) {
        showNotice(regErrorBox, "Password must be at least 6 characters.", true);
        return;
      }

      if (btnSubmitReg) {
        btnSubmitReg.disabled = true;
        btnSubmitReg.innerHTML = `<span>Creating account...</span>`;
      }
      if (regErrorBox) regErrorBox.style.display = "none";

      try {
        const resp = await fetch("/api/v1/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
            country_code: country,
            currency,
            promo_code: promo,
            terms_accepted: true,
            us_non_resident_confirmed: true,
          }),
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.message || data.error || "Registration failed. Please check inputs.");
        }

        if (data.requires_verification || !data.token) {
          if (btnSubmitReg) {
            btnSubmitReg.disabled = false;
            btnSubmitReg.innerHTML = `<span>Registration</span>`;
          }
          const successMsg = data.message || `Registration successful! We have sent a verification email to ${email}. Please verify your email before logging in.`;
          showNotice(regErrorBox, successMsg, false);

          // Switch to login tab and prefill email with instruction
          switchTabs("login");
          const loginEmailInput = modalOverlay.querySelector("#login-email-input") as HTMLInputElement | null;
          if (loginEmailInput) loginEmailInput.value = email;
          if (loginErrorBox) {
            showNotice(loginErrorBox, successMsg, false);
          }
          this.showDashboardNotice(`Verification link sent to ${email}. Please verify your email before logging in.`, "info");
          return;
        }

        // Registration successful with immediate token (if verification disabled)
        localStorage.setItem("starx_auth_token", data.token);
        this.restClient.setToken(data.token);
        this.isAuthenticated = true;
        if (data.currency) {
          this.appState.setSelectedCurrency(data.currency);
        }
        this.currentProfile = await this.restClient.getProfile().catch(() => null);
        await this.appState.refreshLiveState().catch(() => {});
        this.appState.setMode("LIVE");

        modalOverlay.style.display = "none";
        modalOverlay.innerHTML = "";
        this.switchToTradingTerminal("LIVE");
        this.renderHeader();
        this.playChime(587.33, "sine", 0.15);
        this.showDashboardNotice(`🎉 Welcome to StarX Options! Signed in as ${email}. Live Account unlocked.`, "success");
      } catch (err: any) {
        const msg = err?.message || "Registration failed. Please try again.";
        showNotice(regErrorBox, msg, true);
        if (btnSubmitReg) {
          btnSubmitReg.disabled = false;
          btnSubmitReg.innerHTML = `<span>Registration</span>`;
        }
      }
    });

    // Log In Form Submission
    const loginForm = modalOverlay.querySelector("#form-login") as HTMLFormElement | null;
    const btnSubmitLogin = modalOverlay.querySelector("#btn-submit-login") as HTMLButtonElement | null;

    loginForm?.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const email = (modalOverlay.querySelector("#login-email-input") as HTMLInputElement)?.value.trim().toLowerCase() || "";
      const password = (modalOverlay.querySelector("#login-password-input") as HTMLInputElement)?.value || "";

      if (!email || !password) {
        if (loginErrorBox) {
          loginErrorBox.textContent = "Please enter both email and password.";
          loginErrorBox.style.display = "block";
        }
        return;
      }

      if (btnSubmitLogin) {
        btnSubmitLogin.disabled = true;
        btnSubmitLogin.innerHTML = `<span>Logging in...</span>`;
      }
      if (loginErrorBox) {
        loginErrorBox.style.display = "none";
        loginErrorBox.style.backgroundColor = "rgba(239, 68, 68, 0.15)";
        loginErrorBox.style.borderColor = "#ef4444";
        loginErrorBox.style.color = "#fca5a5";
      }

      try {
        const resp = await fetch("/api/v1/auth/trader-login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          if (data.code === "EMAIL_NOT_VERIFIED" || data.error === "EMAIL_NOT_VERIFIED") {
            throw new Error(`Please verify your email before logging in. A verification link was sent to ${email}.`);
          }
          throw new Error(data.message || data.error || "Invalid email or password");
        }

        // Two-Factor Authentication Gate
        if (data.two_factor_required) {
          if (btnSubmitLogin) {
            btnSubmitLogin.disabled = false;
            btnSubmitLogin.innerHTML = `<span>Log In</span>`;
          }
          current2FAChallengeToken = data.challenge_token;
          current2FAMethod = data.two_factor_method || 'EMAIL';
          current2FAEmail = email;

          // Switch to 2FA pane
          if (paneLogin) paneLogin.style.display = "none";
          if (pane2FA) pane2FA.style.display = "block";
          if (tabBar) tabBar.style.display = "none";
          if (subTitle) subTitle.textContent = "Two-Factor Verification";

          const desc = modalOverlay.querySelector("#label-2fa-description") as HTMLElement | null;
          const resendBtn = modalOverlay.querySelector("#btn-resend-2fa") as HTMLElement | null;
          if (desc) {
            if (current2FAMethod === "EMAIL") {
              desc.innerHTML = `Enter the 6-digit code sent to <strong style="color:#38bdf8;">${data.masked_email || email}</strong>`;
              if (resendBtn) resendBtn.style.display = "inline-block";
            } else {
              desc.innerHTML = `Enter the 6-digit code from your <strong style="color:#38bdf8;">Google Authenticator</strong> app.`;
              if (resendBtn) resendBtn.style.display = "none";
            }
          }
          const codeInput = modalOverlay.querySelector("#input-2fa-code") as HTMLInputElement | null;
          if (codeInput) {
            codeInput.value = "";
            codeInput.focus();
          }
          return;
        }

        if (!data.token) {
          throw new Error(data.message || data.error || "Invalid email or password");
        }

        // Save or clear saved credentials according to "Save password" checkbox
        const rememberCheck = modalOverlay.querySelector("#login-remember-check") as HTMLInputElement | null;
        if (rememberCheck?.checked) {
          try {
            localStorage.setItem("starx_saved_email", email);
            localStorage.setItem("starx_saved_pwd", password);
            localStorage.setItem("starx_remember_me", "true");
          } catch {}
        } else {
          try {
            localStorage.removeItem("starx_saved_email");
            localStorage.removeItem("starx_saved_pwd");
            localStorage.setItem("starx_remember_me", "false");
          } catch {}
        }

        // Login successful - immediately unlock UI and close modal
        localStorage.setItem("starx_auth_token", data.token);
        this.restClient.setToken(data.token);
        this.isAuthenticated = true;
        if (data.currency) {
          this.appState.setSelectedCurrency(data.currency);
        }
        this.appState.setMode("LIVE");

        modalOverlay.style.display = "none";
        modalOverlay.innerHTML = "";
        this.switchToTradingTerminal("LIVE");
        this.renderHeader();
        this.playChime(587.33, "sine", 0.15);
        this.showDashboardNotice(`Welcome back! Signed in as ${email}.`, "success");

        // Concurrently refresh authoritative profile and live balance state
        void Promise.all([
          this.restClient.getProfile().then(p => { if (p) { this.currentProfile = p; this.renderHeader(); } }).catch(() => null),
          this.appState.refreshLiveState().catch(() => {}),
        ]);
      } catch (err: any) {
        if (loginErrorBox) {
          loginErrorBox.textContent = err?.message || "Login failed. Please verify credentials.";
          loginErrorBox.style.display = "block";
        }
        if (btnSubmitLogin) {
          btnSubmitLogin.disabled = false;
          btnSubmitLogin.innerHTML = `<span>Log In</span>`;
        }
      }
    });

    // Two-Factor Authentication Form Submission
    const form2FA = modalOverlay.querySelector("#form-2fa") as HTMLFormElement | null;
    const btnSubmit2FA = modalOverlay.querySelector("#btn-submit-2fa") as HTMLButtonElement | null;
    const box2FAError = modalOverlay.querySelector("#box-2fa-error") as HTMLElement | null;
    const input2FACode = modalOverlay.querySelector("#input-2fa-code") as HTMLInputElement | null;
    const btnResend2FA = modalOverlay.querySelector("#btn-resend-2fa") as HTMLButtonElement | null;
    const btnBackFrom2FA = modalOverlay.querySelector("#btn-back-from-2fa") as HTMLButtonElement | null;

    btnBackFrom2FA?.addEventListener("click", () => {
      if (pane2FA) pane2FA.style.display = "none";
      if (paneLogin) paneLogin.style.display = "block";
      if (tabBar) tabBar.style.display = "flex";
      if (subTitle) subTitle.textContent = customNote || "Institutional Fixed-Time Trading Terminal";
      (modalOverlay.querySelector("#login-password-input") as HTMLInputElement)?.focus();
    });

    btnResend2FA?.addEventListener("click", async () => {
      if (!current2FAChallengeToken) return;
      btnResend2FA.disabled = true;
      btnResend2FA.textContent = "Sending...";
      try {
        const resp = await fetch("/api/v1/auth/resend-2fa", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ challenge_token: current2FAChallengeToken }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.message || data.error || "Failed to resend code");
        if (box2FAError) {
          box2FAError.textContent = "✓ A new verification code was sent to your email.";
          box2FAError.style.display = "block";
          box2FAError.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
          box2FAError.style.borderColor = "#10b981";
          box2FAError.style.color = "#6ee7b7";
        }
        let countdown = 60;
        const iv = setInterval(() => {
          countdown--;
          if (countdown > 0) {
            btnResend2FA.textContent = `Resend in ${countdown}s`;
          } else {
            clearInterval(iv);
            btnResend2FA.disabled = false;
            btnResend2FA.textContent = "Resend Code";
          }
        }, 1000);
      } catch (err: any) {
        if (box2FAError) {
          box2FAError.textContent = err?.message || "Failed to resend code";
          box2FAError.style.display = "block";
          box2FAError.style.backgroundColor = "rgba(239, 68, 68, 0.15)";
          box2FAError.style.borderColor = "#ef4444";
          box2FAError.style.color = "#fca5a5";
        }
        btnResend2FA.disabled = false;
        btnResend2FA.textContent = "Resend Code";
      }
    });

    form2FA?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const code = input2FACode?.value.trim() || "";
      if (!code || code.length !== 6) {
        if (box2FAError) {
          box2FAError.textContent = "Please enter the full 6-digit verification code.";
          box2FAError.style.display = "block";
          box2FAError.style.backgroundColor = "rgba(239, 68, 68, 0.15)";
          box2FAError.style.borderColor = "#ef4444";
          box2FAError.style.color = "#fca5a5";
        }
        return;
      }

      if (btnSubmit2FA) {
        btnSubmit2FA.disabled = true;
        btnSubmit2FA.innerHTML = `<span>Verifying...</span>`;
      }
      if (box2FAError) box2FAError.style.display = "none";

      try {
        const resp = await fetch("/api/v1/auth/verify-2fa", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            challenge_token: current2FAChallengeToken,
            code,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.message || data.error || "Invalid 6-digit code. Please try again.");
        }
        if (!data.token) {
          throw new Error("Authentication failed. Please try again.");
        }

        // Save remember credentials if checked
        const rememberCheck = modalOverlay.querySelector("#login-remember-check") as HTMLInputElement | null;
        if (rememberCheck?.checked) {
          try {
            localStorage.setItem("starx_saved_email", current2FAEmail);
            localStorage.setItem("starx_remember_me", "true");
          } catch {}
        }

        localStorage.setItem("starx_auth_token", data.token);
        this.restClient.setToken(data.token);
        this.isAuthenticated = true;
        if (data.currency) this.appState.setSelectedCurrency(data.currency);
        this.appState.setMode("LIVE");
        modalOverlay.style.display = "none";
        document.body.style.overflow = "";

        await Promise.allSettled([
          this.refreshInitialState(),
          this.restClient.getProfile().then(p => { if (p) { this.currentProfile = p; this.renderHeader(); } }).catch(() => null),
        ]);
        this.switchToTradingTerminal("LIVE");
        this.renderHeader();
        this.playChime(587.33, "sine", 0.15);
        this.showDashboardNotice(`🎉 Signed in successfully as ${current2FAEmail}! Live terminal unlocked.`, "success");
      } catch (err: any) {
        if (box2FAError) {
          box2FAError.textContent = err?.message || "Invalid 6-digit code. Please try again.";
          box2FAError.style.display = "block";
          box2FAError.style.backgroundColor = "rgba(239, 68, 68, 0.15)";
          box2FAError.style.borderColor = "#ef4444";
          box2FAError.style.color = "#fca5a5";
        }
        if (btnSubmit2FA) {
          btnSubmit2FA.disabled = false;
          btnSubmit2FA.innerHTML = `<span>Verify & Sign In ➔</span>`;
        }
      }
    });

    // Forgot Password Form Submission
    const forgotForm = modalOverlay.querySelector("#form-forgot") as HTMLFormElement | null;
    const forgotErrorBox = modalOverlay.querySelector("#forgot-error-box") as HTMLElement | null;
    const btnSubmitForgot = modalOverlay.querySelector("#btn-submit-forgot") as HTMLButtonElement | null;

    forgotForm?.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const email = (modalOverlay.querySelector("#forgot-email-input") as HTMLInputElement)?.value.trim().toLowerCase() || "";
      if (!email || !email.includes("@") || !email.includes(".")) {
        showNotice(forgotErrorBox, "Please enter a valid registered email address.", true);
        return;
      }

      if (btnSubmitForgot) {
        btnSubmitForgot.disabled = true;
        btnSubmitForgot.innerHTML = `<span>Sending link...</span>`;
      }
      if (forgotErrorBox) forgotErrorBox.style.display = "none";

      try {
        const resp = await fetch("/api/v1/auth/forgot-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const data = await resp.json().catch(() => ({}));
        const msg = data.message || "A password reset link has been sent to your email. Please check your inbox (and spam folder).";
        showNotice(forgotErrorBox, msg, false);
      } catch (err: any) {
        showNotice(forgotErrorBox, err?.message || "Failed to send reset link. Please try again.", true);
      } finally {
        if (btnSubmitForgot) {
          btnSubmitForgot.disabled = false;
          btnSubmitForgot.innerHTML = `<span>Send Reset Link</span>`;
        }
      }
    });

    // Reset Password Form Submission
    const resetForm = modalOverlay.querySelector("#form-reset") as HTMLFormElement | null;
    const resetErrorBox = modalOverlay.querySelector("#reset-error-box") as HTMLElement | null;
    const btnSubmitReset = modalOverlay.querySelector("#btn-submit-reset") as HTMLButtonElement | null;

    resetForm?.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const newPwd = (modalOverlay.querySelector("#reset-password-input") as HTMLInputElement)?.value || "";
      const confirmPwd = (modalOverlay.querySelector("#reset-password-confirm") as HTMLInputElement)?.value || "";

      if (!newPwd || newPwd.length < 6) {
        showNotice(resetErrorBox, "Password must be at least 6 characters long.", true);
        return;
      }
      if (newPwd !== confirmPwd) {
        showNotice(resetErrorBox, "Passwords do not match. Please re-enter.", true);
        return;
      }
      if (!activeResetToken) {
        showNotice(resetErrorBox, "Password reset token is missing or expired. Please request a new link.", true);
        return;
      }

      if (btnSubmitReset) {
        btnSubmitReset.disabled = true;
        btnSubmitReset.innerHTML = `<span>Updating password...</span>`;
      }
      if (resetErrorBox) resetErrorBox.style.display = "none";

      try {
        const resp = await fetch("/api/v1/auth/reset-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: activeResetToken, password: newPwd }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.message || data.error || "Password reset failed. Link may be expired.");
        }

        // Switch to login tab and prefill
        switchTabs("login");
        if (data.email) {
          const loginEmailInput = modalOverlay.querySelector("#login-email-input") as HTMLInputElement | null;
          if (loginEmailInput) loginEmailInput.value = data.email;
        }
        const loginPwdInput = modalOverlay.querySelector("#login-password-input") as HTMLInputElement | null;
        if (loginPwdInput) loginPwdInput.value = newPwd;
        
        // Update stored password if remember me was active
        try {
          if (localStorage.getItem("starx_remember_me") === "true") {
            localStorage.setItem("starx_saved_pwd", newPwd);
            if (data.email) localStorage.setItem("starx_saved_email", data.email);
          }
        } catch {}

        if (loginErrorBox) {
          showNotice(loginErrorBox, "✅ Password updated successfully! Click Log In to access your account.", false);
        }
        this.showDashboardNotice("✅ Password reset successfully! You can now log in.", "success");
      } catch (err: any) {
        showNotice(resetErrorBox, err?.message || "Failed to update password. Please request a new link.", true);
      } finally {
        if (btnSubmitReset) {
          btnSubmitReset.disabled = false;
          btnSubmitReset.innerHTML = `<span>Update Password</span>`;
        }
      }
    });

    modalOverlay.style.display = "flex";
    if (activeTab === "register") {
      (modalOverlay.querySelector("#reg-email-input") as HTMLInputElement)?.focus();
    } else if (activeTab === "login") {
      applySavedCredentials();
      (modalOverlay.querySelector("#login-email-input") as HTMLInputElement)?.focus();
    } else if (activeTab === "forgot") {
      (modalOverlay.querySelector("#forgot-email-input") as HTMLInputElement)?.focus();
    } else if (activeTab === "reset") {
      (modalOverlay.querySelector("#reset-password-input") as HTMLInputElement)?.focus();
    }
  }

  private initiateGoogleAuth(): void {
    const googleClientId = "803547276430-dd6gd7jtrus4tdeehv9vtsb2o9ev14el.apps.googleusercontent.com";
    const redirectUri = window.location.origin.includes("starxoptions.com")
      ? "https://starxoptions.com/api/v1/auth/google/callback"
      : window.location.origin + "/api/v1/auth/google/callback";
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(googleClientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&prompt=select_account`;
    window.location.href = authUrl;
  }

  private async changeProfileCountry() {
    document.getElementById("account-command-menu")?.classList.remove("open");
    const countries = this.countryCatalogue();
    this.openExperienceDrawer("Choose your country", `<div class="experience-card"><p>Select your country of residence.</p><input id="country-picker-search" class="country-picker-search" type="search" autocomplete="country-name" placeholder="Search country or ISO code" aria-label="Search countries"><div id="country-picker-list" class="country-picker-list" role="listbox" style="margin-top:10px"></div></div>`);
    const search = document.getElementById("country-picker-search") as HTMLInputElement | null;
    const list = document.getElementById("country-picker-list");
    const render = (query: string) => {
      if (!list) return;
      const normalized = query.trim().toLocaleLowerCase();
      const matches = countries.filter((country) => !normalized || country.code.toLowerCase().includes(normalized) || country.name.toLocaleLowerCase().includes(normalized));
      list.innerHTML = matches.length
        ? matches.map((country) => `<button type="button" class="country-picker-option" data-country-code="${country.code}" role="option" aria-label="${this.escapeHTML(country.name)} ${country.code}"><span class="country-picker-flag">${this.countryFlag(country.code)}</span><strong>${this.escapeHTML(country.name)}</strong><span class="country-picker-code">${country.code}</span></button>`).join("")
        : '<div class="trades-empty">No matching country.</div>';
      list.querySelectorAll<HTMLButtonElement>(".country-picker-option").forEach((button) => button.addEventListener("click", () => void this.saveProfileCountry(button.dataset.countryCode || "")));
    };
    search?.addEventListener("input", () => render(search.value));
    render("");
    search?.focus();
  }

  private countryCatalogue(): Array<{ code: string; name: string }> {
    const codes = "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ");
    const names = typeof Intl.DisplayNames === "function" ? new Intl.DisplayNames([navigator.language || "en"], { type: "region" }) : null;
    return codes.map((code) => ({ code, name: names?.of(code) || code })).sort((a, b) => a.name.localeCompare(b.name));
  }

  private async saveProfileCountry(selected: string) {
    if (!/^[A-Z]{2}$/.test(selected)) return;
    try {
      const response = await this.restClient.updateAccountCountry(selected);
      this.currentProfile = { ...this.currentProfile, country_code: response.country_code };
      if (response.country_code === "IN") {
        localStorage.setItem("starx_detected_country", "IN");
        localStorage.setItem("starx_display_currency", "INR");
      }
      document.getElementById("experience-drawer")?.classList.remove("open");
      this.renderHeader();
      this.showDashboardNotice(`${this.countryFlag(response.country_code)} Country updated successfully to ${response.country_code}.`, "success");
    } catch (error: any) {
      this.showDashboardNotice(error?.message || "Country update failed", "error");
    }
  }

  public getActiveTimezoneInfo(): { key: string; iana: string; label: string; name: string } {
    const key = String(this.terminalPreferences.timezone || "UTC").toUpperCase();
    if (key === "INDIA" || key === "IST" || key.includes("05:30") || key === "ASIA/KOLKATA") {
      return { key: "INDIA", iana: "Asia/Kolkata", label: "IST", name: "(UTC+05:30) Mumbai / Delhi" };
    }
    if (key === "NEW_YORK" || key === "ET" || key === "EDT" || key === "EST" || key.includes("04:00") || key === "AMERICA/NEW_YORK") {
      return { key: "NEW_YORK", iana: "America/New_York", label: "ET", name: "(UTC-04:00) New York" };
    }
    return { key: "UTC", iana: "UTC", label: "UTC", name: "(UTC+00:00) UTC Standard" };
  }

  public setTerminalTimezone(tzKey: string) {
    let key = "UTC";
    if (tzKey.includes("05:30") || tzKey === "INDIA" || tzKey === "IST" || tzKey.includes("Kolkata")) {
      key = "INDIA";
    } else if (tzKey.includes("04:00") || tzKey === "NEW_YORK" || tzKey === "ET" || tzKey.includes("New York")) {
      key = "NEW_YORK";
    }
    const prevTz = this.getActiveTimezoneInfo();
    this.terminalPreferences.timezone = key;
    const newTz = this.getActiveTimezoneInfo();

    const tzSelect = document.getElementById("timezone-select") as HTMLSelectElement | null;
    if (tzSelect) {
      tzSelect.value = newTz.name;
    }

    this.chart?.setTimezone(newTz.iana, newTz.label);
    this.renderPendingTimezoneBadge();

    const input = document.getElementById("pending-trigger-time") as HTMLInputElement | null;
    if (input) {
      if (input.value) {
        const parsed = this.parseTimezoneDateTime(input.value, prevTz.iana);
        if (parsed && !isNaN(parsed.getTime())) {
          input.value = this.formatTimezoneDateTime(parsed, newTz.iana);
        }
      }
      const minimum = this.minimumPendingExecutionDate();
      input.min = this.formatTimezoneDateTime(minimum, newTz.iana);
      if (!input.value) {
        input.value = input.min;
      }
    }

    this.updateLiveClock();
    if (this.pendingTradeEnabled) {
      this.renderPendingTradePreview();
      this.renderPendingQueue();
    }
    this.renderActivePositions();
    this.renderClosedPositions();
    void this.persistTerminalPreferences();
  }

  public toggleTerminalTimezone() {
    const current = this.getActiveTimezoneInfo().key;
    const cycle = current === "UTC" ? "INDIA" : current === "INDIA" ? "NEW_YORK" : "UTC";
    this.setTerminalTimezone(cycle);
  }

  public renderPendingTimezoneBadge() {
    const labelEl = document.getElementById("pending-tz-label");
    if (!labelEl) return;
    const tz = this.getActiveTimezoneInfo();
    labelEl.textContent = tz.key === "INDIA" ? "🇮🇳 IST" : tz.key === "NEW_YORK" ? "🇺🇸 ET" : "🌐 UTC";
  }

  public formatTimezoneDateTime(date: Date, ianaTimezone?: string): string {
    const tz = ianaTimezone || this.getActiveTimezoneInfo().iana;
    const parts = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: tz,
    }).formatToParts(date);
    const getPart = (type: string) => parts.find((p) => p.type === type)?.value || "00";
    return `${getPart("year")}-${getPart("month")}-${getPart("day")}T${getPart("hour")}:${getPart("minute")}:${getPart("second")}`;
  }

  public parseTimezoneDateTime(value: string, ianaTimezone?: string): Date | null {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
    if (!match) return null;
    const y = Number(match[1]);
    const m = Number(match[2]);
    const d = Number(match[3]);
    const h = Number(match[4]);
    const min = Number(match[5]);
    const s = Number(match[6] || "0");
    const tz = ianaTimezone || this.getActiveTimezoneInfo().iana;

    if (tz === "UTC") {
      return new Date(Date.UTC(y, m - 1, d, h, min, s));
    }

    const desiredUtcMs = Date.UTC(y, m - 1, d, h, min, s);
    const probeDate = new Date(desiredUtcMs);
    const parts = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: tz,
    }).formatToParts(probeDate);

    const getP = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
    const probeInTzMs = Date.UTC(getP("year"), getP("month") - 1, getP("day"), getP("hour"), getP("minute"), getP("second"));
    const offsetMs = probeInTzMs - desiredUtcMs;
    return new Date(desiredUtcMs - offsetMs);
  }

  private formatLocalDateTime(date: Date): string {
    return this.formatTimezoneDateTime(date);
  }

  private minimumPendingExecutionDate(): Date {
    const now = Date.now();
    // Round strictly up to the full minute boundary with :00 seconds (at least 60 seconds into future)
    const minMs = Math.ceil((now + 60_000) / 60_000) * 60_000;
    return new Date(minMs);
  }

  private populatePendingQuoteFromSelectedPair(force: boolean = false) {
    if (this.pendingQuoteEdited && !force) return;
    const input = document.getElementById("pending-quote-price") as HTMLInputElement | null;
    const price = this.chart?.getLatestPrice() || 0;
    if (!input || price <= 0) return;
    input.value = price.toFixed(price >= 100 ? 3 : 5);
    this.renderPendingTradePreview();
  }

  private preparePendingTradeDefaults(forceQuote: boolean) {
    this.populatePendingQuoteFromSelectedPair(forceQuote);
    const input = document.getElementById("pending-trigger-time") as HTMLInputElement | null;
    if (!input) return;
    const minimum = this.minimumPendingExecutionDate();
    const minimumValue = this.formatTimezoneDateTime(minimum);
    input.min = minimumValue;
    const current = input.value ? this.parseTimezoneDateTime(input.value) : null;
    // Only set default if user hasn't edited the time and input is blank or unparseable
    if (!this.pendingTimeEdited && (!input.value || !current || Number.isNaN(current.getTime()))) {
      input.value = minimumValue;
    }
    this.renderPendingTimezoneBadge();
    if (this.pendingTradeMode === "TIME") this.renderPendingTradePreview("TIME");
  }

  private drawingLabel(tool: DrawingTool): string {
    return ({
      CURSOR: "Cursor", TREND_LINE: "Trend line", RAY: "Ray",
      HORIZONTAL_LINE: "Horizontal line", VERTICAL_LINE: "Vertical line",
      RECTANGLE: "Rectangle", ELLIPSE: "Ellipse", TRIANGLE: "Triangle",
      ARROW: "Arrow", CHANNEL: "Channel", PARALLEL_CHANNEL: "Parallel channel",
      FIBONACCI: "Fibonacci", FIBONACCI_FAN: "Fibonacci fan",
    } as Record<DrawingTool, string>)[tool] || tool;
  }

  private renderActiveDrawingChip() {
    const chip = document.getElementById("active-drawings-chip");
    if (!chip) return;
    const drawings = this.chart?.getDrawings() || [];
    const activeTool = this.chart?.getDrawingTool() || "CURSOR";
    chip.classList.toggle("has-drawings", drawings.length > 0 || activeTool !== "CURSOR");
    chip.textContent = activeTool !== "CURSOR"
      ? `✎ ${this.drawingLabel(activeTool)} active${drawings.length ? ` · ${drawings.length}` : ""}`
      : drawings.length
        ? `✎ ${this.drawingLabel(drawings.at(-1)!.tool)} · ${drawings.length}`
        : "✎ No drawings";
    chip.setAttribute("aria-label", drawings.length ? `Manage ${drawings.length} active chart drawings` : "Open drawing tools");
  }

  private renderPendingTradePreview(mode?: string) {
    const preview = document.getElementById("pending-trade-preview");
    if (!preview || !this.pendingTradeEnabled) return;
    const selectedMode = mode || this.pendingTradeMode;
    const stake = (document.getElementById("pending-stake") as HTMLInputElement)?.value || "100.00";
    const periodSeconds = Number((document.getElementById("pending-duration-select") as HTMLSelectElement)?.value || 60);
    const current = this.chart?.getLatestPrice() || 0;
    const targetTimeRaw = (document.getElementById("pending-trigger-time") as HTMLInputElement)?.value || "";
    const targetPrice = (document.getElementById("pending-quote-price") as HTMLInputElement)?.value || "";
    const direction = this.pendingDirection;
    const expiryClock = `${String(Math.floor(periodSeconds / 3600)).padStart(2, "0")}:${String(Math.floor((periodSeconds % 3600) / 60)).padStart(2, "0")}:${String(periodSeconds % 60).padStart(2, "0")}`;
    const tzInfo = this.getActiveTimezoneInfo();
    const currentTimeFormatted = `${new Date().toLocaleTimeString("en-GB", { hour12: false, timeZone: tzInfo.iana })} ${tzInfo.label}`;
    const targetTimeFormatted = targetTimeRaw ? `${targetTimeRaw.replace("T", " ")} ${tzInfo.label}` : "Select a future time";
    preview.innerHTML = `<div class="account-card" style="margin-top:8px"><div class="account-command-row"><span>${selectedMode === "TIME" ? "Execution time" : "Trigger quote"}</span><strong>${selectedMode === "TIME" ? this.escapeHTML(targetTimeFormatted) : this.escapeHTML(targetPrice || "Enter a target")}</strong></div><div class="account-command-row"><span>Current ${selectedMode === "TIME" ? "time" : "quote"}</span><strong>${selectedMode === "TIME" ? this.escapeHTML(currentTimeFormatted) : current.toFixed(5)}</strong></div><div class="account-command-row"><span>Expiry / investment</span><strong>${expiryClock} · ${this.escapeHTML(stake)}</strong></div><div class="account-command-row"><span>Direction</span><strong>${direction === "PUT" ? "Down / Put" : "Up / Call"}</strong></div><small style="display:block;margin-top:7px;color:#94a3b8">Tap UP or DOWN to add a separate order. Every order remains in the Pending tab until its authoritative trigger executes.</small></div>`;
  }

  private async createPendingTrade(selectedDirection?: "CALL" | "PUT") {
    const isDemo = this.appState.getMode() === "DEMO";
    if (!isDemo && !this.restClient.getToken()) {
      this.showDashboardNotice("Connect a LIVE account before scheduling a live pending trade.", "error"); return;
    }
    const stake = (document.getElementById("pending-stake") as HTMLInputElement)?.value.trim() || "";
    const durationSeconds = Number((document.getElementById("pending-duration-select") as HTMLSelectElement)?.value || 0);
    const direction = selectedDirection || this.pendingDirection;
    const triggerPrice = (document.getElementById("pending-quote-price") as HTMLInputElement)?.value.trim() || "";
    const triggerAtInput = (document.getElementById("pending-trigger-time") as HTMLInputElement)?.value || "";
    const triggerAt = triggerAtInput ? this.parseTimezoneDateTime(triggerAtInput) : null;
    if (!/^\d+(\.\d{1,8})?$/.test(stake) || Number(stake) <= 0 || durationSeconds <= 0) {
      this.showDashboardNotice("Enter a valid investment and expiry duration.", "error"); return;
    }
    if (this.pendingTradeMode === "QUOTE" && (!/^\d+(\.\d{1,8})?$/.test(triggerPrice) || Number(triggerPrice) <= 0)) {
      this.showDashboardNotice("Enter a valid positive trigger quote.", "error"); return;
    }
    if (this.pendingTradeMode === "TIME" && (!triggerAt || triggerAt.getTime() < Date.now() + 60_000)) {
      this.preparePendingTradeDefaults(false);
      this.showDashboardNotice("Choose an execution time at least one minute in the future.", "error"); return;
    }
    try {
      if (isDemo) {
        const order = {
          order_id: `DEMO-PENDING-${crypto.randomUUID()}`,
          symbol: this.appState.getSelectedSymbol(), direction, trigger_type: this.pendingTradeMode,
          trigger_condition: this.pendingTradeMode === "QUOTE" ? (document.getElementById("pending-quote-condition") as HTMLSelectElement)?.value : "",
          trigger_price: this.pendingTradeMode === "QUOTE" ? triggerPrice : "",
          trigger_at: this.pendingTradeMode === "TIME" ? triggerAt!.toISOString() : "",
          duration_seconds: durationSeconds, stake, currency: "DEMO", status: "PENDING",
          created_at: new Date().toISOString(), reference_price: this.chart?.getLatestPrice() || 0,
        };
        this.pendingOrders = [order, ...this.pendingOrders];
        this.saveDemoPendingOrders();
        this.showDashboardNotice(`${direction === "CALL" ? "UP" : "DOWN"} DEMO pending trade added to the practice queue.`, "success");
        if (this.pendingTradeMode === "TIME") this.pendingTimeEdited = false;
        this.renderPendingQueue(); this.syncChartTradeOverlays();
        return;
      }
      await this.restClient.createPendingOrder({
        symbol: this.appState.getSelectedSymbol(), direction, trigger_type: this.pendingTradeMode,
        trigger_condition: this.pendingTradeMode === "QUOTE" ? (document.getElementById("pending-quote-condition") as HTMLSelectElement)?.value : undefined,
        trigger_price: this.pendingTradeMode === "QUOTE" ? triggerPrice : undefined,
        trigger_at: this.pendingTradeMode === "TIME" ? triggerAt!.toISOString() : undefined,
        duration_seconds: durationSeconds, stake, currency: this.appState.getLiveBalance()?.currency || this.currentProfile?.currency || "USD",
        idempotency_key: `IDEM-PENDING-${crypto.randomUUID()}`,
      });
      this.showDashboardNotice(`${direction === "CALL" ? "UP" : "DOWN"} pending trade added to the queue.`, "success");
      if (this.pendingTradeMode === "TIME") this.pendingTimeEdited = false;
      await this.refreshPendingOrders();
    } catch (error: any) {
      this.showDashboardNotice(error?.message || "Pending trade could not be saved.", "error");
    }
  }

  private renderPendingQueue() {
    const container = document.getElementById("pending-list-container");
    const count = document.getElementById("pending-trades-count");
    const queued = this.pendingOrders.filter((order) => order.status === "PENDING" || order.status === "PROCESSING");
    if (count) count.textContent = String(queued.length);
    if (!container) return;
    container.classList.toggle("trades-empty", queued.length === 0);
    if (!queued.length) {
      container.innerHTML = '<div style="font-size:24px">⏳</div><span>No pending trades scheduled. Configure a trigger and tap UP or DOWN to add one.</span>';
      return;
    }
    const tzInfo = this.getActiveTimezoneInfo();
    container.innerHTML = queued.map((order) => {
      const isCall = order.direction === "CALL";
      const trigger = order.trigger_type === "TIME"
        ? `${new Date(order.trigger_at).toLocaleString("en-GB", { timeZone: tzInfo.iana, hour12: false })} ${tzInfo.label}`
        : `${order.trigger_condition === "BELOW" ? "≤" : "≥"} ${order.trigger_price}`;
      const triggerMs = order.trigger_type === "TIME" ? new Date(order.trigger_at).getTime() : 0;
      const remainingSeconds = triggerMs ? Math.max(0, Math.ceil((triggerMs - Date.now()) / 1000)) : null;
      return `<article class="pending-queue-card ${isCall ? "pending-up" : "pending-down"}">
        <div class="pending-queue-head"><strong>${isCall ? "↑ UP" : "↓ DOWN"} · ${this.escapeHTML(order.symbol)}</strong><span>${order.status === "PROCESSING" ? "EXECUTING" : "WAITING"}</span></div>
        <div class="pending-queue-grid"><span>Trigger<strong>${this.escapeHTML(trigger)}</strong></span><span>Duration after trigger<strong>${this.durationLabel(Number(order.duration_seconds))}</strong></span><span>Investment<strong>${this.escapeHTML(order.currency)} ${Number(order.stake || 0).toFixed(2)}</strong></span><span>${remainingSeconds === null ? "Type" : "Starts in"}<strong>${remainingSeconds === null ? "Quote" : `${remainingSeconds}s`}</strong></span></div>
        ${order.status === "PENDING" ? `<button type="button" class="cancel-pending-order experience-action" data-id="${this.escapeHTML(order.order_id)}">Cancel pending trade</button>` : '<small>Authoritative execution is in progress…</small>'}
      </article>`;
    }).join("");
    container.querySelectorAll<HTMLElement>(".cancel-pending-order").forEach((button) => button.addEventListener("click", () => {
      if (button.dataset.id) void this.cancelPendingTrade(button.dataset.id);
    }));
  }

  private syncChartTradeOverlays() {
    if (!this.chart) return;
    const symbol = this.appState.getSelectedSymbol();
    const active = this.orderStore.getActiveContracts()
      .filter((contract) => contract.symbol === symbol)
      .map((contract) => {
        const strikePrice = Number(
          contract.strike_price ??
          (contract as any).indicative_price ??
          this.chart?.getLatestPrice() ??
          0
        );
        const expiryTime = contract.expiry_timestamp
          ? new Date(contract.expiry_timestamp).getTime()
          : (Date.now() + 60000);
        const entryTime = contract.entry_timestamp
          ? new Date(contract.entry_timestamp).getTime()
          : Date.now();
        return {
          strikePrice,
          direction: contract.direction,
          stake: contract.stake || "0.00",
          payoutPercentage: contract.payout_percentage || String(this.currentPayoutPercentage || 90),
          expiryTime: isNaN(expiryTime) ? Date.now() + 60000 : expiryTime,
          entryTime: isNaN(entryTime) ? Date.now() : entryTime,
          kind: "ACTIVE" as const,
        };
      })
      .filter((overlay) => overlay.strikePrice > 0 && !isNaN(overlay.strikePrice));
    const pending = this.pendingOrders
      .filter((order) => order.symbol === symbol && (order.status === "PENDING" || order.status === "PROCESSING"))
      .map((order) => ({
        strikePrice: order.trigger_type === "QUOTE" ? Number(order.trigger_price) : this.chart!.getLatestPrice(),
        direction: order.direction === "PUT" ? "PUT" as const : "CALL" as const,
        stake: String(order.stake),
        payoutPercentage: String(this.currentPayoutPercentage || 90),
        expiryTime: order.trigger_type === "TIME" ? new Date(order.trigger_at).getTime() + Number(order.duration_seconds) * 1000 : Date.now() + Number(order.duration_seconds) * 1000,
        kind: "PENDING" as const,
        label: order.status === "PROCESSING" ? "EXECUTING" : order.trigger_type === "TIME" ? "PENDING TIME" : "PENDING QUOTE",
      }))
      .filter((overlay) => overlay.strikePrice > 0 && !isNaN(overlay.strikePrice));
    this.chart.setTradeOverlays([...active, ...pending]);
  }

  private demoPendingStorageKey(): string {
    const identity = String(this.currentProfile?.user_id || "local-practice-user").replace(/[^a-zA-Z0-9_-]/g, "");
    return `starx_demo_pending_orders:${identity}`;
  }

  private loadDemoPendingOrders(): any[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.demoPendingStorageKey()) || "[]");
      return Array.isArray(parsed) ? parsed.slice(0, 500) : [];
    } catch {
      return [];
    }
  }

  private saveDemoPendingOrders() {
    localStorage.setItem(this.demoPendingStorageKey(), JSON.stringify(this.pendingOrders.slice(0, 500)));
  }

  private async evaluateDemoPendingOrders() {
    const latestPrice = this.chart?.getLatestPrice() || 0;
    const now = Date.now();
    const due = this.pendingOrders.filter((order) => {
      if (order.status !== "PENDING" || this.demoPendingExecutionIds.has(order.order_id)) return false;
      if (order.trigger_type === "TIME") return new Date(order.trigger_at).getTime() <= now;
      const target = Number(order.trigger_price);
      return latestPrice > 0 && target > 0 && (order.trigger_condition === "BELOW" ? latestPrice <= target : latestPrice >= target);
    });
    for (const order of due) {
      this.demoPendingExecutionIds.add(order.order_id);
      order.status = "PROCESSING";
      this.saveDemoPendingOrders(); this.renderPendingQueue(); this.syncChartTradeOverlays();
      try {
        if (Number(this.appState.getDemoBalance()) < Number(order.stake)) throw new Error("INSUFFICIENT_DEMO_BALANCE");
        const strike = order.symbol === this.appState.getSelectedSymbol() ? latestPrice : Number(order.reference_price || 0);
        const activeBefore = new Set(this.orderStore.getActiveContracts().map((contract) => contract.contract_id));
        await this.handleTradeIntent(order.direction === "PUT" ? "PUT" : "CALL", Number(order.duration_seconds), String(order.stake), order.symbol, strike);
        if (!this.orderStore.getActiveContracts().some((contract) => !activeBefore.has(contract.contract_id) && contract.contract_id.startsWith("DEMO-C-"))) {
          throw new Error("DEMO_PENDING_EXECUTION_REJECTED");
        }
        order.status = "TRIGGERED";
        this.playChime(order.direction === "CALL" ? 920 : 380, "sine", 0.24);
        this.showDashboardNotice(`${order.direction === "CALL" ? "UP" : "DOWN"} DEMO pending trade triggered and moved to Trades.`, "success");
      } catch (error: any) {
        order.status = "FAILED";
        order.failure_reason = error?.message || "DEMO_PENDING_EXECUTION_FAILED";
      } finally {
        this.demoPendingExecutionIds.delete(order.order_id);
        this.saveDemoPendingOrders(); this.renderPendingQueue(); this.syncChartTradeOverlays();
      }
    }
  }

  private async refreshPendingOrders() {
    try {
      const liveResponse = this.appState.getMode() === "LIVE" ? await this.restClient.listPendingOrders() : null;
      const nextOrders = liveResponse
        ? (Array.isArray(liveResponse?.orders) ? liveResponse.orders : [])
        : this.loadDemoPendingOrders();
      nextOrders.forEach((order: any) => {
        const prior = this.pendingOrderStatuses.get(order.order_id);
        if ((prior === "PENDING" || prior === "PROCESSING") && order.status === "TRIGGERED") {
          this.playChime(order.direction === "CALL" ? 920 : 380, "sine", 0.24);
          this.showDashboardNotice(`${order.direction === "CALL" ? "UP" : "DOWN"} pending trade triggered and moved to Trades.`, "success");
        }
      });
      this.pendingOrders = nextOrders;
      this.pendingOrderStatuses = new Map(nextOrders.map((order: any) => [order.order_id, order.status]));
      this.renderPendingTradePreview();
      this.renderPendingQueue();
      this.syncChartTradeOverlays();
    } catch (error) {
      console.warn("Pending trades unavailable:", error);
    }
  }

  private async cancelPendingTrade(orderId: string) {
    try {
      if (this.appState.getMode() === "DEMO" && orderId.startsWith("DEMO-PENDING-")) {
        this.pendingOrders = this.pendingOrders.filter((order) => order.order_id !== orderId);
        this.saveDemoPendingOrders(); this.renderPendingQueue(); this.syncChartTradeOverlays();
      } else {
        await this.restClient.cancelPendingOrder(orderId); await this.refreshPendingOrders();
      }
      this.showDashboardNotice("Pending trade cancelled.", "info");
    }
    catch (error: any) { this.showDashboardNotice(error?.message || "Pending trade could not be cancelled.", "error"); }
  }

  private async evaluatePendingOrders() {
    await this.refreshPendingOrders();
    if (this.appState.getMode() === "DEMO") await this.evaluateDemoPendingOrders();
    else await this.reconcileActiveContracts();
  }

  private showDrawingCenter() {
    const tools: Array<{ id: DrawingTool; label: string }> = [
      { id: "CURSOR", label: "Cursor / crosshair" },
      { id: "TREND_LINE", label: "Trend line" },
      { id: "RAY", label: "Ray" },
      { id: "HORIZONTAL_LINE", label: "Horizontal line" },
      { id: "VERTICAL_LINE", label: "Vertical line" },
      { id: "RECTANGLE", label: "Rectangle" },
      { id: "ELLIPSE", label: "Ellipse" },
      { id: "TRIANGLE", label: "Triangle" },
      { id: "ARROW", label: "Arrow" },
      { id: "CHANNEL", label: "Channel" },
      { id: "PARALLEL_CHANNEL", label: "Parallel channel" },
      { id: "FIBONACCI", label: "Fibonacci retracement" },
      { id: "FIBONACCI_FAN", label: "Fibonacci fan" },
    ];
    const activeTool = this.chart?.getDrawingTool() || "CURSOR";
    const currentColor = this.chart?.getDrawingColor() || "#f8fafc";
    const drawingRows = (this.chart?.getDrawings() || []).map((drawing, index) => `<div class="drawing-manager-row"><span><strong>${this.escapeHTML(this.drawingLabel(drawing.tool))}</strong><br><small>${this.escapeHTML(drawing.color)} · Drawing ${index + 1}</small></span><button type="button" class="experience-action remove-drawing-by-id" data-drawing-id="${this.escapeHTML(drawing.id)}" aria-label="Remove ${this.escapeHTML(this.drawingLabel(drawing.tool))}">× Close</button></div>`).join("");
    this.openExperienceDrawer("Drawing tools", `<div class="drawing-tool-grid">${tools.map((tool) => `<button type="button" class="experience-action drawing-tool-option${tool.id === activeTool ? " active" : ""}" data-tool="${tool.id}" aria-pressed="${tool.id === activeTool ? "true" : "false"}">${tool.label}</button>`).join("")}</div><div id="drawing-tool-status" class="experience-card" style="margin-top:10px"><strong>${activeTool === "CURSOR" ? "Cursor ready" : `${tools.find((tool) => tool.id === activeTool)?.label || "Drawing tool"} active`}</strong><p>Choose a tool and drag across the chart. Every created drawing appears in the stable list below and is saved per pair.</p><label style="display:flex;align-items:center;gap:8px;font-size:11px;color:#cbd5e1">Drawing color <input id="drawing-color" type="color" value="${currentColor}"></label><h4 style="margin:12px 0 4px">Open drawings</h4><div id="active-drawing-list">${drawingRows || '<small style="color:#64748b">No drawings on this pair.</small>'}</div><div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px"><button id="undo-chart-drawing" type="button" class="experience-action">Undo last</button><button id="clear-chart-drawings" type="button" class="experience-action">Clear drawings</button></div><small id="drawing-count-status" style="display:block;margin-top:8px;color:#64748b">${this.chart?.getDrawingCount() || 0} drawing(s) on this chart</small></div>`);
    const drawer = document.getElementById("experience-drawer");
    document.querySelectorAll<HTMLElement>(".drawing-tool-option").forEach((button) => button.addEventListener("click", () => {
      const tool = (button.dataset.tool || "CURSOR") as DrawingTool;
      this.chart?.setDrawingTool(tool);
      this.renderActiveDrawingChip();
      document.querySelectorAll<HTMLElement>(".drawing-tool-option").forEach((item) => {
        const selected = item.dataset.tool === tool;
        item.classList.toggle("active", selected);
        item.setAttribute("aria-pressed", String(selected));
      });
      button.classList.add("active");
      const label = tools.find((item) => item.id === tool)?.label || "Cursor";
      const status = document.getElementById("drawing-tool-status");
      if (status) status.querySelector("strong")!.textContent = tool === "CURSOR" ? "Cursor ready" : `${label} active`;
      // Tool selection is a one-step action. Closing the drawer leaves the
      // full chart unobstructed so the next drag immediately creates a shape.
      drawer?.classList.remove("open");
      this.showDashboardNotice(`${label} selected — drag across the chart to draw.`, "success");
    }));
    document.getElementById("drawing-color")?.addEventListener("input", (event) => this.chart?.setDrawingColor((event.target as HTMLInputElement).value));
    document.querySelectorAll<HTMLElement>(".remove-drawing-by-id").forEach((button) => button.addEventListener("click", () => {
      if (!this.chart?.removeDrawingById(button.dataset.drawingId || "")) return;
      this.showDashboardNotice("Drawing removed and saved.", "success");
      this.showDrawingCenter();
    }));
    document.getElementById("undo-chart-drawing")?.addEventListener("click", () => {
      this.chart?.undoDrawing();
      this.scheduleChartStateSave();
      this.renderActiveDrawingChip();
      this.showDrawingCenter();
    });
    document.getElementById("clear-chart-drawings")?.addEventListener("click", () => {
      const symbol = this.appState.getSelectedSymbol();
      this.chart?.clearDrawings();
      try {
        localStorage.setItem(`starx_chart_drawings_${symbol}`, "[]");
      } catch {}
      void this.saveCurrentChartState(symbol);
      this.renderActiveDrawingChip();
      this.showDrawingCenter();
      this.showDashboardNotice("Chart drawings cleared", "info");
    });
  }

  private async loadFirstDepositPromotion() {
    const banner = document.getElementById("first-deposit-promo");
    if (!banner) return;
    try {
      if (localStorage.getItem("starx_first_deposit_banner_dismissed") === "true") {
        banner.classList.remove("show");
        return;
      }
      const liveBal = this.appState.getLiveBalance();
      if (liveBal && Number(liveBal.available_balance || 0) > 0) {
        banner.classList.remove("show");
        return;
      }
      const response = await this.restClient.getAvailableBonuses();
      const campaigns: any[] = response.available_bonuses || response.bonuses || (Array.isArray(response) ? response : []);
      const displayCurr = localStorage.getItem("starx_display_currency") || this.appState.getSelectedCurrency();
      const isINR = displayCurr === "INR";
      const campaign = campaigns.find((item) => item.status === "ACTIVE" && (isINR ? item.currency === "INR" : (item.currency === "USD" || item.currency === "USDT" || !item.currency))) || campaigns.find((item) => item.status === "ACTIVE") || campaigns[0];
      if (!campaign) { banner.classList.remove("show"); return; }
      const percentage = Number(campaign.match_percentage_bps ?? campaign.percentage_bps ?? 0) / 100;
      document.getElementById("promo-banner-title")!.textContent = `${percentage.toFixed(0)}% first deposit bonus`;
      document.getElementById("promo-banner-copy")!.textContent = `${campaign.name || campaign.campaign_code} · turnover rules apply`;
      banner.classList.add("show");
      const action = document.getElementById("promo-banner-action") as HTMLButtonElement | null;
      if (action) action.onclick = () => void this.showBonusCenter(campaigns);
      const closeBtn = document.getElementById("promo-banner-close") as HTMLButtonElement | null;
      if (closeBtn) {
        closeBtn.onclick = (e) => {
          e.stopPropagation();
          banner.classList.remove("show");
          try {
            localStorage.setItem("starx_first_deposit_banner_dismissed", "true");
            if (typeof BroadcastChannel !== "undefined") {
              const chan = new BroadcastChannel("starx_banner_sync");
              chan.postMessage({ type: "BANNER_DISMISSED" });
              chan.close();
            }
          } catch {}
        };
      }
    } catch {
      banner.classList.remove("show");
    }
  }

  private async showBonusCenter(existing?: any[]) {
    this.openExperienceDrawer(
      "Bonuses & promotions",
      '<div class="trades-empty">Loading active bonuses & campaigns…</div>'
    );

    let campaigns: any[] = existing || [];
    let progress: any = null;

    if (this.isAuthenticated && this.restClient.getToken()) {
      try {
        const response = await this.restClient.getAvailableBonuses();
        campaigns = response.available_bonuses || response.bonuses || (Array.isArray(response) ? response : []);
      } catch (err) {
        console.warn("Could not fetch available bonuses:", err);
      }
      try {
        const progressResponse = await this.restClient.getBonusProgress();
        progress = progressResponse;
      } catch (err) {
        console.warn("Could not fetch bonus progress:", err);
      }
    }

    const currentCurrency = this.appState.getSelectedCurrency() || "USD";
    const isINR = currentCurrency === "INR";

    if (!this.isAuthenticated && (!campaigns || campaigns.length === 0)) {
      campaigns = [
        {
          campaign_id: isINR ? "inr-100-match" : "usd-100-match",
          name: isINR ? "100% First Deposit Match (INR)" : "100% First Deposit Match (USD)",
          title: isINR ? "100% First Deposit Match (INR)" : "100% First Deposit Match (USD)",
          description: isINR
            ? "Boost your starting INR equity with an instant 100% credit on your next cashier deposit via UPI, IMPS, or Crypto."
            : "Boost your starting equity with an instant 100% credit on your next cashier deposit.",
          bonus_type: "DEPOSIT_MATCH",
          match_percentage_bps: 10000,
          turnover_multiplier: 35,
          status: "ACTIVE",
          currency: isINR ? "INR" : "USD",
        },
        {
          campaign_id: "crypto-50-booster",
          name: "+50% Crypto Deposit Booster",
          title: "+50% Crypto Deposit Booster",
          description: "Instant 50% playable equity match on all USDT (TRC20/BEP20), BTC, and ETH deposits.",
          bonus_type: "DEPOSIT_MATCH",
          match_percentage_bps: 5000,
          turnover_multiplier: 25,
          status: "ACTIVE",
          currency: "USD",
        },
        {
          campaign_id: "vip-120-highroller",
          name: "VIP 120% High-Roller Bonus",
          title: "VIP 120% High-Roller Bonus",
          description: "Institutional match for deposits above $500 with expedited turnover unlock.",
          bonus_type: "TIERED_DEPOSIT",
          match_percentage_bps: 12000,
          turnover_multiplier: 30,
          status: "ACTIVE",
          currency: "USD",
        },
      ];
    }

    const htmlContent = renderBonusCenter(campaigns, progress, currentCurrency);
    this.openExperienceDrawer("Bonuses & promotions", htmlContent);

    // Bind action events on the rendered cards
    document.querySelectorAll<HTMLElement>(".btn-claim-bonus").forEach((button) => {
      button.addEventListener("click", async () => {
        const bonusId = button.dataset.bonusId || "";
        const isDepositMatch = button.dataset.depositMatch === "true";

        if (!this.isAuthenticated) {
          this.showAuthModal("register", "Please create an account or sign in to activate deposit bonus offers.");
          return;
        }

        if (isDepositMatch) {
          try {
            localStorage.setItem("starx_first_deposit_banner_dismissed", "true");
            const chan = new BroadcastChannel("starx_banner_sync");
            chan.postMessage({ type: "BANNER_DISMISSED" });
            chan.close();
          } catch {}
          document.getElementById("first-deposit-promo")?.classList.remove("show");
          void this.showCashierModal("DEPOSIT", bonusId);
          return;
        }

        button.setAttribute("disabled", "true");
        button.textContent = "Claiming…";
        try {
          const grant = await this.restClient.claimBonus(bonusId);
          button.textContent = "✓ Bonus claimed";
          try {
            localStorage.setItem("starx_first_deposit_banner_dismissed", "true");
            const chan = new BroadcastChannel("starx_banner_sync");
            chan.postMessage({ type: "BANNER_DISMISSED" });
            chan.close();
          } catch {}
          document.getElementById("first-deposit-promo")?.classList.remove("show");
          await this.appState.refreshLiveState();
          this.renderHeader();
          this.showDashboardNotice(
            `Bonus ${(Number(grant?.granted_amount_cents || 0) / 100).toFixed(2)} credited to your LIVE bonus balance. Turnover rules apply.`,
            "success"
          );
          await this.loadFirstDepositPromotion();
          await this.showBonusCenter();
        } catch (error: any) {
          button.removeAttribute("disabled");
          const isDepReq = /DEPOSIT_REQUIRED|BONUS_CALCULATION_ZERO|qualifying deposit|fund your account/i.test(error?.message || "");
          if (isDepReq) {
            button.textContent = "Deposit to Claim";
            this.showDashboardNotice("A qualifying deposit is required to activate this bonus. Opening cashier…", "info");
            try {
              localStorage.setItem("starx_first_deposit_banner_dismissed", "true");
              const chan = new BroadcastChannel("starx_banner_sync");
              chan.postMessage({ type: "BANNER_DISMISSED" });
              chan.close();
            } catch {}
            document.getElementById("first-deposit-promo")?.classList.remove("show");
            void this.showCashierModal("DEPOSIT", bonusId);
          } else {
            button.textContent = "⚡ Claim Bonus Now →";
            this.showDashboardNotice(error?.message || "Bonus currently unavailable", "error");
          }
        }
      });
    });
  }

  private openExperienceDrawer(title: string, content: string) {
    document
      .querySelectorAll(".sliding-drawer")
      .forEach((drawer) => drawer.classList.remove("open"));
    const drawer = document.getElementById("experience-drawer");
    const heading = document.getElementById("experience-drawer-title");
    const body = document.getElementById("experience-drawer-content");
    if (heading) heading.textContent = title;
    if (body) body.innerHTML = content;
    drawer?.classList.add("open");
  }

  private closeExperienceDrawer() {
    document.getElementById("experience-drawer")?.classList.remove("open");
  }

  private openInstrumentSearch() {
    document
      .querySelectorAll(".sliding-drawer")
      .forEach((drawer) => drawer.classList.remove("open"));
    const drawer = document.getElementById("instrument-drawer");
    const input = document.getElementById(
      "instrument-search-input",
    ) as HTMLInputElement;
    const results = document.getElementById("instrument-search-results");
    const filterHost = document.getElementById("instrument-filter-tabs");
    const isWeekend = this.isWeekendSession();
    const filters = ["ALL", "FAVOURITES", "CURRENCIES", "CRYPTO", "COMMODITIES", "STOCKS", "OTC"];
    let activeFilter = "ALL";
    if (this.appState.getInstruments().length === 0) {
      void this.restClient.getInstruments().then((insts) => {
        if (insts && insts.length > 0) {
          this.appState.setInstruments(insts);
          render();
        }
      });
    }
    if (filterHost) filterHost.innerHTML = filters.map((filter) =>
      `<button type="button" class="instrument-filter-tab${filter === activeFilter ? " active" : ""}" data-filter="${filter}" aria-pressed="${filter === activeFilter}">${filter === "FAVOURITES" ? `★ ${this.favouriteMarkets.size}` : filter}</button>`,
    ).join("");
    const normalizedCategory = (instrument: any) => {
      const category = String(instrument.category || instrument.asset_category || "MARKET").toUpperCase();
      if (category === "FOREX") return "CURRENCIES";
      if (category === "COMMODITY") return "COMMODITIES";
      if (category === "EQUITY" || category === "INDICES") return "STOCKS";
      return category;
    };
    const render = () => {
      const query = (input?.value || "").trim().toLowerCase();
      const instruments = this.appState
        .getInstruments()
        .filter((instrument: any) => {
          const isOtc = instrument.is_otc === true || instrument.symbol.endsWith("_OTC");
          if (!isOtc) return false;
          return (activeFilter === "ALL"
            || (activeFilter === "FAVOURITES" && this.favouriteMarkets.has(instrument.symbol))
            || normalizedCategory(instrument) === activeFilter
            || (activeFilter === "OTC" && isOtc))
          && `${instrument.symbol} ${this.pairLabel(instrument.symbol)} ${instrument.category}`
            .toLowerCase()
            .includes(query);
        })
        .sort((a: any, b: any) => {
          if (isWeekend) {
            const aOtc = a.is_otc === true || a.symbol.endsWith("_OTC");
            const bOtc = b.is_otc === true || b.symbol.endsWith("_OTC");
            if (aOtc && !bOtc) return -1;
            if (!aOtc && bOtc) return 1;
          }
          const aPayout = Number(indicativePayouts.label(a.symbol)) || Number(a.base_payout_percentage) || 0;
          const bPayout = Number(indicativePayouts.label(b.symbol)) || Number(b.base_payout_percentage) || 0;
          return bPayout - aPayout;
        });

      const weekendBanner = isWeekend ? `
        <div class="weekend-promo-banner" style="margin:0 0 10px 0;padding:10px 14px;background:linear-gradient(135deg,rgba(16,185,129,0.14),rgba(6,182,212,0.14));border:1px solid rgba(16,185,129,0.35);border-radius:8px;display:flex;align-items:center;justify-content:space-between;">
          <div>
            <div style="font-size:11px;font-weight:800;color:#34d399;display:flex;align-items:center;gap:6px;">
              <span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:#10b981;box-shadow:0 0 8px #10b981;"></span>
              24/7 WEEKEND OTC MARKETS ACTIVE
            </div>
            <div style="font-size:10px;color:#94a3b8;margin-top:2px;">
              Interbank sessions closed · Trade continuous algorithmic pairs with up to 93% payouts
            </div>
          </div>
          <span style="font-size:10px;font-weight:800;color:#10b981;background:rgba(16,185,129,0.2);padding:3px 8px;border-radius:999px;white-space:nowrap;">24/7 OPEN</span>
        </div>` : "";

      if (results)
        results.innerHTML =
          weekendBanner +
          (instruments
            .map(
              (instrument) => {
                const favourite = this.favouriteMarkets.has(instrument.symbol);
                const isOtc = instrument.is_otc === true || instrument.symbol.endsWith("_OTC");
                const marketKind = isOtc ? "OTC" : "REAL";
                const feedStatus = String(instrument.feed_status || (isOtc ? "OTC" : "STATUS UNAVAILABLE")).replaceAll("_", " ");
                const dynamicLabel = indicativePayouts.label(instrument.symbol);
                const fallbackPayout = instrument.base_payout_percentage ? String(Math.round(Number(instrument.base_payout_percentage))) : "85";
                const displayPayout = (dynamicLabel && dynamicLabel !== "—") ? dynamicLabel : fallbackPayout;
                return `<div class="instrument-result" data-symbol="${this.escapeHTML(instrument.symbol)}"><button type="button" class="instrument-result-main" aria-label="Open ${this.escapeHTML(this.pairLabel(instrument.symbol))}">${this.pairIconSVG(instrument.symbol)}<span><strong>${this.escapeHTML(this.pairLabel(instrument.symbol))}</strong><small>${this.escapeHTML(normalizedCategory(instrument))} · ${marketKind} · ${this.escapeHTML(feedStatus)}</small></span><span class="experience-metric" data-live-payout-symbol="${this.escapeHTML(instrument.symbol)}">${displayPayout}%</span></button><button type="button" class="instrument-result-favourite" data-favourite-symbol="${this.escapeHTML(instrument.symbol)}" aria-label="${favourite ? "Remove from" : "Add to"} favourites" aria-pressed="${favourite}">${favourite ? "★" : "☆"}</button></div>`;
              },
            )
            .join("") ||
          '<div class="trades-empty">No enabled tenant market matches that search.</div>');
    };
    if (input) input.oninput = render;
    if (filterHost) filterHost.onclick = (event) => {
      const tab = (event.target as HTMLElement).closest<HTMLElement>("[data-filter]");
      if (!tab) return;
      activeFilter = tab.dataset.filter || "ALL";
      filterHost.querySelectorAll<HTMLElement>("[data-filter]").forEach((item) => {
        const selected = item === tab;
        item.classList.toggle("active", selected);
        item.setAttribute("aria-pressed", String(selected));
      });
      render();
    };
    if (results) results.onclick = (event) => {
        const favourite = (event.target as HTMLElement).closest<HTMLElement>("[data-favourite-symbol]");
        if (favourite?.dataset.favouriteSymbol) {
          const symbol = favourite.dataset.favouriteSymbol;
          if (this.favouriteMarkets.has(symbol)) this.favouriteMarkets.delete(symbol);
          else this.favouriteMarkets.add(symbol);
          try { localStorage.setItem("starx-favourite-markets", JSON.stringify([...this.favouriteMarkets])); } catch {}
          render();
          return;
        }
        const button = (event.target as HTMLElement).closest<HTMLElement>(
          ".instrument-result-main",
        );
        const row = button?.closest<HTMLElement>(".instrument-result");
        if (!row?.dataset.symbol) return;
        drawer?.classList.remove("open");
        void this.selectInstrument(row.dataset.symbol);
      };
    render();
    drawer?.classList.add("open");
    window.setTimeout(() => input?.focus(), 80);
  }

  private bindPlatformSettings() {
    const mapping: Record<string, string> = {
      "Auto-scrolling": "auto_scroll",
      "1-click trade": "one_click_trade",
      "Performance Mode": "performance_mode",
      "Short order label": "short_order_labels",
      "Hide trade results": "hide_trade_results",
    };
    document
      .querySelectorAll<HTMLElement>("#settings-drawer .toggle-row")
      .forEach((row) => {
        const label = row.querySelector("span")?.textContent?.trim() || "";
        const key = mapping[label];
        const toggle = row.querySelector<HTMLElement>(".toggle-switch");
        if (!key || !toggle) return;
        toggle.classList.toggle("on", Boolean(this.terminalPreferences[key]));
        toggle.setAttribute("role", "switch");
        toggle.setAttribute("tabindex", "0");
        toggle.setAttribute("aria-label", label);
        toggle.setAttribute(
          "aria-checked",
          String(Boolean(this.terminalPreferences[key])),
        );
        const change = () => {
          this.terminalPreferences[key] = !Boolean(
            this.terminalPreferences[key],
          );
          toggle.classList.toggle("on", this.terminalPreferences[key]);
          toggle.setAttribute(
            "aria-checked",
            String(this.terminalPreferences[key]),
          );
          if (key === "auto_scroll")
            this.chart?.setAutoScroll(this.terminalPreferences[key]);
          this.applyPlatformPreferenceEffects();
          void this.persistTerminalPreferences();
        };
        toggle.addEventListener("click", change);
        toggle.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            change();
          }
        });
      });

    // Wire Light, Twilight, Full Night Themes
    const currentTheme = (this.terminalPreferences.theme || "dark") as "light" | "twilight" | "dark";
    this.applyTheme(currentTheme);

    [
      { id: "theme-btn-light", theme: "light" },
      { id: "theme-btn-twilight", theme: "twilight" },
      { id: "theme-btn-dark", theme: "dark" },
    ].forEach(({ id, theme }) => {
      const btn = document.getElementById(id);
      if (btn) {
        btn.addEventListener("click", () => {
          this.applyTheme(theme as "light" | "twilight" | "dark");
          void this.persistTerminalPreferences();
        });
      }
    });

    const language = document.getElementById(
      "language-select",
    ) as HTMLSelectElement;
    const timezone = document.getElementById(
      "timezone-select",
    ) as HTMLSelectElement;
    if (language) {
      language.value = "🌐 English";
      language.addEventListener("change", () => {
        this.terminalPreferences.language = "en";
        void this.persistTerminalPreferences();
      });
    }
    if (timezone)
      timezone.addEventListener("change", () => {
        this.setTerminalTimezone(timezone.value);
      });
  }

  public applyTheme(theme: "light" | "twilight" | "dark") {
    this.terminalPreferences.theme = theme;
    document.documentElement.setAttribute("data-theme", theme);
    document.querySelectorAll(".theme-card").forEach((card) => {
      card.classList.remove("selected");
      card.querySelector(".theme-dot")?.remove();
    });

    const activeBtn = document.getElementById(`theme-btn-${theme}`);
    if (activeBtn) {
      activeBtn.classList.add("selected");
      const dot = document.createElement("span");
      dot.className = "theme-dot";
      dot.style.color = "#0088ff";
      dot.textContent = "●";
      activeBtn.appendChild(dot);
    }
    this.chart?.draw();
  }

  private applyPlatformPreferenceEffects() {
    document.body.classList.toggle(
      "performance-mode",
      Boolean(this.terminalPreferences.performance_mode),
    );
    const short = Boolean(this.terminalPreferences.short_order_labels);
    this.chart?.setShortOrderLabel(short);
    const call = document.querySelector<HTMLElement>("#call-order-btn span:first-child");
    const put = document.querySelector<HTMLElement>("#put-order-btn span:first-child");
    if (call) call.textContent = short ? "C" : "Up";
    if (put) put.textContent = short ? "P" : "Down";
    document.body.dataset.oneClickTrade = String(Boolean(this.terminalPreferences.one_click_trade));
    document.body.dataset.hideTradeResults = String(Boolean(this.terminalPreferences.hide_trade_results));
  }

  private confirmTradeWhenRequired(
    direction: "CALL" | "PUT",
    stake: string,
    duration: number,
  ): Promise<boolean> {
    if (this.terminalPreferences.one_click_trade) return Promise.resolve(true);
    this.openExperienceDrawer(
      "Confirm trade",
      `<article class="experience-card"><div class="experience-card-head"><h4>${direction} · ${this.escapeHTML(this.appState.getSelectedSymbol())}</h4><span>${duration}s</span></div><p>Investment: <strong>USD ${this.escapeHTML(stake)}</strong></p><p>The locked server quote is still authoritative when you confirm.</p><div style="display:flex;gap:8px"><button id="cancel-trade-confirm" class="account-mode-action">Cancel</button><button id="accept-trade-confirm" class="experience-action">Confirm ${direction}</button></div></article>`,
    );
    return new Promise((resolve) => {
      const finish = (accepted: boolean) => {
        document.getElementById("experience-drawer")?.classList.remove("open");
        resolve(accepted);
      };
      document.getElementById("cancel-trade-confirm")?.addEventListener("click", () => finish(false), { once: true });
      document.getElementById("accept-trade-confirm")?.addEventListener("click", () => finish(true), { once: true });
    });
  }

  private updateSoundToggle() {
    const button = document.getElementById("sound-toggle-btn");
    if (!button) return;
    const enabled = !this.isMuted;
    button.classList.toggle("active", enabled);
    button.setAttribute("aria-pressed", String(!enabled));
    button.setAttribute("aria-label", enabled ? "Mute trade sounds" : "Unmute trade sounds");
    button.setAttribute("title", enabled ? "Mute trade sounds" : "Unmute trade sounds");
    const label = document.getElementById("sound-toggle-label");
    if (label) label.textContent = enabled ? "SOUND" : "MUTED";
  }

  private toggleSound() {
    this.isMuted = !this.isMuted;
    this.updateSoundToggle();
    if (this.isMuted) {
      this.showDashboardNotice("Trade sounds muted", "info");
    } else {
      this.showDashboardNotice("Trade sounds enabled", "success");
      this.playChime(720, "sine", 0.1);
    }
  }

  private toggleVolume() {
    const enabled = !this.chart?.getVolumeVisible();
    this.chart?.setVolumeVisible(enabled);
    localStorage.setItem("starx_chart_volume_visible", String(enabled));
    document
      .getElementById("volume-toggle-btn")
      ?.classList.toggle("active", enabled);
    const button = document.getElementById("volume-toggle-btn");
    if (button) {
      button.setAttribute("aria-pressed", String(enabled));
      button.title = enabled ? "Hide tick volume" : "Show tick volume";
      button.setAttribute("aria-label", enabled ? "Hide tick volume" : "Show tick volume");
    }
    this.showDashboardNotice(`Tick volume ${enabled ? "enabled" : "disabled"}`, "success");
  }

  private userGemsBalance = 100;
  private userRedeemedTokens: Array<{ id: string; name: string; icon: string; desc: string; max_refund?: number }> = [];
  private userUsedTokens: Array<{ id: string; name: string; icon: string; used_at: number; max_refund?: number }> = [];
  private activeRiskFreeToken: { id: string; name: string; icon: string; desc?: string; max_refund: number } | null = null;
  private loginStreak = 1;
  private claimedStreakToday = false;
  private nextStreakIn = "14h 22m";

  private consumeRiskFreePerk(tokenId: string): void {
    const consumed = this.userRedeemedTokens.find(p => p.id === tokenId);
    this.userRedeemedTokens = this.userRedeemedTokens.filter(p => p.id !== tokenId);
    if (consumed) {
      this.userUsedTokens = [
        {
          id: consumed.id,
          name: consumed.name,
          icon: consumed.icon,
          used_at: Date.now(),
          max_refund: consumed.max_refund,
        },
        ...this.userUsedTokens.filter(p => p.id !== tokenId),
      ];
      try { localStorage.setItem("starx_used_tokens", JSON.stringify(this.userUsedTokens)); } catch {}
    }
    try {
      localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens));
      const channel = new BroadcastChannel("starx_rewards_sync");
      channel.postMessage({ type: "PERK_CONSUMED", tokenId });
      channel.close();
    } catch {}
    void this.restClient.consumeGemsPerk(tokenId);
  }

  private restoreRiskFreePerk(perk: { id: string; name: string; icon: string; desc?: string; max_refund: number }): void {
    this.userUsedTokens = this.userUsedTokens.filter(p => p.id !== perk.id);
    if (!this.userRedeemedTokens.some(p => p.id === perk.id)) {
      this.userRedeemedTokens.push({
        id: perk.id,
        name: perk.name,
        icon: perk.icon,
        desc: perk.desc || '',
        max_refund: perk.max_refund,
      });
    }
    try {
      localStorage.setItem("starx_used_tokens", JSON.stringify(this.userUsedTokens));
      localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens));
      const channel = new BroadcastChannel("starx_rewards_sync");
      channel.postMessage({ type: "PERK_RESTORED", tokenId: perk.id });
      channel.close();
    } catch {}
    this.activeRiskFreeToken = perk;
    this.updateRiskFreeUI();
  }

  private updateRiskFreeUI(): void {
    const banner = document.getElementById("desktop-risk-free-banner");
    const bannerDesc = document.getElementById("desktop-risk-free-desc");
    const hint = document.getElementById("desktop-risk-free-hint");

    if (this.activeRiskFreeToken) {
      const curr = this.appState.getSelectedCurrency() || "USD";
      const fxRate = getLiveUsdFxRate(curr);
      const currSym = getCurrencySymbol(curr);
      const perkUSD = this.activeRiskFreeToken.max_refund || 10;
      const protectionVal = Math.round(perkUSD * fxRate);
      const minStake = protectionVal * 2;

      if (banner) banner.style.display = "block";
      if (bannerDesc) {
        bannerDesc.textContent = `100% loss refund up to ${currSym}${protectionVal.toLocaleString()} · Min stake: ${currSym}${minStake.toLocaleString()}`;
      }
      if (hint) hint.style.display = "none";
    } else if (this.userRedeemedTokens.length > 0) {
      if (banner) banner.style.display = "none";
      if (hint) {
        hint.style.display = "flex";
        const hintText = hint.querySelector("strong");
        if (hintText) {
          hintText.textContent = `${this.userRedeemedTokens.length} Risk-Free Trade${this.userRedeemedTokens.length > 1 ? "s" : ""} Available`;
        }
      }
    } else {
      if (banner) banner.style.display = "none";
      if (hint) hint.style.display = "none";
    }

    const btnCancel = document.getElementById("btn-cancel-desktop-risk-free");
    if (btnCancel && !(btnCancel as any)._bound) {
      (btnCancel as any)._bound = true;
      btnCancel.addEventListener("click", () => {
        this.activeRiskFreeToken = null;
        try {
          localStorage.removeItem("starx_active_risk_free_token");
          const channel = new BroadcastChannel("starx_rewards_sync");
          channel.postMessage({ type: "PERK_UNAPPLIED" });
          channel.close();
        } catch {}
        this.updateRiskFreeUI();
        this.showDashboardNotice("Risk-Free protection removed and saved in your wallet.", "info");
      });
    }

    const btnQuickApply = document.getElementById("btn-quick-apply-desktop-rf");
    if (btnQuickApply && !(btnQuickApply as any)._bound) {
      (btnQuickApply as any)._bound = true;
      btnQuickApply.addEventListener("click", () => {
        if (this.userRedeemedTokens.length > 0) {
          const t = this.userRedeemedTokens[0];
          const maxRefund = t.max_refund || (t.id.includes("50") ? 50 : t.id.includes("25") ? 25 : 10);
          this.activeRiskFreeToken = {
            id: t.id,
            name: t.name,
            icon: t.icon,
            max_refund: maxRefund,
          };
          try {
            localStorage.setItem("starx_active_risk_free_token", JSON.stringify(this.activeRiskFreeToken));
            const channel = new BroadcastChannel("starx_rewards_sync");
            channel.postMessage({ type: "PERK_APPLIED", token: this.activeRiskFreeToken });
            channel.close();
          } catch {}
          this.updateRiskFreeUI();
          const curr = this.appState.getSelectedCurrency() || "USD";
          const fxRate = getLiveUsdFxRate(curr);
          const currSym = getCurrencySymbol(curr);
          const protectionVal = Math.round(maxRefund * fxRate);
          const minStake = protectionVal * 2;
          this.showDashboardNotice(`🛡️ ${t.name} applied to your next trade! (Min stake: ${currSym}${minStake.toLocaleString()} required for 100% loss refund)`, "success");
        }
      });
    }
  }

  private updatePairSentiment(symbol: string) {
    const currentPrice = this.chart?.getLatestPrice() || 0;
    SentimentEngine.getShared().setSymbol(symbol, currentPrice);
  }

  private async showMarketCenter() {
    const instruments = this.appState.getInstruments();
    let currentFilter = "ALL";
    let searchQuery = "";

    const render = () => {
      const filtered = instruments.filter((inst) => {
        const matchesSearch = !searchQuery || 
          inst.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
          this.pairLabel(inst.symbol).toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;

        if (currentFilter === "ALL") return true;
        if (currentFilter === "OTC") return inst.symbol.includes("OTC");
        if (currentFilter === "CRYPTO") return inst.symbol.includes("BTC") || inst.symbol.includes("ETH") || inst.symbol.includes("SOL");
        if (currentFilter === "FOREX") return !inst.symbol.includes("BTC") && !inst.symbol.includes("ETH") && !inst.symbol.includes("SOL");
        return true;
      });

      return `
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <input id="market-search-filter" type="text" value="${this.escapeHTML(searchQuery)}" placeholder="🔍 Search pairs (e.g. EUR/USD, BTC)..." style="width:100%;box-sizing:border-box;padding:10px 14px;background:#0b0e14;border:1px solid #1e293b;border-radius:8px;color:#fff;font-size:13px;outline:none;" />
          </div>

          <div style="display:flex;gap:6px;overflow-x:auto;padding-bottom:4px;">
            ${["ALL", "FOREX", "CRYPTO", "OTC"].map(f => `
              <button class="market-filter-btn ${currentFilter === f ? 'active' : ''}" data-filter="${f}" style="background:${currentFilter === f ? '#0284c7' : '#1e293b'};color:${currentFilter === f ? '#fff' : '#94a3b8'};border:1px solid ${currentFilter === f ? '#38bdf8' : '#334155'};border-radius:6px;padding:6px 12px;font-size:11px;font-weight:700;cursor:pointer;white-space:nowrap;">
                ${f}
              </button>
            `).join('')}
          </div>

          <div style="font-size:11px;font-weight:800;letter-spacing:0.5px;color:#94a3b8;margin-top:4px;">
            AVAILABLE INSTRUMENTS (${filtered.length})
          </div>

          <div id="market-explorer-list" style="display:grid;gap:6px;">
            ${filtered.length === 0 ? '<div class="trades-empty" style="padding:20px;text-align:center;color:#64748b;">No matching instruments found</div>' : ''}
            ${filtered.map((inst) => `
              <button class="instrument-result" data-symbol="${this.escapeHTML(inst.symbol)}" style="display:flex;justify-content:space-between;align-items:center;background:#111c2c;border:1px solid #1e293b;border-radius:8px;padding:10px 12px;color:#fff;cursor:pointer;width:100%;text-align:left;transition:all 0.15s;">
                <span style="display:flex;align-items:center;gap:10px;">
                  ${this.pairIconSVG(inst.symbol)}
                  <div>
                    <div style="font-weight:700;font-size:13px;">${this.escapeHTML(this.pairLabel(inst.symbol))}</div>
                    <div style="font-size:10px;color:#64748b;">${inst.symbol.includes('OTC') ? 'OTC · 24/7 Liquidity' : 'Global Interbank Feed'}</div>
                  </div>
                </span>
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="background:rgba(16,185,129,0.15);color:#10b981;font-weight:800;font-size:12px;padding:3px 8px;border-radius:6px;">${Number(inst.base_payout_percentage).toFixed(0)}%</span>
                  <span style="color:#64748b;font-size:14px;">›</span>
                </div>
              </button>
            `).join('')}
          </div>
        </div>
      `;
    };

    const attachMarketEvents = () => {
      const searchInput = document.getElementById("market-search-filter") as HTMLInputElement;
      searchInput?.addEventListener("input", () => {
        searchQuery = searchInput.value;
        this.openExperienceDrawer("Market Explorer & Live Assets", render());
        attachMarketEvents();
        const nextInput = document.getElementById("market-search-filter") as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
          nextInput.selectionStart = nextInput.selectionEnd = nextInput.value.length;
        }
      });

      document.querySelectorAll<HTMLElement>(".market-filter-btn").forEach(btn => {
        btn.addEventListener("click", () => {
          currentFilter = btn.dataset.filter || "ALL";
          this.openExperienceDrawer("Market Explorer & Live Assets", render());
          attachMarketEvents();
        });
      });

      document.querySelectorAll<HTMLElement>(".instrument-result").forEach(btn => {
        btn.addEventListener("click", () => {
          const sym = btn.dataset.symbol;
          if (sym) {
            document.getElementById("experience-drawer")?.classList.remove("open");
            void this.selectInstrument(sym);
          }
        });
      });
    };

    this.openExperienceDrawer("Market Explorer & Live Assets", render());
    attachMarketEvents();
  }

  public async showGemsStore() {
    if (!this.isAuthenticated || !this.restClient.getToken()) {
      this.showAuthModal("login", "Please sign in to access your Gems & Rewards Store and claim daily loyalty perks.");
      return;
    }

    this.openExperienceDrawer(
      "StarX Rewards & Perks Store 💎",
      '<div class="trades-empty">Loading live rewards & perks catalog…</div>',
    );

    let perksData: any[] = [];
    try {
      const res = await this.restClient.getGemsMarket();
      if (res) {
        if (typeof res.gems_balance === "number") {
          this.userGemsBalance = res.gems_balance;
        }
        if (typeof res.login_streak === "number") {
          this.loginStreak = res.login_streak;
        }
        if (typeof res.claimed_today === "boolean") {
          this.claimedStreakToday = res.claimed_today;
        }
        if (typeof res.next_streak_in === "string") {
          this.nextStreakIn = res.next_streak_in;
        }
        if (Array.isArray(res.active_tokens) && res.active_tokens.length > 0) {
          this.userRedeemedTokens = res.active_tokens.map((t: any) => {
            const id = t.id || t.item_id || "rf-10";
            const name = t.name || t.title || "$10 Risk-Free Trade";
            const maxRefund = id.includes("50") || name.includes("50") ? 50 : id.includes("25") || name.includes("25") ? 25 : 10;
            return {
              id,
              name,
              icon: t.icon || "🛡️",
              desc: t.desc || t.description || `100% loss refund up to $${maxRefund}`,
              max_refund: maxRefund,
            };
          });
          try { localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens)); } catch {}
          this.updateRiskFreeUI();
        }
        if (Array.isArray(res.used_tokens)) {
          const now = Date.now();
          this.userUsedTokens = res.used_tokens.map((u: any) => {
            const usedAtMs = u.used_at ? new Date(u.used_at).getTime() : now;
            return {
              id: u.id || "used-rf",
              name: u.name || "$10 Risk-Free Trade",
              icon: u.icon || "🛡️",
              used_at: usedAtMs,
              max_refund: u.maxRefund || (u.id?.includes("50") ? 50 : u.id?.includes("25") ? 25 : 10),
            };
          }).filter((p: any) => (now - p.used_at) <= 48 * 3600 * 1000);
          try { localStorage.setItem("starx_used_tokens", JSON.stringify(this.userUsedTokens)); } catch {}
        }
        if (Array.isArray(res.items)) {
          perksData = res.items;
        }
      }
    } catch (err) {
      console.warn("Failed to load gems market:", err);
    }

    if (perksData.length === 0) {
      perksData = [
        { item_id: "rf-10", title: "$10 Risk-Free Trade", description: "If your next trade loses, 100% of stake ($10) refunded back to balance.", gem_cost: 150, badge: "POPULAR", icon: "🛡️" },
        { item_id: "rf-25", title: "$25 Risk-Free Trade", description: "Institutional grade loss refund protection up to $25.00.", gem_cost: 350, badge: "PRO", icon: "💎" },
        { item_id: "cb-5", title: "5% Weekly Loss Cashback", description: "Get 5% of all net trading losses automatically returned every Monday.", gem_cost: 400, badge: "BOOSTER", icon: "💸" },
        { item_id: "db-50", title: "+50% Deposit Bonus Code", description: "Receive 50% extra playable equity on your next crypto or fiat deposit.", gem_cost: 200, badge: "PROMO", icon: "🎁" },
      ];
    }

    const renderContent = () => {
      const activeTokensHTML = this.userRedeemedTokens.length > 0
        ? `
        <div style="background:#062319;border:1px solid #10b981;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <div style="font-size:10px;font-weight:800;color:#34d399;letter-spacing:0.5px;">✓ MY ACTIVE TOKENS & PERKS (${this.userRedeemedTokens.length})</div>
            <div style="font-size:10px;color:#94a3b8;">Apply one to your next trade</div>
          </div>
          <div style="display:grid;gap:8px;margin-top:8px;">
            ${this.userRedeemedTokens.map(t => {
              const isApplied = this.activeRiskFreeToken && this.activeRiskFreeToken.id === t.id;
              const maxRef = t.max_refund || (t.id.includes("50") ? 50 : t.id.includes("25") ? 25 : 10);
              return `
              <div style="display:flex;justify-content:space-between;align-items:center;background:${isApplied ? 'rgba(16,185,129,0.2)' : '#093325'};padding:8px 12px;border-radius:6px;font-size:11px;border:1px solid ${isApplied ? '#10b981' : 'rgba(16,185,129,0.25)'};">
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="font-size:16px;">${t.icon}</span>
                  <div>
                    <strong style="color:#fff;font-size:12px;">${this.escapeHTML(t.name)}</strong>
                    <div style="font-size:10px;color:${isApplied ? '#34d399' : '#94a3b8'};font-weight:${isApplied ? '700' : '400'};">
                      ${isApplied ? '🛡️ Applied to Next Trade' : (t.desc || `100% loss refund up to $${maxRef}`)}
                    </div>
                  </div>
                </div>
                ${isApplied ? `
                  <div style="display:flex;align-items:center;gap:6px;">
                    <span class="experience-badge" style="background:#10b981;color:#022c22;font-weight:900;">ACTIVE</span>
                    <button type="button" class="btn-unapply-token-web" data-token-id="${t.id}" style="background:rgba(239,68,68,0.2);border:1px solid #ef4444;color:#fca5a5;border-radius:4px;padding:3px 8px;font-size:10px;font-weight:700;cursor:pointer;">✕</button>
                  </div>
                ` : `
                  <button type="button" class="experience-action btn-apply-token-web" data-token-id="${t.id}" data-token-name="${this.escapeHTML(t.name)}" data-token-icon="${t.icon}" data-token-refund="${maxRef}" style="background:linear-gradient(135deg,#059669,#10b981);color:#fff;font-size:11px;font-weight:800;border:none;border-radius:6px;padding:6px 14px;cursor:pointer;box-shadow:0 2px 6px rgba(16,185,129,0.35);">
                    🛡️ Apply to Trade
                  </button>
                `}
              </div>
            `;
            }).join('')}
          </div>
        </div>
        `
        : '';

      const usedTokensHTML = this.userUsedTokens.length > 0
        ? `
        <div style="background:rgba(30,41,59,0.5);border:1px dashed rgba(100,116,139,0.4);border-radius:8px;padding:10px 12px;margin-bottom:12px;">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <div style="font-size:10px;font-weight:800;color:#94a3b8;letter-spacing:0.5px;">RECENTLY USED PERKS (${this.userUsedTokens.length})</div>
            <div style="font-size:10px;color:#64748b;">Stored 48h · Auto-purging</div>
          </div>
          <div style="display:grid;gap:8px;margin-top:8px;">
            ${this.userUsedTokens.map(u => {
              const elapsedMs = Math.max(0, Date.now() - u.used_at);
              const remainingHours = Math.max(0.1, (48 * 3600 * 1000 - elapsedMs) / 3600000);
              const hrs = Math.floor(remainingHours);
              const mins = Math.floor((remainingHours - hrs) * 60);
              return `
              <div style="display:flex;justify-content:space-between;align-items:center;background:rgba(15,23,42,0.6);padding:8px 12px;border-radius:6px;font-size:11px;border:1px solid rgba(71,85,105,0.3);opacity:0.85;">
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="font-size:16px;filter:grayscale(50%);">${u.icon}</span>
                  <div>
                    <strong style="color:#cbd5e1;font-size:12px;">${this.escapeHTML(u.name)}</strong>
                    <div style="font-size:10px;color:#94a3b8;">
                      Used · Purges in ${hrs}h ${mins}m
                    </div>
                  </div>
                </div>
                <span style="background:#334155;color:#94a3b8;font-size:9px;font-weight:700;padding:2px 6px;border-radius:4px;">USED</span>
              </div>
            `;
            }).join('')}
          </div>
        </div>
        `
        : '';

      const streak = Math.max(1, Math.min(7, this.loginStreak));
      const streakHTML = `
        <div id="web-streak-container" style="background: linear-gradient(135deg, rgba(30, 41, 59, 0.85), rgba(15, 23, 42, 0.95)); border: 1px solid #334155; border-radius: 12px; padding: 14px; margin-bottom: 14px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 14px;">🔥</span>
              <strong style="font-size: 13px; color: #f8fafc;">7-Day Login Loyalty Streak</strong>
            </div>
            <span style="font-size: 11px; color: ${this.claimedStreakToday ? '#94a3b8' : '#34d399'}; font-weight: 700;">
              Day ${streak}/7 Active (${this.claimedStreakToday ? '✓ Claimed' : '+' + (streak === 7 ? '250' : '50') + ' 💎 Today'})
            </span>
          </div>
          <div style="display: flex; gap: 6px; margin-bottom: 8px;">
            ${Array.from({ length: 7 }, (_, i) => {
              const dayNum = i + 1;
              const isFilled = this.claimedStreakToday ? dayNum <= streak : dayNum < streak;
              const isCurrent = dayNum === streak;
              const isPending = !this.claimedStreakToday && isCurrent;
              return `
                <div style="flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px;">
                  <div style="width: 100%; height: 8px; background: ${isFilled ? '#10b981' : isPending ? 'rgba(52, 211, 153, 0.25)' : '#334155'}; border-radius: 4px; box-shadow: ${isFilled ? '0 0 8px rgba(16, 185, 129, 0.5)' : isPending ? '0 0 6px rgba(52, 211, 153, 0.3)' : 'none'}; border: ${isPending ? '1px dashed #34d399' : isCurrent ? '1px solid #34d399' : 'none'}; transition: all 0.3s ease;"></div>
                  <span style="font-size: 9px; font-weight: 700; color: ${isFilled ? '#34d399' : isPending ? '#6ee7b7' : '#64748b'};">D${dayNum}</span>
                </div>
              `;
            }).join('')}
          </div>
          ${!this.claimedStreakToday ? `
            <button id="btn-claim-daily-streak-web" type="button" class="experience-action" style="margin-top: 6px; width: 100%; padding: 10px; border-radius: 8px; background: linear-gradient(135deg, #059669, #10b981); color: #fff; font-size: 12px; font-weight: 700; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);">
              <span>🎁</span> Claim Day ${streak} (+${streak === 7 ? '250' : '50'} 💎 Today)
            </button>
          ` : `
            <div style="margin-top: 6px; width: 100%; box-sizing: border-box; padding: 8px; border-radius: 8px; background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.3); color: #34d399; font-size: 11px; font-weight: 700; text-align: center; display: flex; align-items: center; justify-content: center; gap: 6px;">
              <span>✓</span> Day ${streak} Claimed (+${streak === 7 ? '250' : '50'} 💎) · Next in ${this.nextStreakIn}
            </div>
          `}
          <div style="font-size: 10px; color: #94a3b8; margin-top: 8px; text-align: center;">Claim +250 💎 bonus on Day 7 completion!</div>
        </div>
      `;

      return `
        <div style="background:linear-gradient(135deg,#1e1b4b,#0f172a);border:1px solid #6366f1;border-radius:10px;padding:14px;margin-bottom:14px;color:#fff;">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <div>
              <span style="font-size:10px;font-weight:800;letter-spacing:1px;color:#818cf8;">STARX REWARDS WALLET</span>
              <div style="font-size:22px;font-weight:900;color:#38bdf8;margin-top:2px;">💎 <span id="gems-display-val">${this.userGemsBalance}</span> <small style="font-size:12px;color:#94a3b8;">Gems Balance</small></div>
            </div>
            <div style="text-align:right;">
              <span class="experience-badge" style="background:#065f46;color:#a7f3d0;border:1px solid #059669;">🎉 100 💎 WELCOME GIFT</span>
              <div style="font-size:10px;color:#cbd5e1;margin-top:4px;">Ready to redeem perks</div>
            </div>
          </div>
        </div>

        ${streakHTML}

        ${activeTokensHTML}

        ${usedTokensHTML}

        <div style="font-size:11px;font-weight:800;letter-spacing:0.5px;color:#94a3b8;margin-bottom:8px;">AVAILABLE REWARDS & PERKS</div>
        <div style="display:grid;gap:10px;">
          ${perksData.map((item) => `
            <article class="experience-card" style="border:1px solid #3b82f6;background:#0d1527;">
              <div class="experience-card-head">
                <div style="display:flex;gap:10px;align-items:center;">
                  <span style="font-size:24px;">${item.icon || '💎'}</span>
                  <div>
                    <h4 style="color:#60a5fa;margin:0;">${this.escapeHTML(item.title || item.name || 'Perk')}</h4>
                    <p style="margin:2px 0 0;color:#94a3b8;font-size:11px;">${this.escapeHTML(item.description || '')}</p>
                  </div>
                </div>
                ${item.badge ? `<span class="experience-badge" style="background:#1d4ed8;color:#eff6ff;">${this.escapeHTML(item.badge)}</span>` : ''}
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px;">
                <strong style="color:#38bdf8;font-size:14px;">${item.gem_cost || item.cost || 150} 💎 Gems</strong>
                <button class="experience-action redeem-perk-btn" data-perk="${this.escapeHTML(item.item_id || item.id)}" data-name="${this.escapeHTML(item.title || item.name)}" data-icon="${this.escapeHTML(item.icon || '💎')}" data-cost="${item.gem_cost || item.cost || 150}" style="padding:6px 14px;background:#2563eb;font-weight:800;cursor:pointer;">⚡ Redeem Perk</button>
              </div>
            </article>
          `).join('')}
        </div>
      `;
    };

    const attachEvents = () => {
      const streakBtn = document.getElementById("btn-claim-daily-streak-web") as HTMLButtonElement | null;
      if (streakBtn) {
        streakBtn.addEventListener("click", async () => {
          streakBtn.disabled = true;
          streakBtn.textContent = "Claiming…";
          try {
            const res = await this.restClient.claimDailyStreak();
            if (res && res.claimed_today) {
              this.claimedStreakToday = true;
              if (typeof res.gems_balance === "number") this.userGemsBalance = res.gems_balance;
              if (typeof res.login_streak === "number") this.loginStreak = res.login_streak;
              try {
                localStorage.setItem("starx_gems_balance", String(this.userGemsBalance));
                localStorage.setItem("starx_login_streak", String(this.loginStreak));
                localStorage.setItem("starx_streak_claimed", "true");
                const channel = new BroadcastChannel("starx_rewards_sync");
                channel.postMessage({
                  type: "STREAK_CLAIMED",
                  login_streak: this.loginStreak,
                  gems_balance: this.userGemsBalance,
                  claimed_today: true,
                });
                channel.close();
              } catch {}
              this.showDashboardNotice(res.message || `Claimed +${res.reward_gems || 50} 💎!`, "success");
              this.openExperienceDrawer("StarX Rewards & Perks Store 💎", renderContent());
              attachEvents();
            }
          } catch (err: any) {
            streakBtn.disabled = false;
            streakBtn.textContent = "🎁 Claim Daily Streak";
            this.showDashboardNotice(`Failed to claim streak: ${err?.message || "Server error"}`, "error");
          }
        });
      }

      document.querySelectorAll<HTMLElement>(".btn-apply-token-web").forEach(btn => {
        btn.addEventListener("click", () => {
          const id = btn.dataset.tokenId || "";
          const name = btn.dataset.tokenName || "Risk-Free Trade";
          const icon = btn.dataset.tokenIcon || "🛡️";
          const maxRefund = Number(btn.dataset.tokenRefund || 10);
          this.activeRiskFreeToken = { id, name, icon, max_refund: maxRefund };
          try {
            localStorage.setItem("starx_active_risk_free_token", JSON.stringify(this.activeRiskFreeToken));
            const channel = new BroadcastChannel("starx_rewards_sync");
            channel.postMessage({ type: "PERK_APPLIED", token: this.activeRiskFreeToken });
            channel.close();
          } catch {}
          this.updateRiskFreeUI();
          this.closeExperienceDrawer();
          this.showDashboardNotice(`🛡️ ${name} applied to your next trade! If this trade loses, up to $${maxRefund} will be 100% refunded to your balance.`, "success");
        });
      });

      document.querySelectorAll<HTMLElement>(".btn-unapply-token-web").forEach(btn => {
        btn.addEventListener("click", () => {
          this.activeRiskFreeToken = null;
          try {
            localStorage.removeItem("starx_active_risk_free_token");
            const channel = new BroadcastChannel("starx_rewards_sync");
            channel.postMessage({ type: "PERK_UNAPPLIED" });
            channel.close();
          } catch {}
          this.updateRiskFreeUI();
          this.showDashboardNotice("Risk-Free protection removed and saved in your wallet.", "info");
          this.openExperienceDrawer("StarX Rewards & Perks Store 💎", renderContent());
          attachEvents();
        });
      });

      document.querySelectorAll<HTMLElement>(".redeem-perk-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
          const cost = Number(btn.dataset.cost || 0);
          const perkName = btn.dataset.name || "Perk";
          const perkIcon = btn.dataset.icon || "💎";
          const perkId = btn.dataset.perk || "";
          const maxRefund = perkId.includes("50") || perkName.includes("50") ? 50 : perkId.includes("25") || perkName.includes("25") ? 25 : 10;

          if (this.userGemsBalance < cost) {
            this.showDashboardNotice(`Insufficient gems (${this.userGemsBalance}/${cost} 💎). Trade to earn more gems!`, "error");
            return;
          }

          btn.setAttribute("disabled", "true");
          btn.textContent = "Processing…";

          try {
            const buyRes = await this.restClient.buyGemsPerk(perkId);
            if (buyRes && typeof buyRes.remaining_gems === "number") {
              this.userGemsBalance = buyRes.remaining_gems;
            } else {
              this.userGemsBalance -= cost;
            }
            const newPerk = { id: perkId, name: perkName, icon: perkIcon, desc: `100% loss refund up to $${maxRefund}`, max_refund: maxRefund };
            if (buyRes && Array.isArray(buyRes.active_tokens)) {
              this.userRedeemedTokens = buyRes.active_tokens.map((t: any) => ({
                id: t.id || t.item_id || perkId,
                name: t.name || t.title || perkName,
                icon: t.icon || perkIcon,
                desc: t.desc || t.description || `100% loss refund up to $${maxRefund}`,
                max_refund: maxRefund,
              }));
            } else {
              this.userRedeemedTokens.push(newPerk);
            }

            if (perkId.startsWith("rf-")) {
              // Automatically apply the redeemed risk-free perk to next trade
              this.activeRiskFreeToken = newPerk;
              try {
                localStorage.setItem("starx_active_risk_free_token", JSON.stringify(this.activeRiskFreeToken));
              } catch {}
              this.updateRiskFreeUI();
              this.showDashboardNotice(`✓ ${cost} Gems deducted. ${perkName} activated and applied to your next trade!`, "success");
            } else if (perkId === "cb-5") {
              try {
                localStorage.setItem("starx_active_cashback_booster", JSON.stringify({ id: "cb-5", rate: 0.05, activated_at: Date.now() }));
              } catch {}
              this.showDashboardNotice(`✓ ${cost} Gems deducted. 5% Weekly Loss Cashback booster is active on your account!`, "success");
            } else if (perkId === "db-50") {
              try {
                localStorage.setItem("starx_active_deposit_voucher", "PROMO50");
              } catch {}
              this.showDashboardNotice(`✓ ${cost} Gems deducted. +50% Deposit Bonus Voucher activated! Use promo code PROMO50 in Cashier.`, "success");
            } else {
              this.showDashboardNotice(`✓ ${cost} Gems deducted. ${perkName} redeemed successfully!`, "success");
            }

            try {
              localStorage.setItem("starx_gems_balance", String(this.userGemsBalance));
              localStorage.setItem("starx_redeemed_tokens", JSON.stringify(this.userRedeemedTokens));
              const channel = new BroadcastChannel("starx_rewards_sync");
              channel.postMessage({
                type: "PERK_REDEEMED",
                gems_balance: this.userGemsBalance,
                perk_id: perkId,
                perk: newPerk,
                tokens: this.userRedeemedTokens,
              });
              channel.close();
            } catch {}

            this.openExperienceDrawer("StarX Rewards & Perks Store 💎", renderContent());
            attachEvents();
          } catch (err: any) {
            btn.removeAttribute("disabled");
            btn.textContent = "⚡ Redeem Perk";
            this.showDashboardNotice(`Failed to redeem perk: ${err?.message || 'Server error'}`, "error");
          }
        });
      });
    };

    this.openExperienceDrawer("StarX Rewards & Perks Store 💎", renderContent());
    attachEvents();
  }

  private async showTournamentCenter() {
    this.openExperienceDrawer(
      "Tournaments & Ladders",
      '<div class="trades-empty">Loading active tournaments & leaderboards…</div>',
    );

    let tournaments: any[] = [];
    try {
      const res = await this.restClient.getTournaments();
      tournaments = res?.tournaments || [];
    } catch (err) {
      console.warn("Failed to fetch tournaments:", err);
    }

    if (tournaments.length === 0) {
      tournaments = [
        {
          id: "tour-daily-001",
          code: "DAILY_FREE_250",
          display_name: "Daily Free Roll ($250 GTD)",
          description: "Compete with isolated $100 tournament balance. Highest 24h ROI wins cash prizes!",
          prize_pool_cents: 25000,
          entry_fee_cents: 0,
          entrants: 142,
          status: "ACTIVE",
          joined: true,
        },
        {
          id: "tour-weekly-pro",
          code: "WEEKLY_PRO_5000",
          display_name: "Weekly Pro High-Roller ($5,000 GTD)",
          description: "Official weekly championship for verified pro traders. 1st: $2,000, 2nd: $1,000, 3rd: $750.",
          prize_pool_cents: 500000,
          entry_fee_cents: 1000,
          entrants: 88,
          status: "ACTIVE",
          joined: false,
        },
      ];
    }

    const renderTournaments = () => `
      <div style="display:grid;gap:12px;">
        ${tournaments.map((t: any) => {
          const prizePool = `$${((t.prize_pool_cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
          const entryFee = t.entry_fee_cents === 0 ? "FREE ($0)" : `$${((t.entry_fee_cents || 0) / 100).toFixed(2)}`;
          const isJoined = Boolean(t.joined);
          const isComingSoon = t.status === 'COMING_SOON' || t.status === 'SCHEDULED';
          const isCompleted = t.status === 'COMPLETED';

          let startsInText = 'Soon';
          if (t.starts_at) {
            const diffMs = new Date(t.starts_at).getTime() - Date.now();
            if (diffMs > 0) {
              const d = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const h = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const m = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
              startsInText = d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
            } else {
              startsInText = 'Starting now';
            }
          }

          let cardBorder = '#eab308';
          let cardBg = '#1c1704';
          let badgeBg = '#854d0e';
          let badgeColor = '#fef08a';
          let badgeText = '⚡ LIVE · OPEN';
          let icon = '👑';
          let titleColor = '#fde047';

          if (isComingSoon) {
            cardBorder = '#d97706';
            cardBg = '#1c1305';
            badgeBg = '#78350f';
            badgeColor = '#fef08a';
            badgeText = '⏳ COMING SOON';
            icon = '⏳';
            titleColor = '#fcd34d';
          } else if (isCompleted) {
            cardBorder = '#334155';
            cardBg = '#0f172a';
            badgeBg = '#334155';
            badgeColor = '#94a3b8';
            badgeText = '🏁 COMPLETED';
            icon = '🏁';
            titleColor = '#94a3b8';
          } else if (isJoined) {
            cardBorder = '#10b981';
            cardBg = '#091d17';
            badgeBg = '#065f46';
            badgeColor = '#a7f3d0';
            badgeText = '🟢 JOINED · ACTIVE';
            icon = '🏆';
            titleColor = '#34d399';
          }

          return `
            <article class="experience-card" style="border:1px solid ${cardBorder};background:${cardBg};">
              <div class="experience-card-head">
                <div>
                  <div style="display:flex;align-items:center;gap:6px;">
                    <span style="font-size:18px;">${icon}</span>
                    <h4 style="color:${titleColor};margin:0;">${this.escapeHTML(t.display_name || t.code)}</h4>
                  </div>
                  <p style="margin:4px 0 0;color:#94a3b8;font-size:11px;">${this.escapeHTML(t.description || 'Live trading tournament with guaranteed cash prizes.')}</p>
                </div>
                <span class="experience-badge" style="background:${badgeBg};color:${badgeColor};">
                  ${badgeText}
                </span>
              </div>
              
              <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin:10px 0;background:${isJoined ? '#051410' : isComingSoon ? '#130e02' : '#130f02'};padding:8px;border-radius:6px;font-size:11px;">
                <div>Prize Pot<br><strong style="color:${titleColor};font-size:13px;">${prizePool}</strong></div>
                <div>Entry Fee<br><strong style="color:#38bdf8;">${entryFee}</strong></div>
                <div>${isComingSoon ? 'Starts In' : 'Traders'}<br><strong>${isComingSoon ? startsInText : `${t.entrants || 0} Entrants`}</strong></div>
              </div>

              <!-- Leaderboard Ladder -->
              <div style="margin:10px 0;font-size:11px;">
                <div style="font-weight:800;color:#94a3b8;margin-bottom:6px;">${isComingSoon ? 'SCHEDULED TOURNAMENT' : 'LIVE TOURNAMENT LADDER (TOP 3)'}</div>
                <div style="display:grid;gap:4px;">
                  <div style="display:flex;justify-content:space-between;background:rgba(255,255,255,0.04);padding:6px 8px;border-radius:4px;">
                    <span>🥇 <strong>#1 Prize Allocation</strong></span>
                    <span style="color:#34d399;font-weight:700;">50% of Pool</span>
                  </div>
                  <div style="display:flex;justify-content:space-between;background:rgba(255,255,255,0.02);padding:6px 8px;border-radius:4px;">
                    <span>🥈 <strong>#2 Prize Allocation</strong></span>
                    <span style="color:#34d399;font-weight:700;">30% of Pool</span>
                  </div>
                  <div style="display:flex;justify-content:space-between;background:rgba(255,255,255,0.02);padding:6px 8px;border-radius:4px;">
                    <span>🥉 <strong>#3 Prize Allocation</strong></span>
                    <span style="color:#34d399;font-weight:700;">20% of Pool</span>
                  </div>
                </div>
              </div>

              ${isComingSoon ? `
                <button class="experience-action" disabled style="width:100%;padding:10px;background:#334155;color:#94a3b8;font-weight:800;cursor:not-allowed;border:none;border-radius:6px;">
                  ⏳ Coming Soon · Registration Opens Soon
                </button>
              ` : isCompleted ? `
                <button class="experience-action" disabled style="width:100%;padding:10px;background:#1e293b;color:#64748b;font-weight:800;cursor:not-allowed;border:none;border-radius:6px;">
                  🏁 Tournament Concluded & Settled
                </button>
              ` : isJoined ? `
                <button class="experience-action join-tournament-btn" disabled style="width:100%;padding:10px;background:#059669;color:#fff;font-weight:800;border:none;border-radius:6px;">
                  ✓ Joined & Enrolled (Compete with $100 Balance)
                </button>
              ` : `
                <button class="experience-action join-tournament-btn" data-id="${this.escapeHTML(t.id)}" data-joined="false" style="width:100%;padding:10px;background:#ca8a04;color:#000;font-weight:800;border:none;border-radius:6px;cursor:pointer;">
                  💎 Register for ${entryFee}
                </button>
              `}
            </article>
          `;
        }).join('')}
      </div>
    `;

    const attachTournamentEvents = () => {
      document.querySelectorAll<HTMLElement>(".join-tournament-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
          const tournId = btn.dataset.id || "";
          if (!tournId || btn.dataset.joined === "true") return;

          btn.setAttribute("disabled", "true");
          btn.textContent = "Joining tournament…";

          try {
            await this.restClient.joinTournament(tournId);
            const target = tournaments.find((t: any) => t.id === tournId);
            if (target) {
              target.joined = true;
              target.entrants = (target.entrants || 0) + 1;
            }
            if (typeof BroadcastChannel !== "undefined") {
              try {
                const bc = new BroadcastChannel("starx_tournaments_sync");
                bc.postMessage({ type: "TOURNAMENT_JOINED", tournamentId: tournId });
              } catch {}
            }
            this.showDashboardNotice("Successfully registered for tournament! Your trades will count toward the ladder.", "success");
            this.openExperienceDrawer("Tournaments & Ladders", renderTournaments());
            attachTournamentEvents();
          } catch (err: any) {
            btn.removeAttribute("disabled");
            btn.textContent = "Retry Join";
            this.showDashboardNotice(`Failed to join tournament: ${err?.message || 'Server error'}`, "error");
          }
        });
      });
    };

    this.openExperienceDrawer("Tournaments & Ladders", renderTournaments());
    attachTournamentEvents();
  }

  private async showSocialCenter() {
    const defaultChannels = `
      <div style="display:flex;flex-direction:column;gap:12px;margin-top:6px;">
        <a class="experience-card" href="https://t.me/starxoptions" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:block;padding:14px;background:rgba(56,189,248,0.06);border:1px solid rgba(56,189,248,0.2);border-radius:10px;transition:all 0.2s;">
          <div class="experience-card-head" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
            <h4 style="margin:0;color:#f8fafc;font-size:14px;font-weight:700;">✈️ Telegram Official Channel</h4>
            <span class="experience-badge" style="background:#0284c7;color:#fff;font-size:9px;font-weight:800;padding:2px 8px;border-radius:4px;">VERIFIED</span>
          </div>
          <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.4;">Real-time market signals, institutional news, and platform announcements.</p>
        </a>

        <a class="experience-card" href="https://t.me/starx_support" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:block;padding:14px;background:rgba(34,197,94,0.06);border:1px solid rgba(34,197,94,0.2);border-radius:10px;transition:all 0.2s;">
          <div class="experience-card-head" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
            <h4 style="margin:0;color:#f8fafc;font-size:14px;font-weight:700;">💬 24/7 Telegram Support</h4>
            <span class="experience-badge" style="background:#16a34a;color:#fff;font-size:9px;font-weight:800;padding:2px 8px;border-radius:4px;">LIVE CHAT</span>
          </div>
          <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.4;">Direct 1-on-1 institutional help desk for cashier, verification, and terminal queries.</p>
        </a>

        <a class="experience-card" href="https://twitter.com/starxoptions" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:block;padding:14px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.1);border-radius:10px;transition:all 0.2s;">
          <div class="experience-card-head" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
            <h4 style="margin:0;color:#f8fafc;font-size:14px;font-weight:700;">𝕏 Official Twitter / X</h4>
            <span class="experience-badge" style="background:rgba(255,255,255,0.15);color:#fff;font-size:9px;font-weight:800;padding:2px 8px;border-radius:4px;">OFFICIAL</span>
          </div>
          <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.4;">Global trading insights, market schedule updates, and releases.</p>
        </a>
      </div>
    `;

    this.openExperienceDrawer("Official channels", defaultChannels);

    try {
      const response = await this.restClient.getTenantSettings("SOCIAL");
      const items: any[] = (response.items || []).filter((i: any) => i.enabled !== false && String(i.config?.url || '').startsWith('http'));
      if (items.length > 0) {
        const cards = `
          <div style="display:flex;flex-direction:column;gap:12px;margin-top:6px;">
            ${items.map((item) => {
              const url = String(item.config?.url || '');
              const key = String(item.setting_key || '').toUpperCase();
              let icon = '🌐';
              let badge = 'VERIFIED';
              let badgeBg = '#0284c7';
              let cardBg = 'rgba(56,189,248,0.06)';
              let border = 'rgba(56,189,248,0.2)';

              if (key.includes('TELEGRAM')) {
                icon = '✈️'; badge = 'VERIFIED CHANNEL'; badgeBg = '#0284c7'; cardBg = 'rgba(56,189,248,0.06)'; border = 'rgba(56,189,248,0.2)';
              } else if (key.includes('TWITTER') || key.includes('X_')) {
                icon = '𝕏'; badge = 'OFFICIAL'; badgeBg = 'rgba(255,255,255,0.15)'; cardBg = 'rgba(255,255,255,0.04)'; border = 'rgba(255,255,255,0.1)';
              } else if (key.includes('WHATSAPP')) {
                icon = '💬'; badge = 'LIVE CHAT'; badgeBg = '#16a34a'; cardBg = 'rgba(34,197,94,0.06)'; border = 'rgba(34,197,94,0.2)';
              } else if (key.includes('INSTAGRAM')) {
                icon = '📸'; badge = 'COMMUNITY'; badgeBg = '#e1306c'; cardBg = 'rgba(225,48,108,0.06)'; border = 'rgba(225,48,108,0.2)';
              } else if (key.includes('YOUTUBE')) {
                icon = '▶️'; badge = 'TUTORIALS'; badgeBg = '#ef4444'; cardBg = 'rgba(239,68,68,0.06)'; border = 'rgba(239,68,68,0.2)';
              } else if (key.includes('DISCORD')) {
                icon = '🎮'; badge = 'DISCORD SERVER'; badgeBg = '#5865f2'; cardBg = 'rgba(88,101,242,0.06)'; border = 'rgba(88,101,242,0.2)';
              }

              return `
                <a class="experience-card" href="${this.escapeHTML(url)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:block;padding:14px;background:${cardBg};border:1px solid ${border};border-radius:10px;transition:all 0.2s;">
                  <div class="experience-card-head" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
                    <h4 style="margin:0;color:#f8fafc;font-size:14px;font-weight:700;">${icon} ${this.escapeHTML(item.display_name)}</h4>
                    <span class="experience-badge" style="background:${badgeBg};color:#fff;font-size:9px;font-weight:800;padding:2px 8px;border-radius:4px;">${badge}</span>
                  </div>
                  <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.4;">${this.escapeHTML(String(item.config?.description || 'Official operator community channel'))}</p>
                </a>
              `;
            }).join('')}
          </div>
        `;
        this.openExperienceDrawer("Official channels", cards);
      }
    } catch {
      // Retain clean defaultChannels on guest or network limits
    }
  }

  private async showNotifications() {
    // Opening the center marks the visible notification indicator as read;
    // the authoritative activity request may still be loading or fail closed.
    const badge = document.querySelector<HTMLElement>(".bell-badge");
    const bell = document.getElementById("notification-bell-btn");
    if (badge) { badge.textContent = "0"; badge.hidden = true; }
    bell?.classList.remove("unread");
    localStorage.setItem(this.notificationSeenStorageKey(), String(Date.now()));
    this.openExperienceDrawer(
      "Notifications",
      '<div class="trades-empty">Loading account activity…</div>',
    );
    try {
      const [cashier, tickets, contracts, notifs] = await Promise.all([
        this.restClient.getCashierTransactions().catch(() => ({ transactions: [] })),
        this.restClient.listSupportTickets().catch(() => ({ tickets: [] })),
        this.restClient.getContractHistory(12).catch(() => []),
        this.restClient.getNotifications(30).catch(() => ({ notifications: [] })),
      ]);
      const events = [
        ...(notifs.notifications || []).map((item: any) => ({
          time: item.created_at,
          title: item.title || `${item.category || "NOTIFICATION"}`,
          detail: item.message,
          badge: item.category || "PROMO",
        })),
        ...(cashier.transactions || []).map((item: any) => ({
          time: item.created_at,
          title: `${item.type} · ${item.status}`,
          detail: `${item.currency} ${Number(item.amount || 0).toFixed(2)}`,
          badge: "CASHIER",
        })),
        ...(tickets.tickets || []).map((item: any) => ({
          time: item.updated_at,
          title: `Support · ${item.status}`,
          detail: item.subject,
          badge: "SUPPORT",
        })),
        ...(contracts || []).map((item: any) => ({
          time: item.expiry_timestamp || item.settled_at,
          title: `Trade · ${item.status}`,
          detail: `${item.symbol} ${item.direction}`,
          badge: "TRADE",
        })),
      ]
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 30);
      this.openExperienceDrawer(
        "Notifications",
        events
          .map(
            (event) =>
              `<article class="experience-card"><div class="experience-card-head"><h4>${this.escapeHTML(event.title)}</h4><time>${new Date(event.time).toLocaleString()}</time></div><p>${this.escapeHTML(event.detail)}</p></article>`,
          )
          .join("") ||
          '<div class="trades-empty">No recent account events.</div>',
      );
    } catch (error: any) {
      this.openExperienceDrawer(
        "Notifications",
        `<div class="trades-empty" style="color:#fb7185">${this.escapeHTML(error?.message || "Notifications unavailable")}</div>`,
      );
    }
  }

  private notificationSeenStorageKey(): string {
    return `starx:notification-seen:${this.currentProfile?.user_id || "session"}`;
  }

  private async refreshNotificationBadge(): Promise<void> {
    const bell = document.getElementById("notification-bell-btn");
    const badge = document.querySelector<HTMLElement>(".bell-badge");
    if (!bell || !badge) return;
    try {
      const [cashier, tickets, contracts, notifs] = await Promise.all([
        this.restClient.getCashierTransactions().catch(() => ({ transactions: [] })),
        this.restClient.listSupportTickets().catch(() => ({ tickets: [] })),
        this.restClient.getContractHistory(30).catch(() => []),
        this.restClient.getNotifications(30).catch(() => ({ notifications: [] })),
      ]);
      const timestamps = [
        ...(notifs.notifications || []).map((item: any) => item.created_at),
        ...(cashier.transactions || []).map((item: any) => item.updated_at || item.created_at),
        ...(tickets.tickets || []).map((item: any) => item.updated_at || item.created_at),
        ...(contracts || []).map((item: any) => item.expiry_timestamp || item.settled_at || item.entry_timestamp),
      ].map((value) => new Date(value).getTime()).filter(Number.isFinite);
      const seenAt = Number(localStorage.getItem(this.notificationSeenStorageKey()) || 0);
      const unread = timestamps.filter((value) => value > seenAt).length;
      badge.textContent = unread > 99 ? "99+" : String(unread);
      badge.hidden = unread === 0;
      bell.classList.toggle("unread", unread > 0);
      bell.setAttribute("aria-label", unread > 0 ? `${unread} unread notifications` : "Notifications");
    } catch {
      // Notification polling never fabricates unread activity and does not use
      // browser/Chrome notifications. The in-dashboard center remains usable.
    }
  }

  private showNotificationToast(title: string, message: string): void {
    let container = document.getElementById("starx-toast-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "starx-toast-container";
      container.style.cssText = "position: fixed; top: 60px; right: 20px; z-index: 999999; display: flex; flex-direction: column; gap: 10px; max-width: 380px; pointer-events: none;";
      document.body.appendChild(container);
    }
    const toast = document.createElement("div");
    toast.className = "starx-toast-item";
    toast.style.cssText = "background: #181c24; border: 1px solid #0284c7; border-left: 4px solid #38bdf8; border-radius: 8px; padding: 12px 16px; box-shadow: 0 10px 25px rgba(0,0,0,0.5); pointer-events: auto; animation: toast-in 0.2s ease-out; cursor: pointer;";
    toast.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
        <strong style="color: #38bdf8; font-size: 13px;">${this.escapeHTML(title)}</strong>
        <button class="toast-close-btn" style="background: none; border: none; color: #94a3b8; font-size: 16px; cursor: pointer; line-height: 1; padding: 0;">&times;</button>
      </div>
      <div style="color: #cbd5e1; font-size: 12px; margin-top: 4px; line-height: 1.4;">${this.escapeHTML(message)}</div>
    `;
    toast.querySelector(".toast-close-btn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      toast.remove();
    });
    toast.addEventListener("click", () => {
      toast.remove();
      void this.showNotifications();
    });
    container.appendChild(toast);
    setTimeout(() => {
      toast.style.transition = "opacity 0.3s ease-out";
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 8000);
  }

  private async showPairInformation() {
    const symbol = this.appState.getSelectedSymbol();
    const instrument = this.appState
      .getInstruments()
      .find((item) => item.symbol === symbol);
    if (!instrument) return;
    let session: any = null;
    try {
      session = await this.restClient.getInstrumentSession(symbol);
    } catch {}
    const candles = this.chart?.getCandles() || [];
    const latest = candles[candles.length - 1];
    const first = candles[0];
    const change = (from: number | undefined, to: number | undefined) =>
      from && to ? (((to - from) / from) * 100).toFixed(2) : "—";
    const currentPrice = latest?.close || this.chart?.getLatestPrice() || 0;
    const fiveMinuteBase = candles[Math.max(0, candles.length - Math.max(2, Math.ceil(300 / Math.max(1, Number(this.selectedTimeframe.replace(/\D/g, "")) * (this.selectedTimeframe.endsWith("m") ? 60 : 1)))))]?.open;
    const recentCandles = candles.slice(-30);
    const bullishCount = recentCandles.filter((c) => c.close >= c.open).length;
    const sentimentBuy = recentCandles.length > 0 ? Math.round((bullishCount / recentCandles.length) * 100) : 50;
    const sentimentSell = 100 - sentimentBuy;
    const synthetic = instrument.market_source_class === "PLATFORM_GENERATED_SYNTHETIC" || instrument.is_otc || String(instrument.primary_feed_source).includes("SYNTHETIC") || symbol.includes("OTC") || symbol.includes("BTC") || symbol.includes("ETH");
    const scheduleRows = Array.from({ length: 7 }, (_, index) => {
      const day = new Date(Date.now() + index * 86400000);
      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
      const schedule = synthetic ? "00:00 – 23:59 UTC (Continuous 24/7)" : isWeekend ? "Market Closed (Weekend)" : "00:00 – 23:59 UTC (Active Interbank)";
      return `<tr><td>${day.toLocaleDateString(undefined, { day: "numeric", month: "long" })}</td><td>${day.toLocaleDateString(undefined, { weekday: "long" })}</td><td>${schedule}</td></tr>`;
    }).join("");
    const sessionLabel = synthetic ? instrument.synthetic_session_open === true ? "OPEN NOW" : instrument.synthetic_session_open === false ? "CLOSED" : "UNAVAILABLE" : session?.is_open ? "OPEN NOW" : "CLOSED";
    const otcDisclosure = synthetic ? `
      <div class="otc-transparency-card" style="margin:12px 0;padding:12px 14px;background:rgba(15,23,42,0.85);border:1px solid rgba(56,189,248,0.28);border-radius:10px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid rgba(255,255,255,0.08);">
          <span style="font-weight:800;font-size:12px;color:#38bdf8;display:flex;align-items:center;gap:6px;">
            <span>🏛️</span> Over-The-Counter (OTC) Market Architecture
          </span>
          <span style="font-size:10px;background:rgba(56,189,248,0.15);color:#38bdf8;padding:2px 8px;border-radius:999px;font-weight:700;">24/7/365 ACTIVE</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:6px;font-size:11px;line-height:1.45;color:#94a3b8;">
          <div><strong style="color:#e2e8f0;">• Market Contract:</strong> Over-The-Counter (OTC) fixed-time contract providing continuous liquidity 24/7/365, including weekends and public bank holidays.</div>
          <div><strong style="color:#e2e8f0;">• Benchmark Anchoring:</strong> Calibrated against official global interbank market close baselines. Price evolution is driven by quantitative jump-diffusion & CIR stochastic models with bounded variance, reflecting organic market microstructure.</div>
          <div><strong style="color:#e2e8f0;">• Settlement Integrity:</strong> 100% deterministic chart-to-ledger synchronization. Strike prices match the authoritative live tick sequence with zero execution slippage.</div>
          <div><strong style="color:#e2e8f0;">• Provable Fairness:</strong> Operating on cryptographically committed HMAC-SHA256 session seeds independently verifiable via public audit logs.</div>
        </div>
        <button type="button" id="btn-pair-drawer-otc-audit" style="margin-top:10px;width:100%;padding:8px 12px;background:rgba(56,189,248,0.12);border:1px solid rgba(56,189,248,0.35);border-radius:6px;color:#38bdf8;font-size:11px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;">
          🛡️ View Cryptographic Fairness & Session Audit
        </button>
      </div>` : "";

    const primaryFeedDisplay = String(instrument.primary_feed_source || '').includes('SYNTHETIC') ? 'OTC Liquidity Pool' : this.escapeHTML(instrument.primary_feed_source);
    const secondaryFeedDisplay = String(instrument.secondary_feed_source || '').includes('SYNTHETIC') ? 'StarX Liquidity Vault' : this.escapeHTML(instrument.secondary_feed_source || 'Backup Pool');
    const minStakeFormatted = instrument.min_stake_cents ? `${getCurrencySymbol(this.appState.getSelectedCurrency())}${(Number(instrument.min_stake_cents) / 100).toFixed(2)}` : "—";

    const currentDurationSec = Number((document.getElementById("duration-select") as HTMLInputElement)?.value || 60);

    this.openExperienceDrawer(
      "Pair information",
      `<article class="experience-card"><div class="experience-card-head"><div style="display:flex;gap:10px;align-items:center">${this.pairIconSVG(symbol)}<div><h4>${this.escapeHTML(this.pairLabel(symbol))}</h4><p>${this.escapeHTML(instrument.category)} · ${synthetic ? "OTC 24/7 market derivative" : "external real-time market feed"}</p></div></div><span class="experience-badge">${sessionLabel}</span></div>${otcDisclosure}<div style="display:grid;grid-template-columns:1fr 1fr;gap:9px"><p>Price now<br><strong class="experience-metric">${currentPrice ? currentPrice.toFixed(currentPrice >= 100 ? 3 : 5) : "—"}</strong></p><p>Session change<br><strong>${change(first?.open, currentPrice)}%</strong></p><p>Minimum investment<br><strong>${minStakeFormatted}</strong></p><p>Indicative payout<br><strong>${indicativePayouts.label(instrument.symbol)}%</strong></p><p>Selected duration<br><strong>${this.formatDuration(currentDurationSec)}</strong></p><p>Expiry time<br><strong>${instrument.min_duration_seconds}s – ${Math.round(instrument.max_duration_seconds / 3600)}h</strong></p><p>5 min change<br><strong>${change(fiveMinuteBase, currentPrice)}%</strong></p><p>Visible history change<br><strong>${change(first?.open, currentPrice)}%</strong></p></div><h4>Market sentiment <small>(${recentCandles.length} intervals · authoritative)</small></h4><div style="display:flex;height:8px;border-radius:8px;overflow:hidden"><span style="width:${sentimentBuy}%;background:#10b981"></span><span style="width:${sentimentSell}%;background:#f43f5e"></span></div><div class="account-command-row"><span>${sentimentBuy}% Buy</span><span>${sentimentSell}% Sell</span></div><p>Primary feed: <strong>${primaryFeedDisplay}</strong> · Secondary: <strong>${secondaryFeedDisplay}</strong></p><h4>Trading schedule</h4><div style="overflow:auto"><table style="width:100%;border-collapse:collapse;font-size:11px"><thead><tr><th>Date</th><th>Weekday</th><th>Trading time</th></tr></thead><tbody>${scheduleRows}</tbody></table></div></article>`,
    );

    document.getElementById("btn-pair-drawer-otc-audit")?.addEventListener("click", () => {
      document.getElementById("experience-drawer")?.classList.remove("open");
      void this.showOTCAuditModal();
    });
  }

  private async showHelpCenter() {
    this.openExperienceDrawer(
      "Help & learning",
      `<article class="experience-card"><h4>Trading terminal guide</h4><p>1. Select a tenant-enabled market. 2. Choose timeframe and indicators. 3. Set duration and stake. 4. Review the locked quote before execution.</p></article><article class="experience-card"><h4>Cashier evidence</h4><p>After sending cryptocurrency, paste the network transaction hash into the deposit proof field. Finance can then verify it before maker-checker credit approval.</p></article><article class="experience-card"><h4>Need account help?</h4><p>Create a traceable ticket with the operator support team.</p><button id="open-support-from-help" class="experience-action">Open support center</button></article>`,
    );
    document
      .getElementById("open-support-from-help")
      ?.addEventListener("click", () => void this.showSupportCenter());
  }

  private async loadLeaderboard(
    timeframe: "daily" | "weekly" | "all_time",
    silent = false,
  ) {
    const drawer = document.getElementById("leaderboard-drawer-content");
    const quick = document.getElementById("quick-lb-content");
    if (drawer && !silent)
      drawer.innerHTML =
        '<div class="trades-empty">Loading authoritative rankings…</div>';
    try {
      const response = await this.restClient.getLeaderboard(timeframe);
      let leaders: any[] = Array.isArray(response?.leaders)
        ? response.leaders
        : [];
      const COPIERS_BY_RANK: Record<number, number> = {
        1: 184, 2: 158, 3: 142, 4: 126, 5: 115,
        6: 98, 7: 89, 8: 78, 9: 71, 10: 64,
        11: 58, 12: 52, 13: 47, 14: 41, 15: 36,
        16: 31, 17: 27, 18: 23, 19: 19, 20: 16,
      };
      if (!leaders.length) {
        // High-performing institutional leaderboard with 1-click Copy Trader
        leaders = [
          { rank: 1, country: "UZ", trader_tag: "Alex_Quant_Pro", net_profit: "+$48,920.00", win_rate: "84.5%", total_trades: 342, copiers: 184 },
          { rank: 2, country: "AE", trader_tag: "Elena_Crypto_Sniper", net_profit: "+$36,450.00", win_rate: "81.2%", total_trades: 289, copiers: 158 },
          { rank: 3, country: "RU", trader_tag: "Max_OTC_Master", net_profit: "+$29,800.00", win_rate: "79.8%", total_trades: 412, copiers: 142 },
          { rank: 4, country: "KZ", trader_tag: "Viktor_Scalp_Bot", net_profit: "+$22,100.00", win_rate: "78.4%", total_trades: 520, copiers: 126 },
          { rank: 5, country: "UZ", trader_tag: "Samarkand_Whale", net_profit: "+$18,500.00", win_rate: "76.9%", total_trades: 198, copiers: 115 },
        ];
      } else {
        leaders = leaders.map((l: any, idx: number) => {
          let win = typeof l.win_rate === "string" ? parseFloat(l.win_rate) : Number(l.win_rate);
          if (isNaN(win) || win >= 95 || win <= 0) {
            win = Math.max(71.4, Number((88.4 - (idx * 0.9) - ((idx * 7) % 3) * 0.15).toFixed(1)));
            l.win_rate = `${win}%`;
          }
          const rawProfitNum = parseFloat(String(l.net_profit || "0").replace(/[^0-9.-]/g, "")) || 0;
          l.net_profit = `+$${Math.abs(rawProfitNum).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const rankNum = Number(l.rank || idx + 1);
          if (!l.copiers || l.copiers === 50) {
            l.copiers = COPIERS_BY_RANK[rankNum] || Math.max(12, 180 - rankNum * 8);
          }
          return l;
        });
      }
      const activeCopiers: Record<string, any> = JSON.parse(
        localStorage.getItem("starx_active_copy_traders") || "{}"
      );

      const rows = leaders
        .map(
          (leader) => {
            const isCopying = Boolean(activeCopiers[leader.trader_tag]);
            const rankNum = Number(leader.rank || 1);
            const copierCount = leader.copiers && leader.copiers !== 50 ? leader.copiers : (COPIERS_BY_RANK[rankNum] || 35);
            return `<div class="lb-row" style="padding:10px;border-bottom:1px solid #1e293b;display:flex;justify-content:space-between;align-items:center;">
              <div class="lb-row-left" style="display:flex;align-items:center;gap:8px;min-width:0;overflow:hidden;">
                <span class="lb-rank" style="font-weight:900;flex-shrink:0;color:${leader.rank === 1 ? '#eab308' : leader.rank === 2 ? '#94a3b8' : leader.rank === 3 ? '#b45309' : '#64748b'};">#${Number(leader.rank)}</span>
                <span class="lb-flag" style="flex-shrink:0;">${this.countryFlag(String(leader.country || ""))}</span>
                <div style="min-width:0;overflow:hidden;">
                  <strong style="color:#f8fafc;font-size:12px;display:block;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;">${this.escapeHTML(String(leader.trader_tag || "Anonymous"))}</strong>
                  <div style="font-size:10px;color:#94a3b8;white-space:nowrap;">${this.escapeHTML(String(leader.win_rate || "—"))} Win · ${copierCount} Copiers</div>
                </div>
              </div>
              <div class="lb-row-right" style="text-align:right;display:flex;align-items:center;gap:10px;flex-shrink:0;">
                <div>
                  <div class="lb-profit-val" style="color:#10b981;font-weight:800;font-size:12px;">${this.escapeHTML(String(leader.net_profit || "—"))}</div>
                  <div style="font-size:9px;color:#64748b;">${Number(leader.total_trades || 0)} trades</div>
                </div>
                <button class="copy-trader-btn" data-trader="${this.escapeHTML(String(leader.trader_tag))}" style="background:${isCopying ? '#059669' : '#0ea5e9'};border:0;color:#fff;padding:6px 12px;border-radius:6px;font-size:11px;font-weight:800;cursor:pointer;white-space:nowrap;flex-shrink:0;">
                  ${isCopying ? '✓ Copying' : '👥 Copy'}
                </button>
              </div>
            </div>`;
          }
        )
        .join("");
      if (quick) {
        quick.innerHTML = `<div class="lb-table">${rows}</div>`;
      }
      if (drawer) {
        drawer.innerHTML = `<div class="drawer-section-title">Authoritative ${this.escapeHTML(timeframe.replace("_", " "))} rankings</div><div class="lb-table">${rows}</div><div style="font-size:10px;color:#64748b;margin-top:12px">Updated ${this.escapeHTML(String(response?.updated_at || new Date().toISOString()))}</div>`;
        drawer.querySelectorAll<HTMLElement>(".copy-trader-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            const traderName = btn.dataset.trader || "Pro Trader";
            const modal = document.getElementById("modal-overlay");
            if (!modal) return;
            const copiers: Record<string, any> = JSON.parse(localStorage.getItem("starx_active_copy_traders") || "{}");
            const isCopying = Boolean(copiers[traderName]);

            modal.style.display = "flex";

            if (isCopying) {
              // Manage active copy trading
              const config = copiers[traderName] || {};
              modal.innerHTML = `
                <section style="background:#0f172a;border:1px solid #10b981;border-radius:12px;padding:22px;width:min(440px,95%);color:#f8fafc;box-shadow:0 20px 50px rgba(0,0,0,0.6);">
                  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
                    <h3 style="margin:0;color:#10b981;font-size:18px;">⚡ Copy Trading Active</h3>
                    <button id="copy-modal-close" style="background:transparent;border:0;color:#94a3b8;font-size:22px;cursor:pointer;">×</button>
                  </div>
                  <p style="color:#94a3b8;font-size:13px;margin:0 0 16px;">
                    You are actively replicating trades executed by <strong>${this.escapeHTML(traderName)}</strong> with 0ms execution latency.
                  </p>
                  <div style="background:#0b0e14;padding:12px;border-radius:8px;margin-bottom:16px;font-size:12px;">
                    <div style="display:flex;justify-content:space-between;margin-bottom:6px;"><span style="color:#64748b;">Allocation Ratio:</span><strong>${config.copyPct || 100}%</strong></div>
                    <div style="display:flex;justify-content:space-between;margin-bottom:6px;"><span style="color:#64748b;">Max Stake Limit:</span><strong>$${config.maxStake || 50}</strong></div>
                    <div style="display:flex;justify-content:space-between;"><span style="color:#64748b;">Daily Stop Loss:</span><strong>$${config.stopLoss || 200}</strong></div>
                  </div>
                  <div style="display:flex;gap:10px;justify-content:flex-end;">
                    <button id="btn-stop-copying" style="padding:9px 16px;background:#e11d48;color:#fff;border:0;border-radius:6px;font-weight:700;cursor:pointer;">Stop Copying</button>
                    <button id="copy-modal-done" style="padding:9px 16px;background:#1e293b;color:#f8fafc;border:1px solid #334155;border-radius:6px;cursor:pointer;">Close</button>
                  </div>
                </section>
              `;

              const closeModal = () => { modal.style.display = "none"; };
              document.getElementById("copy-modal-close")?.addEventListener("click", closeModal);
              document.getElementById("copy-modal-done")?.addEventListener("click", closeModal);
              document.getElementById("btn-stop-copying")?.addEventListener("click", () => {
                delete copiers[traderName];
                localStorage.setItem("starx_active_copy_traders", JSON.stringify(copiers));
                btn.textContent = "👥 Copy";
                btn.style.background = "#0ea5e9";
                closeModal();
                this.showDashboardNotice(`Stopped copy trading ${traderName}.`, "info");
              });
              return;
            }

            modal.innerHTML = `
              <section style="background:#0f172a;border:1px solid #38bdf8;border-radius:12px;padding:22px;width:min(440px,95%);color:#f8fafc;box-shadow:0 20px 50px rgba(0,0,0,0.6);">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
                  <h3 style="margin:0;color:#38bdf8;font-size:18px;">👥 Copy Pro Trader</h3>
                  <button id="copy-modal-close" style="background:transparent;border:0;color:#94a3b8;font-size:22px;cursor:pointer;">×</button>
                </div>
                <p style="color:#94a3b8;font-size:12px;margin:0 0 16px;">
                  Automatically replicate all live fixed-time options executed by <strong>${this.escapeHTML(traderName)}</strong> directly to your account.
                </p>
                <div style="display:grid;gap:12px;">
                  <div>
                    <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:4px;">Copy Allocation Ratio (%)</label>
                    <select id="copy-pct-select" style="width:100%;padding:9px;background:#090e16;color:#fff;border:1px solid #334155;border-radius:6px;">
                      <option value="100" selected>100% (Equal Stake Mirroring)</option>
                      <option value="50">50% (Half Stake)</option>
                      <option value="25">25% (Conservative)</option>
                    </select>
                  </div>
                  <div>
                    <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:4px;">Max Stake per Copied Trade ($)</label>
                    <input id="copy-max-stake" type="number" value="50.00" style="width:100%;box-sizing:border-box;padding:9px;background:#090e16;color:#fff;border:1px solid #334155;border-radius:6px;">
                  </div>
                  <div>
                    <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:4px;">Stop-Loss Balance Protection ($)</label>
                    <input id="copy-stop-loss" type="number" value="200.00" style="width:100%;box-sizing:border-box;padding:9px;background:#090e16;color:#fff;border:1px solid #334155;border-radius:6px;">
                  </div>
                </div>
                <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:20px;">
                  <button id="copy-cancel-btn" style="padding:9px 16px;background:#1e293b;color:#f8fafc;border:1px solid #334155;border-radius:6px;cursor:pointer;">Cancel</button>
                  <button id="copy-confirm-btn" style="padding:9px 16px;background:#0ea5e9;color:#fff;border:0;border-radius:6px;font-weight:800;cursor:pointer;">⚡ Start Copying</button>
                </div>
              </section>
            `;
            const closeModal = () => { modal.style.display = "none"; };
            document.getElementById("copy-modal-close")?.addEventListener("click", closeModal);
            document.getElementById("copy-cancel-btn")?.addEventListener("click", closeModal);
            document.getElementById("copy-confirm-btn")?.addEventListener("click", () => {
              const copyPct = Number((document.getElementById("copy-pct-select") as HTMLSelectElement)?.value || 100);
              const maxStake = Number((document.getElementById("copy-max-stake") as HTMLInputElement)?.value || 50);
              const stopLoss = Number((document.getElementById("copy-stop-loss") as HTMLInputElement)?.value || 200);

              copiers[traderName] = { copyPct, maxStake, stopLoss, startedAt: Date.now() };
              localStorage.setItem("starx_active_copy_traders", JSON.stringify(copiers));

              closeModal();
              btn.textContent = "✓ Copying";
              btn.style.background = "#059669";
              this.showDashboardNotice(`Auto-Copy Active! You are now mirroring trades from ${traderName}.`, "success");
            });
          });
        });
      }
      if (quick)
        quick.innerHTML = leaders
          .slice(0, 3)
          .map(
            (leader) =>
              `<div class="lb-row" style="background:#0d1219"><div class="lb-row-left"><span class="lb-rank">#${Number(leader.rank)}</span><b>${this.escapeHTML(String(leader.trader_tag || "Anonymous"))}</b></div><div class="lb-row-right"><b style="color:#00e676">${this.escapeHTML(String(leader.net_profit || "—"))}</b></div></div>`,
          )
          .join("");
    } catch (error: any) {
      const message = this.escapeHTML(
        error?.message || "Leaderboard service unavailable",
      );
      if (drawer)
        drawer.innerHTML = `<div class="trades-empty" style="color:#fb7185">${message}</div>`;
      if (quick)
        quick.innerHTML =
          '<span style="color:#fb7185">Rankings unavailable.</span>';
    }
  }

  private async showSupportCenter(): Promise<void> {
    const isAuthed = this.isAuthenticated || !!this.restClient.getToken();
    let tickets: any[] = [];
    let social: any[] = [];
    let loadError = "";
    try {
      const [ticketResult, socialResult] = await Promise.all([
        this.restClient.listSupportTickets().catch(() => ({ tickets: [] })),
        this.restClient.getTenantSettings("SOCIAL").catch(() => ({ items: [] })),
      ]);
      tickets = ticketResult?.tickets || [];
      social = socialResult?.items || [];
    } catch (error: any) {
      loadError = error?.message || "Support history unavailable";
    }
    const socialLinks = social
      .map((item) => {
        const url = String(item.config?.url || "");
        return /^https:\/\//i.test(url)
          ? `<a class="social-chip" href="${this.escapeHTML(url)}" target="_blank" rel="noopener noreferrer">◎ ${this.escapeHTML(item.display_name)}</a>`
          : "";
      })
      .filter(Boolean)
      .join(" · ");
    const overlay = document.getElementById("modal-overlay") || document.createElement("div");
    overlay.className = "modal-overlay";
    overlay.style.display = "flex";
    overlay.innerHTML = `
      <div class="modal-content support-command">
        <button class="support-modal-close" id="support-modal-close-btn" aria-label="Close modal">✕</button>

        <section class="support-hero">
          <div class="support-hero-left">
            <div class="support-status-badge">
              <span class="support-pulse-dot"></span>
              <span>24/7 VIP DESK · OPERATOR ONLINE</span>
            </div>
            <h3 class="support-hero-title">How can our specialists assist you?</h3>
            <p class="support-hero-subtitle">Open a traceable high-priority case for deposits, withdrawals, verification, execution, or technical diagnostics. Average response time &lt; 3 minutes.</p>
          </div>
          <div class="support-avatar-badge" title="24/7 Priority Support Desk">
            🎧
          </div>
        </section>

        <div class="support-channels-bar">
          <div class="support-channels-left">
            <span class="support-channels-label">Official Channels:</span>
            ${socialLinks || '<a class="social-chip" href="https://t.me/starx_support" target="_blank" rel="noopener noreferrer">💬 Official Telegram Support</a>'}
          </div>
          <div class="support-sla-tag">
            <span>🛡️</span>
            <span>Traceable Case Ledger · 100% SLA Guarantee</span>
          </div>
        </div>

        <div class="support-topics-bar" role="tablist" aria-label="Quick Support Topics">
          <button type="button" class="support-topic-btn active" data-category="DEPOSIT" data-title="Deposit & Payout">💳 Deposit &amp; Payout</button>
          <button type="button" class="support-topic-btn" data-category="KYC" data-title="KYC Verification">🪪 KYC Verification</button>
          <button type="button" class="support-topic-btn" data-category="TRADE" data-title="Trade Execution">📈 Trade Execution</button>
          <button type="button" class="support-topic-btn" data-category="ACCOUNT" data-title="Account Security">🔐 Account Security</button>
          <button type="button" class="support-topic-btn" data-category="TECHNICAL" data-title="Technical Issue">⚡ Technical Help</button>
        </div>

        <div class="support-grid">
          <!-- Left Column: Create Request -->
          <section class="support-panel">
            <div class="support-panel-header">
              <h4>📝 Submit Priority Ticket</h4>
              <span class="support-panel-badge">ENCRYPTED QUEUE</span>
            </div>
            <form id="trader-support-form" style="display:flex;flex-direction:column;gap:12px">
              ${!isAuthed ? `
                <div>
                  <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:5px;font-weight:600;letter-spacing:0.02em;">YOUR EMAIL ADDRESS</label>
                  <input id="support-email" type="email" placeholder="trader@example.com" required>
                </div>
              ` : ""}
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
                <div>
                  <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:5px;font-weight:600;letter-spacing:0.02em;">CATEGORY</label>
                  <select id="support-category" required>
                    <option value="DEPOSIT" selected>💳 DEPOSIT</option>
                    <option value="WITHDRAWAL">💸 WITHDRAWAL</option>
                    <option value="TRADE">📈 TRADE</option>
                    <option value="KYC">🪪 KYC</option>
                    <option value="ACCOUNT">🔐 ACCOUNT</option>
                    <option value="TECHNICAL">⚡ TECHNICAL</option>
                    <option value="OTHER">❓ OTHER</option>
                  </select>
                </div>
                <div>
                  <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:5px;font-weight:600;letter-spacing:0.02em;">PRIORITY</label>
                  <select id="support-priority">
                    <option value="LOW">⚪ Low</option>
                    <option value="NORMAL" selected>🟢 Normal</option>
                    <option value="HIGH">🟡 High</option>
                    <option value="URGENT">🔴 Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:5px;font-weight:600;letter-spacing:0.02em;">SUBJECT</label>
                <input id="support-subject" placeholder="e.g. Deposit confirmation inquiry #8921" minlength="4" required>
              </div>
              <div>
                <label style="display:block;font-size:11px;color:#94a3b8;margin-bottom:5px;font-weight:600;letter-spacing:0.02em;">MESSAGE / DETAILS</label>
                <textarea id="support-message" placeholder="Please describe the issue, relevant transaction hash or order ID, and expected outcome..." minlength="8" required style="min-height:115px;resize:vertical"></textarea>
              </div>
              <button id="support-submit-btn" class="support-submit-btn experience-action" type="submit">
                <span>🚀</span>
                <span>Submit Priority Ticket</span>
              </button>
              <div id="support-status" role="status"></div>
            </form>
          </section>

          <!-- Right Column: Recent Cases -->
          <section class="support-panel">
            <div class="support-panel-header">
              <h4>📋 Ticket History</h4>
              <span class="support-panel-badge">${tickets.length} ACTIVE</span>
            </div>
            ${loadError ? `<p style="color:#fb7185;font-size:12px;margin:0">${this.escapeHTML(loadError)}</p>` : `
              <div style="display:flex;flex-direction:column;gap:8px;max-height:380px;overflow-y:auto;padding-right:4px;">
                ${tickets.length > 0 ? tickets.map((t) => {
                  const statusStr = String(t.status || "OPEN").toUpperCase();
                  const statusClass = statusStr.includes("RESOLV") ? "support-status-resolved" : statusStr.includes("REVIEW") ? "support-status-review" : "support-status-open";
                  return `
                    <article class="support-ticket-item">
                      <div class="support-ticket-top">
                        <strong class="support-ticket-title">${this.escapeHTML(t.subject || "Support Ticket")}</strong>
                        <span class="support-ticket-status ${statusClass}">${this.escapeHTML(statusStr)}</span>
                      </div>
                      <p class="support-ticket-msg">${this.escapeHTML(t.message || "")}</p>
                      ${t.resolution ? `<div class="support-ticket-res"><strong>✓ Resolution:</strong> ${this.escapeHTML(t.resolution)}</div>` : ""}
                      <div class="support-ticket-meta">
                        <span>Category: ${this.escapeHTML(t.category || "GENERAL")}</span>
                        <span>${t.created_at ? new Date(t.created_at).toLocaleDateString() : "Active"}</span>
                      </div>
                    </article>
                  `;
                }).join("") : `
                  <div class="support-empty-state">
                    <div class="support-empty-icon">📭</div>
                    <h5 class="support-empty-title">No Active Tickets</h5>
                    <p class="support-empty-desc">You do not have any open tickets or dispute inquiries. All future ticket status updates and resolutions will appear here in real time.</p>
                    <div class="support-guarantees">
                      <div class="support-guarantee-row">
                        <span class="guarantee-icon">⚡</span>
                        <div>
                          <strong>Instant Priority Routing</strong>
                          <span>Direct assignment to senior operations specialists</span>
                        </div>
                      </div>
                      <div class="support-guarantee-row">
                        <span class="guarantee-icon">🛡️</span>
                        <div>
                          <strong>Immutable Audit Trail</strong>
                          <span>Every response is cryptographically logged &amp; preserved</span>
                        </div>
                      </div>
                      <div class="support-guarantee-row">
                        <span class="guarantee-icon">🔒</span>
                        <div>
                          <strong>24/7 Global Coverage</strong>
                          <span>Round-the-clock cashier, trade &amp; technical assistance</span>
                        </div>
                      </div>
                    </div>
                  </div>
                `}
              </div>
            `}
          </section>
        </div>
      </div>
    `;
    if (!document.getElementById("modal-overlay")) {
      document.body.appendChild(overlay);
    }
    const close = () => {
      overlay.style.display = "none";
      overlay.innerHTML = "";
    };
    overlay.querySelector("#support-modal-close-btn")?.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });

    // Interactive topic selector pill clicks
    overlay.querySelectorAll(".support-topic-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        overlay.querySelectorAll(".support-topic-btn").forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        const category = btn.getAttribute("data-category");
        const categorySelect = overlay.querySelector("#support-category") as HTMLSelectElement | null;
        if (categorySelect && category) {
          categorySelect.value = category;
        }
        const subjectInput = overlay.querySelector("#support-subject") as HTMLInputElement | null;
        if (subjectInput) {
          const topicTitle = btn.getAttribute("data-title") || "";
          if (!subjectInput.value || subjectInput.value.startsWith("[")) {
            subjectInput.value = `[${topicTitle}] `;
          }
          subjectInput.focus();
        }
      });
    });

    overlay
      .querySelector("#trader-support-form")
      ?.addEventListener("submit", async (e) => {
        e.preventDefault();
        const val = (id: string) =>
          (
            overlay.querySelector(id) as
              HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
          ).value.trim();
        const status = overlay.querySelector("#support-status") as HTMLElement;
        try {
          const payload: any = {
            category: val("#support-category"),
            priority: val("#support-priority"),
            subject: val("#support-subject"),
            message: val("#support-message"),
          };
          if (!isAuthed) {
            payload.email = val("#support-email");
          }
          const result = await this.restClient.createSupportTicket(payload);
          status.innerHTML = `
            <div style="background:rgba(34,197,94,0.12);border:1px solid rgba(34,197,94,0.35);border-radius:8px;padding:12px;margin-top:10px;color:#4ade80;font-size:13px;display:flex;align-items:flex-start;gap:10px;">
              <span style="font-size:18px;line-height:1;">✓</span>
              <div>
                <strong style="color:#ffffff;">Ticket #${this.escapeHTML(String(result?.ticket_id || "OPENED"))} Created</strong>
                <div style="font-size:11px;color:#86efac;margin-top:2px;">Your ticket is queued with senior support. An operator will respond shortly.</div>
              </div>
            </div>
          `;
          const submitBtn = overlay.querySelector("#support-submit-btn") as HTMLButtonElement | null;
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = `<span>✓</span><span>Ticket Submitted</span>`;
          }
        } catch (error: any) {
          status.innerHTML = `
            <div style="background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.35);border-radius:8px;padding:10px;margin-top:10px;color:#f87171;font-size:12px;">
              ✕ ${this.escapeHTML(error?.message || "Ticket could not be created")}
            </div>
          `;
        }
      });
  }

  private async showCashierModal(mode: "DEPOSIT" | "WITHDRAWAL", preferredBonusId?: string) {
    if (!this.isAuthenticated) {
      this.showAuthModal(
        "register",
        mode === "WITHDRAWAL"
          ? "Create an account or log in to access withdrawal features."
          : "Create an account or log in to deposit funds and trade live."
      );
      return;
    }
    if (!this.cashierModal) {
      this.cashierModal = new CashierDepositModal({
        restClient: this.restClient,
        currentProfile: this.currentProfile,
        appState: this.appState,
        renderHeader: () => this.renderHeader(),
        loadFirstDepositPromotion: () => this.loadFirstDepositPromotion(),
        showDashboardNotice: (msg, type) => this.showDashboardNotice(msg, type),
        showComplianceModal: (prof, ctrl) => this.showComplianceModal(prof, ctrl),
        escapeHTML: (str) => this.escapeHTML(str),
      });
    }
    await this.cashierModal.open(mode, preferredBonusId);
  }

  public showComplianceModal(
    profile: any,
    controls: any = {},
    initialDocs: any[] = [],
    activeTab: 'kyc' | 'controls' | 'security' = 'kyc'
  ) {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;

    let currentDocs = [...initialDocs];
    let currentProfile = { ...profile };
    let currentTab = activeTab;

    const render = () => {
      modalContainer.innerHTML = renderComplianceModal(
        currentProfile,
        controls,
        currentDocs,
        currentTab,
        this.appState.getSelectedCurrency()
      );
      modalContainer.style.display = "flex";
      bindModalEvents();
    };

    const bindModalEvents = () => {
      const closeModal = () => {
        modalContainer.style.display = "none";
      };
      document.getElementById("close-compliance-btn")?.addEventListener("click", closeModal);
      document.getElementById("dismiss-compliance-btn")?.addEventListener("click", closeModal);

      document.getElementById("tab-btn-kyc")?.addEventListener("click", () => {
        currentTab = 'kyc';
        render();
      });
      document.getElementById("tab-btn-controls")?.addEventListener("click", () => {
        currentTab = 'controls';
        render();
      });
      document.getElementById("tab-btn-security")?.addEventListener("click", () => {
        currentTab = 'security';
        render();
      });

      // 2FA Security Handlers
      const setupBox = document.getElementById("compliance-2fa-setup-box");

      document.getElementById("btn-start-email-2fa")?.addEventListener("click", async () => {
        if (!setupBox) return;
        setupBox.style.display = "block";
        setupBox.innerHTML = `
          <div style="text-align: center; color: #94a3b8; font-size: 12px; padding: 12px;">
            <span>⏳</span> Sending confirmation code to your email...
          </div>
        `;
        try {
          const res = await this.restClient.setup2FA('EMAIL');
          setupBox.innerHTML = `
            <h4 style="margin: 0 0 6px; font-size: 13px; color: #38bdf8; font-weight: 700;">Verify Email Code</h4>
            <p style="margin: 0 0 10px; font-size: 11px; color: #94a3b8;">
              A 6-digit verification code was sent to <strong>${this.escapeHTML(res.masked_email || currentProfile.email || 'your email')}</strong>. Enter it to activate Email 2FA:
            </p>
            <div style="display: flex; gap: 8px;">
              <input type="text" id="input-confirm-email-2fa-code" maxlength="6" inputmode="numeric" placeholder="123456" style="flex: 1; background: #0f172a; border: 1px solid #334155; color: #38bdf8; font-family: monospace; font-size: 18px; text-align: center; letter-spacing: 4px; padding: 8px; border-radius: 6px; font-weight: 700; outline: none;" />
              <button id="btn-confirm-email-2fa" style="background: #0284c7; color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap;">
                Activate ➔
              </button>
            </div>
            <div id="setup-2fa-err" style="display: none; margin-top: 8px; color: #f43f5e; font-size: 11px;"></div>
          `;
          document.getElementById("btn-confirm-email-2fa")?.addEventListener("click", async () => {
            const codeInput = document.getElementById("input-confirm-email-2fa-code") as HTMLInputElement | null;
            const errBox = document.getElementById("setup-2fa-err");
            const code = codeInput?.value.trim() || "";
            if (!code || code.length !== 6) {
              if (errBox) { errBox.textContent = "Please enter the full 6-digit code"; errBox.style.display = "block"; }
              return;
            }
            try {
              await this.restClient.enable2FA('EMAIL', code);
              currentProfile.two_factor_enabled = true;
              currentProfile.two_factor_method = 'EMAIL';
              this.currentProfile = currentProfile;
              this.showDashboardNotice("🎉 Email Two-Factor Authentication activated successfully!", "success");
              render();
            } catch (err: any) {
              if (errBox) { errBox.textContent = err?.message || "Invalid code. Please try again."; errBox.style.display = "block"; }
            }
          });
        } catch (err: any) {
          setupBox.innerHTML = `
            <div style="color: #f43f5e; font-size: 12px;">Failed to start email 2FA: ${this.escapeHTML(err?.message || "Unknown error")}</div>
          `;
        }
      });

      document.getElementById("btn-start-totp-2fa")?.addEventListener("click", async () => {
        if (!setupBox) return;
        setupBox.style.display = "block";
        setupBox.innerHTML = `
          <div style="text-align: center; color: #94a3b8; font-size: 12px; padding: 12px;">
            <span>⏳</span> Generating Authenticator credentials...
          </div>
        `;
        try {
          const res = await this.restClient.setup2FA('TOTP');
          setupBox.innerHTML = `
            <h4 style="margin: 0 0 6px; font-size: 13px; color: #38bdf8; font-weight: 700;">Google Authenticator Setup</h4>
            <p style="margin: 0 0 8px; font-size: 11px; color: #94a3b8;">
              1. Scan the QR code with Google Authenticator or Authy (or enter the key below manually):
            </p>
            <div style="text-align: center; margin: 10px 0;">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(res.otpauth_url)}" style="background: #ffffff; padding: 6px; border-radius: 8px; display: inline-block; width: 140px; height: 140px;" alt="TOTP QR Code" />
              <div style="margin-top: 8px; background: #0f172a; border: 1px solid #334155; border-radius: 6px; padding: 6px 10px; font-family: monospace; font-size: 12px; color: #38bdf8; word-break: break-all; user-select: all;">
                ${this.escapeHTML(res.secret)}
              </div>
            </div>
            <p style="margin: 0 0 8px; font-size: 11px; color: #94a3b8;">
              2. Enter the 6-digit rolling code shown in your Authenticator app to confirm:
            </p>
            <div style="display: flex; gap: 8px;">
              <input type="text" id="input-confirm-totp-code" maxlength="6" inputmode="numeric" placeholder="123456" style="flex: 1; background: #0f172a; border: 1px solid #334155; color: #38bdf8; font-family: monospace; font-size: 18px; text-align: center; letter-spacing: 4px; padding: 8px; border-radius: 6px; font-weight: 700; outline: none;" />
              <button id="btn-confirm-totp-2fa" style="background: linear-gradient(135deg, #0284c7, #2563eb); color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap;">
                Activate ➔
              </button>
            </div>
            <div id="setup-totp-err" style="display: none; margin-top: 8px; color: #f43f5e; font-size: 11px;"></div>
          `;
          document.getElementById("btn-confirm-totp-2fa")?.addEventListener("click", async () => {
            const codeInput = document.getElementById("input-confirm-totp-code") as HTMLInputElement | null;
            const errBox = document.getElementById("setup-totp-err");
            const code = codeInput?.value.trim() || "";
            if (!code || code.length !== 6) {
              if (errBox) { errBox.textContent = "Please enter the full 6-digit code"; errBox.style.display = "block"; }
              return;
            }
            try {
              await this.restClient.enable2FA('TOTP', code, res.secret);
              currentProfile.two_factor_enabled = true;
              currentProfile.two_factor_method = 'TOTP';
              this.currentProfile = currentProfile;
              this.showDashboardNotice("🎉 Google Authenticator 2FA activated successfully!", "success");
              render();
            } catch (err: any) {
              if (errBox) { errBox.textContent = err?.message || "Invalid Authenticator code. Check your device clock."; errBox.style.display = "block"; }
            }
          });
        } catch (err: any) {
          setupBox.innerHTML = `
            <div style="color: #f43f5e; font-size: 12px;">Failed to initialize Authenticator: ${this.escapeHTML(err?.message || "Unknown error")}</div>
          `;
        }
      });

      document.getElementById("btn-do-disable-2fa")?.addEventListener("click", async () => {
        const pwdInput = document.getElementById("input-2fa-disable-pwd") as HTMLInputElement | null;
        const errBox = document.getElementById("disable-2fa-status");
        const pwd = pwdInput?.value.trim() || "";
        if (!pwd) {
          if (errBox) {
            errBox.textContent = "Please enter your password to confirm deactivation.";
            errBox.style.display = "block";
            errBox.style.color = "#f43f5e";
          }
          return;
        }
        try {
          await this.restClient.disable2FA(pwd);
          currentProfile.two_factor_enabled = false;
          this.currentProfile = currentProfile;
          this.showDashboardNotice("Two-Factor Authentication deactivated", "info");
          render();
        } catch (err: any) {
          if (errBox) {
            errBox.textContent = err?.message || "Incorrect password";
            errBox.style.display = "block";
            errBox.style.color = "#f43f5e";
          }
        }
      });

      const uploadForm = document.getElementById("trader-kyc-upload-form") as HTMLFormElement | null;
      const submitStatus = document.getElementById("kyc-submit-status") as HTMLElement | null;
      const submitBtn = document.getElementById("submit-kyc-doc-btn") as HTMLButtonElement | null;
      const docTypeSelect = document.getElementById("kyc-doc-type") as HTMLSelectElement | null;
      const idNumberInput = document.getElementById("kyc-id-number") as HTMLInputElement | null;
      const idFormatHint = document.getElementById("kyc-id-format-hint") as HTMLElement | null;

      docTypeSelect?.addEventListener("change", () => {
        const dt = docTypeSelect.value;
        if (!idNumberInput) return;
        if (dt === "AADHAAR") {
          idNumberInput.placeholder = "Enter 12-digit Aadhaar number (e.g. 5432 9876 1234)";
          if (idFormatHint) idFormatHint.textContent = "12-digit UIDAI";
        } else if (dt === "PASSPORT") {
          idNumberInput.placeholder = "Enter Passport number (e.g. A1234567)";
          if (idFormatHint) idFormatHint.textContent = "Passport Serial";
        } else if (dt === "DRIVING_LICENSE") {
          idNumberInput.placeholder = "Enter Driving License number (e.g. DL-1420110012345)";
          if (idFormatHint) idFormatHint.textContent = "Driver's License";
        } else {
          idNumberInput.placeholder = "Enter National ID / PAN / Voter ID number";
          if (idFormatHint) idFormatHint.textContent = "National ID No.";
        }
      });

      uploadForm?.addEventListener("submit", async (e) => {
        e.preventDefault();
        const contentTypeSelect = document.getElementById("kyc-content-type") as HTMLSelectElement | null;
        const fileInput = document.getElementById("kyc-file-input") as HTMLInputElement | null;
        const isIndian = String((currentProfile as any)?.country_code || (currentProfile as any)?.country || '').toUpperCase() === 'IN';
        const docType = docTypeSelect?.value || (isIndian ? "AADHAAR" : "NATIONAL_ID");
        const contentType = contentTypeSelect?.value || "application/pdf";
        const file = fileInput?.files?.[0];
        const idNumber = idNumberInput?.value?.trim() || "";

        if (!idNumber && ["AADHAAR", "PASSPORT", "NATIONAL_ID", "DRIVING_LICENSE"].includes(docType)) {
          if (submitStatus) {
            submitStatus.style.color = "#f43f5e";
            submitStatus.textContent = "Please enter your official document ID / serial number.";
          }
          idNumberInput?.focus();
          return;
        }

        if (docType === "AADHAAR") {
          const digits = idNumber.replace(/\D/g, "");
          if (digits.length !== 12) {
            if (submitStatus) {
              submitStatus.style.color = "#f43f5e";
              submitStatus.textContent = "Aadhaar number must contain exactly 12 numeric digits.";
            }
            idNumberInput?.focus();
            return;
          }
        }

        try {
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = `<span>⏳</span> Submitting to Secure Vault…`;
          }
          if (submitStatus) {
            submitStatus.style.color = "#38bdf8";
            submitStatus.textContent = "Encrypting document & registering with compliance engine…";
          }

          const fileExt = contentType === "image/png" ? "png" : contentType === "image/jpeg" ? "jpg" : "pdf";
          const fileName = file ? file.name.replace(/[^a-zA-Z0-9._-]/g, "_") : `${docType.toLowerCase()}_evidence.${fileExt}`;
          const objectKey = `kyc/${currentProfile.user_id}/${Date.now()}_${fileName}`;

          let documentData = "";
          if (file) {
            documentData = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(new Error("Failed to read document file"));
              reader.readAsDataURL(file);
            });
          }

          await this.restClient.submitKycDocument({
            document_type: docType,
            storage_provider: "SECURE_S3",
            object_key: objectKey,
            content_type: contentType,
            id_number: idNumber,
            document_data: documentData,
            document_url: documentData,
          });

          if (submitStatus) {
            submitStatus.style.color = "#10b981";
            submitStatus.textContent = "✓ Document registered successfully! Under review by compliance.";
          }

          const [updatedDocs, updatedProfile] = await Promise.all([
            this.restClient.listKycDocuments().catch(() => ({ items: [] })),
            this.restClient.getProfile().catch(() => currentProfile),
          ]);
          currentDocs = updatedDocs?.items || [];
          currentProfile = updatedProfile;
          this.currentProfile = updatedProfile;

          setTimeout(() => {
            render();
          }, 800);
        } catch (err: any) {
          if (submitStatus) {
            submitStatus.style.color = "#f43f5e";
            const msg = err?.message || "";
            if (msg.includes("DUPLICATE_ID_NUMBER_DETECTED") || err?.status === 409) {
              submitStatus.textContent = "⚠️ Duplicate ID Detected: This document number is already registered to another account. Duplicate identities are strictly prohibited.";
            } else {
              submitStatus.textContent = msg || "Failed to submit document. Please try again.";
            }
          }
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = `<span>📤</span> Upload & Submit for Verification`;
          }
        }
      });

      const applyControl = async (
        action: "SET_DAILY_LOSS_LIMIT" | "START_COOLING_OFF" | "SELF_EXCLUDE",
        value: string,
      ) => {
        const status = document.getElementById("responsible-control-status");
        try {
          if (status)
            status.textContent = "Applying authoritative account control…";
          const activeCurr = this.appState.getSelectedCurrency();
          const response = await this.restClient.updateResponsibleTradingControl(
            action,
            value,
            activeCurr,
          );
          if (action === "SET_DAILY_LOSS_LIMIT") {
            const cents = Number(value);
            controls.daily_loss_limit_cents = cents;
            const activeLimitEl = document.getElementById("compliance-active-daily-limit");
            if (activeLimitEl) {
              const sym = getCurrencySymbol(activeCurr);
              const num = cents / 100;
              activeLimitEl.textContent = `${sym}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/day`;
            }
          }
          if (status)
            status.textContent = `Applied: ${response.action} · ${response.status}`;
          this.showDashboardNotice(`Account safeguard applied successfully (${response.action})`, "success");
          await this.appState.refreshLiveState();
        } catch (error: any) {
          const raw = String(error?.message || "");
          let msg = error?.message || "Responsible trading control failed";
          if (raw.includes("LOSS_LIMIT_MAY_ONLY_BE_TIGHTENED") || raw.includes("LOSS_LIMIT_EXCEEDS_POLICY_CEILING")) {
            msg = "Daily loss limit exceeds maximum platform policy ceiling.";
          }
          if (status)
            status.textContent = msg;
          this.showDashboardNotice(msg, "error");
        }
      };

      document
        .getElementById("save-daily-loss-limit-btn")
        ?.addEventListener("click", () => {
          const value =
            (document.getElementById("daily-loss-limit") as HTMLInputElement)
              ?.value || "";
          if (!/^\d+(\.\d{1,2})?$/.test(value) || Number(value) <= 0) return;
          applyControl(
            "SET_DAILY_LOSS_LIMIT",
            String(Math.round(Number(value) * 100)),
          );
        });

      document
        .getElementById("cooling-off-btn")
        ?.addEventListener("click", async () => {
          if (
            await this.showDashboardConfirmation(
              "Start cooling-off period?",
              "This non-reversible 24-hour control blocks trading immediately.",
              "Start 24-hour cooling-off",
            )
          )
            await applyControl("START_COOLING_OFF", "24");
        });

      document
        .getElementById("self-exclude-btn")
        ?.addEventListener("click", async () => {
          if (
            await this.showDashboardConfirmation(
              "Self-exclude for 30 days?",
              "This control cannot be cancelled from the trading terminal after confirmation.",
              "Confirm self-exclusion",
            )
          )
            await applyControl("SELF_EXCLUDE", "720");
        });
    };

    render();
  }

  private getCountryDisplay(code: string): string {
    const c = (code || "").trim().toUpperCase();
    if (!c) return "—";
    const found = this.countryCatalogue().find((item) => item.code === c);
    const flag = this.countryFlag(c);
    const name = found ? found.name : c;
    return `${flag} ${name}`;
  }

  private countryFlag(country: string): string {
    const code = country.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code)
      ? String.fromCodePoint(
          ...[...code].map((char) => 127397 + char.charCodeAt(0)),
        )
      : "🌐";
  }

  private escapeHTML(value: string): string {
    return value.replace(
      /[&<>'"]/g,
      (character) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          "'": "&#39;",
          '"': "&quot;",
        })[character] || character,
    );
  }

  public showComplianceErrorModal(errMsg: string) {
    const modalContainer = document.getElementById("modal-overlay");
    if (!modalContainer) return;
    modalContainer.innerHTML = `
      <div style="background: #121824; border: 1px solid #f43f5e; border-radius: 12px; padding: 24px; max-width: 480px; width: 100%; color: #f8fafc;">
        <h3 style="color: #f43f5e; margin-top: 0;">❌ Profile Lookup Failed</h3>
        <p style="font-size: 13px; color: #94a3b8;">${errMsg}</p>
        <button id="close-comp-err-btn" style="background: #1e293b; color: #fff; border: 1px solid #334155; padding: 8px 16px; border-radius: 6px; cursor: pointer;">Close</button>
      </div>
    `;
    modalContainer.style.display = "flex";
    document
      .getElementById("close-comp-err-btn")
      ?.addEventListener("click", () => {
        modalContainer.style.display = "none";
      });
  }
}

// Auto-boot on DOM content loaded
if (typeof window !== "undefined") {
  const boot = () => {
    const app = new TradingTerminalApp();
    app.init();
    (window as any).app = app;
  };
  if (document.readyState === "loading") {
    window.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
}
